import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import { supabase } from './lib/supabase';

/** Primary Team Admin Owner — other admins cannot remove this account. */
const TEAM_ADMIN_OWNER_EMAIL = 'vanessa@tikix.io';

const normalizeEmail = (email) =>
  String(email || '')
    .trim()
    .toLowerCase();

const isTeamAdminOwner = (memberOrEmail) => {
  if (!memberOrEmail) return false;
  if (typeof memberOrEmail === 'string') {
    return normalizeEmail(memberOrEmail) === TEAM_ADMIN_OWNER_EMAIL;
  }
  return normalizeEmail(memberOrEmail.email) === TEAM_ADMIN_OWNER_EMAIL;
};

const BUILTIN_TAG_NAMES = [
  'Static Posts',
  'Carousel Posts',
  'Video Editing (No Caption, Onscreen Text, BGM)',
  'Video Editing (No Captions Only)',
  'Story Sets',
  'Social Engagement',
  'Client Communication',
  'Document Collection',
  'Compliance Packages',
  'Full Inbox Management',
  'Calendar Management',
  'Weekly Pipeline Reports',
  'CRM Cleanup',
  'CRM Data Entry',
  'CRM Setup',
  'Newsletter',
  'Blog Writing',
  'LinkedIn Outreach',
  'Landing Pages',
  'CRM Automations',
  'Email Automations',
  'Website Updates',
  'Referral Partner Research',
  // Legacy builtins (kept for existing task tags)
  'Social Media Management',
  'Email Marketing & Newsletters',
  'Data Entry & CRM Management',
  'Inbox & Calendar Management',
  'Lead Gen & Referral Research',
  'Compliance & E-Sign',
  'Automations & Workflows',
  'Landing Pages, Funnels & Websites',
  'Webinars & Events',
  'Content Creation',
  'Light Design',
];

const DEFAULT_PROJECT_STATUSES = [
  { name: 'Active', color: 'purple', sortOrder: 0 },
  { name: 'On Hold', color: 'amber', sortOrder: 1 },
  { name: 'Completed', color: 'emerald', sortOrder: 2 },
  { name: 'Cancelled', color: 'gray', sortOrder: 3 },
];

const DEFAULT_TASK_STATUSES = [
  { name: 'To Do', color: 'gray', sortOrder: 0 },
  { name: 'In Progress', color: 'blue', sortOrder: 1 },
  { name: 'Blocked', color: 'red', sortOrder: 2 },
  { name: 'Done', color: 'emerald', sortOrder: 3 },
];

const normalizeLegacyTaskStatus = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'todo') return 'To Do';
  if (raw === 'in_progress') return 'In Progress';
  if (raw === 'completed') return 'Done';
  return raw;
};

const statusBadgeClass = (color) => {
  const map = {
    purple: 'bg-purple-50 text-purple-800 border-purple-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return map[color] || map.purple;
};

/** Colored status indicator (matches Task Status / title radio dot). */
const statusDotClass = (color, isRunning = false) => {
  if (isRunning) return 'bg-green-500 animate-pulse';
  const map = {
    purple: 'bg-purple-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    red: 'bg-red-500',
    gray: 'bg-gray-400',
  };
  return map[color] || map.gray;
};

const resolveStatusColor = (statuses, statusName, fallback = 'gray') =>
  (statuses || []).find((item) => item.name === statusName)?.color || fallback;

/** Native tooltip: show full text when labels are truncated in the UI. */
const hoverFullText = (...parts) => {
  const text = parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' — ');
  return text || undefined;
};

const escapeRegExp = (value) =>
  String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getMentionableMembers = (members) =>
  (members || [])
    .filter((member) => member?.id && String(member.name || '').trim())
    .map((member) => ({
      id: member.id,
      name: String(member.name).trim(),
      email: member.email || '',
      role: member.role || '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

/** Detect an active `@query` at the caret for autocomplete. */
const getActiveMentionQuery = (text, caret) => {
  const value = String(text || '');
  const pos = Math.max(0, Math.min(Number(caret) || 0, value.length));
  const before = value.slice(0, pos);
  const match = before.match(/(^|[\s([{])@([^\n@]*)$/);
  if (!match) return null;
  const query = match[2] || '';
  // Disallow spaces past a reasonable query length without a match UI.
  if (query.length > 48) return null;
  const startIndex = before.length - query.length - 1;
  return { query, startIndex, endIndex: pos };
};

const filterMentionMembers = (members, query) => {
  const q = String(query || '').trim().toLowerCase();
  const list = getMentionableMembers(members);
  if (!q) return list.slice(0, 8);
  return list
    .filter(
      (member) =>
        member.name.toLowerCase().includes(q) ||
        member.email.toLowerCase().includes(q)
    )
    .slice(0, 8);
};

const insertMentionAt = (text, startIndex, endIndex, memberName) => {
  const value = String(text || '');
  const before = value.slice(0, startIndex);
  const after = value.slice(endIndex);
  const insertion = `@${memberName} `;
  const next = `${before}${insertion}${after.replace(/^\s*/, '')}`;
  return { text: next, caret: before.length + insertion.length };
};

const extractActivityMentions = (text, members) => {
  const value = String(text || '');
  if (!value.includes('@')) return [];
  const sorted = getMentionableMembers(members).sort(
    (a, b) => b.name.length - a.name.length
  );
  const found = [];
  const seen = new Set();
  sorted.forEach((member) => {
    const re = new RegExp(
      `(^|[\\s([{])@${escapeRegExp(member.name)}(?=$|[\\s.,!?;:)\\]})])`,
      'gi'
    );
    if (!re.test(value) || seen.has(member.id)) return;
    seen.add(member.id);
    found.push({ user_id: member.id, name: member.name });
  });
  return found;
};

/** Highlight @Name tokens in activity text (plain-text safe). */
const renderActivityMentionText = (text, mentionNames = []) => {
  const value = String(text || '');
  const names = [
    ...new Set(
      (mentionNames || [])
        .map((name) => String(name || '').trim())
        .filter(Boolean)
    ),
  ].sort((a, b) => b.length - a.length);
  if (!names.length || !value.includes('@')) return value;

  const pattern = new RegExp(
    `(@(?:${names.map(escapeRegExp).join('|')}))(?=$|[\\s.,!?;:)\\]})])`,
    'gi'
  );
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push(value.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={`mention-${key++}`}
        className="font-bold text-purple-700 bg-purple-50 px-0.5 rounded"
      >
        {match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) parts.push(value.slice(lastIndex));
  return parts.length ? parts : value;
};

/** Indent native <select> choices so the first "Project"/"Client" row reads as a title. */
const DROPDOWN_TITLE_INDENT = '\u00A0\u00A0\u00A0';
const asDropdownChoice = (label) => `${DROPDOWN_TITLE_INDENT}${label}`;

/** Master Task Matrix: Category → Services (optional project tags). */
const MASTER_TASK_MATRIX = [
  {
    category: 'Social Media',
    services: [
      'Static Posts',
      'Carousel Posts',
      'Video Editing (No Caption, Onscreen Text, BGM)',
      'Video Editing (No Captions Only)',
      'Story Sets',
      'Social Engagement',
    ],
  },
  {
    category: 'Deal Support',
    services: [
      'Client Communication',
      'Document Collection',
      'Compliance Packages',
    ],
  },
  {
    category: 'Administration',
    services: [
      'Full Inbox Management',
      'Calendar Management',
      'Weekly Pipeline Reports',
    ],
  },
  {
    category: 'CRM',
    services: ['CRM Cleanup', 'CRM Data Entry', 'CRM Setup'],
  },
  {
    category: 'Marketing',
    services: ['Newsletter', 'Blog Writing', 'LinkedIn Outreach'],
  },
  {
    category: 'Web & Automation',
    services: [
      'Landing Pages',
      'CRM Automations',
      'Email Automations',
      'Website Updates',
      'Referral Partner Research',
    ],
  },
];

const PROJECT_CATEGORY_SUGGESTIONS = MASTER_TASK_MATRIX.map(
  (group) => group.category
);

const getStartOfDay = (date = new Date()) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

/** Local calendar YYYY-MM-DD (avoid UTC shift from toISOString). */
const toLocalDateKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStartOfWeek = (date = new Date(), mondayStart = true) => {
  const start = getStartOfDay(date);
  const day = start.getDay();
  const daysFromWeekStart = mondayStart ? (day === 0 ? 6 : day - 1) : day;
  start.setDate(start.getDate() - daysFromWeekStart);
  return start;
};

const getEndOfWeek = (date = new Date(), mondayStart = true) => {
  const end = getStartOfWeek(date, mondayStart);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const computeTrackedInRange = (entries, rangeStart, rangeEnd = new Date()) =>
  (entries || []).reduce((total, entry) => {
    const start = new Date(entry.started_at).getTime();
    const end = entry.ended_at
      ? new Date(entry.ended_at).getTime()
      : rangeEnd.getTime();
    const overlapStart = Math.max(start, rangeStart.getTime());
    const overlapEnd = Math.min(end, rangeEnd.getTime());
    if (overlapEnd <= overlapStart) return total;
    return total + Math.floor((overlapEnd - overlapStart) / 1000);
  }, 0);

const PROJECT_DOT_COLORS = [
  '#f59e0b',
  '#3b82f6',
  '#10b981',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
];

const projectDotColor = (id = '') => {
  if (!id) return '#9ca3af';
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i) * 17) % PROJECT_DOT_COLORS.length;
  }
  return PROJECT_DOT_COLORS[hash];
};

const formatClockRangeTime = (date) =>
  date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const formatTrackerDayLabel = (date) => {
  const day = getStartOfDay(date);
  const today = getStartOfDay();
  if (day.getTime() === today.getTime()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const toLocalDateInputValue = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toDateOnlyValue = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return toLocalDateInputValue(parsed);
};

const parseManualTimeRange = (dateStr, startStr, endStr) => {
  if (!dateStr || !startStr || !endStr) {
    return { error: 'Date, start time, and end time are required.' };
  }
  const startedAt = new Date(`${dateStr}T${startStr}:00`);
  let endedAt = new Date(`${dateStr}T${endStr}:00`);
  if (Number.isNaN(startedAt.getTime()) || Number.isNaN(endedAt.getTime())) {
    return { error: 'Enter a valid date and time range.' };
  }
  // Overnight entries: end before start means next day.
  if (endedAt.getTime() <= startedAt.getTime()) {
    endedAt = new Date(endedAt.getTime() + 24 * 60 * 60 * 1000);
  }
  const durationSeconds = Math.max(
    0,
    Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000)
  );
  if (durationSeconds <= 0) {
    return { error: 'End time must be after start time.' };
  }
  return { startedAt, endedAt, durationSeconds };
};

/** Parse signed duration: hours (-15, 1.5h) or HH:MM:SS (-02:30:00). */
const parseSignedDurationInput = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return { error: 'Enter a duration (e.g. -15, 1.5, or 02:30:00).' };

  const hms = text.match(/^(-)?(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
  if (hms) {
    const sign = hms[1] ? -1 : 1;
    const hours = Number(hms[2]);
    const minutes = Number(hms[3]);
    const seconds = Number(hms[4] || 0);
    if (![hours, minutes, seconds].every((n) => Number.isFinite(n))) {
      return { error: 'Enter a valid HH:MM:SS duration.' };
    }
    return {
      durationSeconds: sign * (hours * 3600 + minutes * 60 + seconds),
    };
  }

  const hoursMatch = text.match(/^(-?\d+(?:\.\d+)?)\s*h(?:ours?)?$/i);
  const plainMatch = text.match(/^(-?\d+(?:\.\d+)?)$/);
  const matched = hoursMatch || plainMatch;
  if (matched) {
    const hours = Number(matched[1]);
    if (!Number.isFinite(hours)) {
      return { error: 'Enter a valid number of hours.' };
    }
    return { durationSeconds: Math.round(hours * 3600) };
  }

  return {
    error: 'Use hours (e.g. -15 or 1.5) or HH:MM:SS (e.g. 02:30:00).',
  };
};

const secondsToDurationHoursInput = (totalSeconds) => {
  const seconds = Math.floor(Number(totalSeconds) || 0);
  const hours = seconds / 3600;
  if (Number.isInteger(hours)) return String(hours);
  const rounded = Math.round(hours * 1000) / 1000;
  return String(rounded);
};

const USER_ALERTS = {
  noProjects: {
    title: 'No projects yet',
    message:
      'You cannot start a timer until you belong to a project and have a task assigned.',
    steps: [
      'Go to Projects. If you are an admin, create a project.',
      'If you are not an admin, ask an admin to create a project and add you as a member.',
      'Open the project and create a task (or ask to be assigned to one).',
      'Return to Time Tracker and start the timer on that task.',
    ],
  },
  noProjectSelected: {
    title: 'Select a project first',
    message: 'A project is required before the timer can start.',
    steps: [
      'Choose a project from the “Select Project” dropdown.',
      'If the list is empty, create or join a project under Projects first.',
      'Optionally add a description and client, then press Start.',
    ],
  },
  notProjectMember: {
    title: 'You are not on this project',
    message:
      'Timers only work for projects you belong to, with a task assigned to you.',
    steps: [
      'Ask a project admin (or a global admin) to add you to the project roster.',
      'Once added, create a task or ask to be assigned to an existing task.',
      'Then start the timer from Time Tracker.',
    ],
  },
  noTasksAvailable: {
    title: 'No tasks to track yet',
    message: 'There is no assigned task available for your timer.',
    steps: [
      'Open Projects and enter a project you belong to.',
      'Create a task and make sure you are assigned to it.',
      'Or use the quick timer bar: enter what you are working on, pick a project, and press Start.',
    ],
  },
  taskNotAssigned: {
    title: 'Task is not assigned to you',
    message: 'You can only run a timer on tasks assigned to your account.',
    steps: [
      'Open the project that owns this task.',
      'Ask an admin (or assign yourself if allowed) so your name appears on the task.',
      'Try Start again from Time Tracker.',
    ],
  },
};

const normalizeErrorText = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return error.message || error.details || String(error);
};

const toUserAlert = (error, fallbackTitle = 'Something went wrong') => {
  if (error && typeof error === 'object' && error.title) {
    return {
      title: error.title,
      message: error.message || '',
      steps: Array.isArray(error.steps) ? error.steps : [],
    };
  }

  const text = normalizeErrorText(error);
  const lower = text.toLowerCase();

  if (
    lower.includes('row-level security') ||
    lower.includes('permission denied') ||
    lower.includes('not authorized') ||
    lower.includes('42501') ||
    lower.includes('pgrst116')
  ) {
    const isCreateProject = /create project/i.test(fallbackTitle || '');
    return {
      title: 'Permission blocked this action',
      message: isCreateProject
        ? 'The database blocked creating or reading back this project. Run the latest project-creator SQL migration, then reload.'
        : 'Your account does not have access to complete this step yet (often because you are not on the project or not assigned to the task).',
      steps: isCreateProject
        ? [
            'Open the Supabase SQL editor for this project.',
            'Run frontend/supabase/migrations/20260720212000_project_creator_can_select.sql',
            'Hard-refresh the app and create the project again.',
          ]
        : [
            'Confirm you are a member of the project under Projects.',
            'Confirm the task is assigned to you.',
            'If either is missing, ask an admin to add/assign you, then try again.',
          ],
    };
  }

  if (lower.includes('no registered user')) {
    return {
      title: 'User not found',
      message: text,
      steps: [
        'Double-check the exact name or email.',
        'Invite the person from Team first if they do not have an account yet.',
        'Then add them to the project roster.',
      ],
    };
  }

  if (lower.includes('already a project member')) {
    return {
      title: 'Already a member',
      message: text,
      steps: ['No further action needed — that user is already on the project.'],
    };
  }

  return {
    title: fallbackTitle,
    message: text || 'An unexpected error occurred.',
    steps: [
      'Refresh the page and try the action again.',
      'If it keeps failing, share this message with your admin.',
    ],
  };
};

const getReportRangeBounds = (
  preset,
  customFrom,
  customTo,
  mondayStart = true
) => {
  const now = new Date();
  if (preset === 'custom') {
    const from = customFrom
      ? getStartOfDay(new Date(`${customFrom}T00:00:00`))
      : getStartOfDay(now);
    const to = customTo
      ? new Date(`${customTo}T23:59:59.999`)
      : now;
    return { from, to };
  }
  if (preset === 'month') {
    const from = getStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
    return { from, to: now };
  }
  // Full calendar week (Mon–Sun or Sun–Sat) so the bar chart always has 7 days.
  return {
    from: getStartOfWeek(now, mondayStart),
    to: getEndOfWeek(now, mondayStart),
  };
};

const downloadTextFile = (filename, content, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const escapeCsvValue = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const Dashboard = () => {
  const { currentUser, signOut, updateEmailNotificationsEnabled } = useAuth();

  // Projects and their member rosters are persisted in Supabase.
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectSaving, setProjectSaving] = useState(false);
  const [userAlert, setUserAlert] = useState(null);

  const clearUserAlert = () => setUserAlert(null);
  const setDatabaseError = (value) => {
    if (!value) {
      setUserAlert(null);
      return;
    }
    setUserAlert(toUserAlert(value));
  };
  const databaseError = userAlert;

  // Tasks, assignees, and timer entries are persisted in Supabase.
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // 3. View Port and In-Line Mutation Controllers
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeMenu, setActiveMenu] = useState('time-tracker');
  const [viewMode, setViewMode] = useState('grid');
  const [projectViewHelpOpen, setProjectViewHelpOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [newClientName, setNewClientName] = useState('');
  const [clientInviteStatus, setClientInviteStatus] = useState('');
  const [editingClientId, setEditingClientId] = useState(null);
  const [editClientName, setEditClientName] = useState('');
  const [showArchivedClients, setShowArchivedClients] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviteStatus, setInviteStatus] = useState('');
  const [editingTeamMemberId, setEditingTeamMemberId] = useState(null);
  const [editTeamMemberName, setEditTeamMemberName] = useState('');
  const [editTeamMemberRole, setEditTeamMemberRole] = useState('employee');
  const [teamSaving, setTeamSaving] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => currentUser?.emailNotificationsEnabled !== false
  );
  const [weekStartsMonday, setWeekStartsMonday] = useState(true);
  
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [inlineEditProjectId, setInlineEditProjectId] = useState(null);
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [inlineMemberUserId, setInlineMemberUserId] = useState('');
  const [inlineMemberRole, setInlineMemberRole] = useState('Employee');
  const [inlineProjectServiceMenuOpen, setInlineProjectServiceMenuOpen] =
    useState(false);
  const [inlineProjectTagName, setInlineProjectTagName] = useState('');
  const [inlineProjectTagSaving, setInlineProjectTagSaving] = useState(false);
  const [projectDetailsSavingId, setProjectDetailsSavingId] = useState(null);
  const [projectDetailsSavedId, setProjectDetailsSavedId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editingSubtaskKey, setEditingSubtaskKey] = useState(null); // `${taskId}:${subtaskId}`
  const [editSubtaskName, setEditSubtaskName] = useState('');
  const [assigningTaskId, setAssigningTaskId] = useState(null);
  const [assigningSubtaskKey, setAssigningSubtaskKey] = useState(null); // `${taskId}:${subtaskId}`
  const [followingTaskId, setFollowingTaskId] = useState(null);
  const [newSubtaskByTaskId, setNewSubtaskByTaskId] = useState({});
  const [subtaskSavingTaskId, setSubtaskSavingTaskId] = useState(null);

  // Creation Pipeline Elements Holds
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectClientId, setNewProjectClientId] = useState('');
  const [newProjectClientMode, setNewProjectClientMode] = useState('select'); // select | create
  const [newProjectClientName, setNewProjectClientName] = useState('');
  const [selectedProjectServices, setSelectedProjectServices] = useState([]);
  const [customProjectServices, setCustomProjectServices] = useState([]);
  const [newProjectTagName, setNewProjectTagName] = useState('');
  const [projectServiceMenuOpen, setProjectServiceMenuOpen] = useState(false);
  const [projectClientMenuOpen, setProjectClientMenuOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedFollowers, setSelectedFollowers] = useState([]);
  const [selectedTaskServices, setSelectedTaskServices] = useState([]);
  const [customTaskServices, setCustomTaskServices] = useState([]);
  const [newTaskTagName, setNewTaskTagName] = useState('');
  const [taskServiceMenuOpen, setTaskServiceMenuOpen] = useState(false);
  const [createTaskTagMenuOpen, setCreateTaskTagMenuOpen] = useState(false);
  const [createTaskFollowerMenuOpen, setCreateTaskFollowerMenuOpen] = useState(false);
  const [selectedTaskClientIds, setSelectedTaskClientIds] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [taggingTaskId, setTaggingTaskId] = useState(null);
  const [taggingSubtaskKey, setTaggingSubtaskKey] = useState(null); // `${taskId}:${subtaskId}`
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('Employee');
  const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
  const [newTaskEstimatedHours, setNewTaskEstimatedHours] = useState('');
  const [newTaskStartDate, setNewTaskStartDate] = useState('');
  const [newTaskEndDate, setNewTaskEndDate] = useState('');
  const [listColumnPickerOpen, setListColumnPickerOpen] = useState(false);
  const [showDetailedColumns, setShowDetailedColumns] = useState(false);
  const listColumnPickerRef = useRef(null);
  const [listRowMenuId, setListRowMenuId] = useState(null); // taskId or `sub:${taskId}:${subtaskId}`
  const [expandedTaskIds, setExpandedTaskIds] = useState(() => new Set());
  const [listColumns, setListColumns] = useState(() => {
    // Default task list: Title, Status, Assignee, Hours, Service tags, Total tracked.
    // Optional: Followers. Time details toggled separately.
    const defaults = {
      tags: true,
      followers: false,
      totalTrackedHours: true,
    };
    try {
      const rawV3 = localStorage.getItem('tikix.projectListColumns.v3');
      if (rawV3) {
        const parsed = JSON.parse(rawV3);
        return {
          tags:
            parsed?.tags === undefined ? true : Boolean(parsed.tags),
          followers: Boolean(parsed?.followers),
          totalTrackedHours:
            parsed?.totalTrackedHours === undefined
              ? true
              : Boolean(parsed.totalTrackedHours),
        };
      }
      // Migrate from v2: promote Service tags as a default column.
      const rawV2 = localStorage.getItem('tikix.projectListColumns.v2');
      if (rawV2) {
        const parsed = JSON.parse(rawV2);
        const migrated = {
          tags: true,
          followers: Boolean(parsed?.followers),
          totalTrackedHours:
            parsed?.totalTrackedHours === undefined
              ? true
              : Boolean(parsed.totalTrackedHours),
        };
        try {
          localStorage.setItem(
            'tikix.projectListColumns.v3',
            JSON.stringify(migrated)
          );
        } catch {
          /* ignore */
        }
        return migrated;
      }
      const legacy = localStorage.getItem('tikix.projectListColumns');
      if (!legacy) return defaults;
      const parsed = JSON.parse(legacy);
      const migrated = {
        tags: true,
        followers: Boolean(parsed?.followers),
        totalTrackedHours:
          parsed?.totalTrackedHours === undefined
            ? true
            : Boolean(parsed.totalTrackedHours),
      };
      try {
        localStorage.setItem(
          'tikix.projectListColumns.v3',
          JSON.stringify(migrated)
        );
      } catch {
        /* ignore */
      }
      return migrated;
    } catch {
      return defaults;
    }
  });
  const [estimatedHoursDraftByTaskId, setEstimatedHoursDraftByTaskId] = useState({});
  
  const [newComment, setNewComment] = useState('');
  const [commentFiles, setCommentFiles] = useState([]); // [{ id, file, previewUrl }]
  const [commentTaskId, setCommentTaskId] = useState('');
  const [commentLinkUrl, setCommentLinkUrl] = useState('');
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyTaskId, setReplyTaskId] = useState('');
  const [activityPosting, setActivityPosting] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editActivityText, setEditActivityText] = useState('');
  const [editActivityTaskId, setEditActivityTaskId] = useState('');
  const [editActivityLinkUrl, setEditActivityLinkUrl] = useState('');
  const [activityEditSaving, setActivityEditSaving] = useState(false);
  // Activity feed: filter by linked task / files; sort newest|oldest
  const [activityFeedFilterTask, setActivityFeedFilterTask] = useState(false);
  const [activityFeedFilterFiles, setActivityFeedFilterFiles] = useState(false);
  const [activityFeedSort, setActivityFeedSort] = useState('newest'); // newest | oldest
  // { target: 'comment' | 'reply', query, startIndex, endIndex, selectedIndex }
  const [mentionMenu, setMentionMenu] = useState(null);
  const activityFileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const commentInputRef = useRef(null);
  const replyTextInputRef = useRef(null);
  const [timerMenuOpen, setTimerMenuOpen] = useState(false);
  const [selectedQuickTaskId, setSelectedQuickTaskId] = useState('');

  // Clockify-style quick timer bar (Time Tracker view)
  const [trackerInputMode, setTrackerInputMode] = useState('timer'); // timer | manual
  const [quickTimerDescription, setQuickTimerDescription] = useState('');
  const [quickTimerClientId, setQuickTimerClientId] = useState('');
  const [quickTimerProjectId, setQuickTimerProjectId] = useState('');
  const [quickTimerTaskId, setQuickTimerTaskId] = useState('');
  const [quickTimerStarting, setQuickTimerStarting] = useState(false);
  const [quickCreatingClient, setQuickCreatingClient] = useState(false);
  const [quickNewClientName, setQuickNewClientName] = useState('');
  const [quickCreatingProject, setQuickCreatingProject] = useState(false);
  const [quickNewProjectName, setQuickNewProjectName] = useState('');
  const [quickCreateSaving, setQuickCreateSaving] = useState(false);
  const [manualLogDate, setManualLogDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [manualLogStart, setManualLogStart] = useState('09:00');
  const [manualLogEnd, setManualLogEnd] = useState('10:00');
  const [manualLogSaving, setManualLogSaving] = useState(false);
  const [loggingTimeTaskId, setLoggingTimeTaskId] = useState(null);
  const [taskManualDate, setTaskManualDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [taskManualStart, setTaskManualStart] = useState('09:00');
  const [taskManualEnd, setTaskManualEnd] = useState('10:00');
  const [weekTrackedSeconds, setWeekTrackedSeconds] = useState(0);
  const [trackerEntries, setTrackerEntries] = useState([]);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [trackerEntryMenuId, setTrackerEntryMenuId] = useState(null);
  const [trackerEditingEntryId, setTrackerEditingEntryId] = useState(null);
  const [editEntryDurationInput, setEditEntryDurationInput] = useState('');
  const [editEntryAdjustInput, setEditEntryAdjustInput] = useState('');
  const [editEntryDurationSaving, setEditEntryDurationSaving] = useState(false);
  const [trackerClientMenuEntryId, setTrackerClientMenuEntryId] = useState(null);
  const [trackerTagMenuEntryId, setTrackerTagMenuEntryId] = useState(null);
  const [trackerTagSearch, setTrackerTagSearch] = useState('');
  const [trackerNewTagName, setTrackerNewTagName] = useState('');
  const [trackerNewTagSaving, setTrackerNewTagSaving] = useState(false);
  const [timerStatus, setTimerStatus] = useState('');

  // Admin reporting dashboard
  const [reportRangePreset, setReportRangePreset] = useState('week');
  const [reportCustomFrom, setReportCustomFrom] = useState('');
  const [reportCustomTo, setReportCustomTo] = useState('');
  const [reportClientId, setReportClientId] = useState('');
  const [reportProjectId, setReportProjectId] = useState('');
  const [reportMemberId, setReportMemberId] = useState('');
  const [reportTagId, setReportTagId] = useState('');
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [dashboardViewMode, setDashboardViewMode] = useState('cards'); // cards | list
  const [dashboardHoverDayKey, setDashboardHoverDayKey] = useState(null);
  const [projectStatusMenuOpen, setProjectStatusMenuOpen] = useState(false);
  const [newProjectStatusName, setNewProjectStatusName] = useState('');
  const [newTaskStatusName, setNewTaskStatusName] = useState('');
  const [statusOptionSaving, setStatusOptionSaving] = useState(false);

  const isSystemAdmin = currentUser.role === 'admin';
  // Team "Employee" (and Admin) may create projects; they become Project Admin via DB trigger.
  // Clients cannot create projects. Team/app-wide admin stays isSystemAdmin-only.
  const canCreateProjects = currentUser.role !== 'client';

  // Project-level admin (or system admin): manage this project's members/tasks only.
  const canManageProject = (projectId) => {
    if (!projectId) return false;
    if (isSystemAdmin) return true;
    const project = projects.find((item) => item.id === projectId);
    return (project?.members || []).some(
      (member) => member.id === currentUser.id && member.role === 'Admin'
    );
  };

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setDatabaseError('');

    const [
      { data: projectRowsRaw, error: projectsError },
      { data: memberRows, error: membersError },
      { data: profileRows, error: profilesError },
      { data: timeRows },
      { data: taskRows },
      { data: taskClientRows },
      { data: clientRows },
      { data: projectTagRows, error: projectTagsError },
      { data: projectClientRows, error: projectClientsError },
      { data: projectStatusRows, error: projectStatusError },
      { data: taskStatusRows, error: taskStatusError },
      { data: tagRows },
    ] = await Promise.all([
      supabase
        .from('projects')
        .select('id, name, description, created_at, client_id, status')
        .order('created_at', { ascending: false }),
      supabase
        .from('project_members')
        .select('project_id, user_id, role'),
      supabase
        .from('profiles')
        .select('id, full_name, email, role'),
      supabase.from('time_entries').select('task_id, user_id'),
      supabase.from('tasks').select('id, project_id'),
      supabase.from('task_clients').select('task_id, client_id'),
      supabase.from('clients').select('id, name'),
      supabase.from('project_tags').select('project_id, tag_id'),
      supabase.from('project_clients').select('project_id, client_id'),
      supabase
        .from('project_status_options')
        .select('id, project_id, name, color, sort_order'),
      supabase
        .from('task_status_options')
        .select('id, project_id, name, color, sort_order'),
      supabase.from('tags').select('id, name, is_builtin'),
    ]);

    let projectRows = projectRowsRaw;
    let resolvedProjectsError = projectsError;
    if (
      projectsError &&
      /client_id|status|schema cache|column/i.test(projectsError.message || '')
    ) {
      const fallback = await supabase
        .from('projects')
        .select('id, name, description, created_at, client_id')
        .order('created_at', { ascending: false });
      if (fallback.error && /client_id/i.test(fallback.error.message || '')) {
        const bare = await supabase
          .from('projects')
          .select('id, name, description, created_at')
          .order('created_at', { ascending: false });
        projectRows = bare.data;
        resolvedProjectsError = bare.error;
      } else {
        projectRows = fallback.data;
        resolvedProjectsError = fallback.error;
      }
    }

    const projectTagsMissing =
      projectTagsError &&
      /project_tags|schema cache|does not exist/i.test(
        projectTagsError.message || ''
      );
    const projectClientsMissing =
      projectClientsError &&
      /project_clients|schema cache|does not exist/i.test(
        projectClientsError.message || ''
      );
    const projectStatusesMissing =
      projectStatusError &&
      /project_status_options|schema cache|does not exist/i.test(
        projectStatusError.message || ''
      );
    const taskStatusesMissing =
      taskStatusError &&
      /task_status_options|schema cache|does not exist/i.test(
        taskStatusError.message || ''
      );

    const error =
      resolvedProjectsError ||
      membersError ||
      profilesError ||
      (projectTagsError && !projectTagsMissing ? projectTagsError : null) ||
      (projectClientsError && !projectClientsMissing
        ? projectClientsError
        : null) ||
      (projectStatusError && !projectStatusesMissing
        ? projectStatusError
        : null) ||
      (taskStatusError && !taskStatusesMissing ? taskStatusError : null);
    if (error) {
      setDatabaseError(error.message);
      setProjectsLoading(false);
      return;
    }

    const profilesById = new Map(
      (profileRows || []).map((profile) => [profile.id, profile])
    );
    setTeamMembers(
      (profileRows || []).map((profile) => ({
        id: profile.id,
        name: profile.full_name || profile.email || 'Unnamed user',
        email: profile.email || '',
        role: ['admin', 'client'].includes(profile.role) ? profile.role : 'employee',
      }))
    );

    const tasksById = new Map((taskRows || []).map((task) => [task.id, task]));
    const memberKeySet = new Set(
      (memberRows || []).map((row) => `${row.project_id}:${row.user_id}`)
    );
    const effectiveMemberRows = [...(memberRows || [])];
    const missingMembers = [];

    (timeRows || []).forEach((entry) => {
      const task = tasksById.get(entry.task_id);
      if (!task) return;
      const key = `${task.project_id}:${entry.user_id}`;
      if (memberKeySet.has(key)) return;
      const row = {
        project_id: task.project_id,
        user_id: entry.user_id,
        role: 'employee',
      };
      missingMembers.push(row);
      effectiveMemberRows.push(row);
      memberKeySet.add(key);
    });

    if (missingMembers.length > 0) {
      await supabase.from('project_members').upsert(missingMembers, {
        onConflict: 'project_id,user_id',
        ignoreDuplicates: true,
      });
    }

    const clientsById = new Map(
      (clientRows || []).map((client) => [client.id, client.name])
    );
    const tagsById = new Map(
      (tagRows || []).map((tag) => [tag.id, tag.name])
    );
    setTags(
      (tagRows || [])
        .map((tag) => ({
          id: tag.id,
          name: tag.name,
          isBuiltin: Boolean(tag.is_builtin),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    const clientsByProject = new Map();
    const tagsByProject = new Map();

    // Prefer explicit project_clients rows; fall back to legacy projects.client_id.
    if (!projectClientsMissing) {
      (projectClientRows || []).forEach((row) => {
        const list = clientsByProject.get(row.project_id) || [];
        const name = clientsById.get(row.client_id);
        if (!name) return;
        if (!list.some((client) => client.id === row.client_id)) {
          list.push({ id: row.client_id, name });
        }
        clientsByProject.set(row.project_id, list);
      });
    }

    (projectRows || []).forEach((project) => {
      if (!project.client_id) return;
      const name = clientsById.get(project.client_id);
      if (!name) return;
      const list = clientsByProject.get(project.id) || [];
      if (!list.some((client) => client.id === project.client_id)) {
        list.push({ id: project.client_id, name });
      }
      clientsByProject.set(project.id, list);
    });

    (taskClientRows || []).forEach((row) => {
      const task = tasksById.get(row.task_id);
      if (!task) return;
      const list = clientsByProject.get(task.project_id) || [];
      const name = clientsById.get(row.client_id);
      if (!name) return;
      if (!list.some((client) => client.id === row.client_id)) {
        list.push({ id: row.client_id, name });
      }
      clientsByProject.set(task.project_id, list);
    });

    if (!projectTagsMissing) {
      (projectTagRows || []).forEach((row) => {
        const list = tagsByProject.get(row.project_id) || [];
        const name = tagsById.get(row.tag_id);
        if (!name) return;
        if (!list.some((tag) => tag.id === row.tag_id)) {
          list.push({ id: row.tag_id, name });
        }
        tagsByProject.set(row.project_id, list);
      });
    }

    const projectStatusesByProject = new Map();
    const taskStatusesByProject = new Map();

    if (!projectStatusesMissing) {
      (projectStatusRows || []).forEach((row) => {
        const list = projectStatusesByProject.get(row.project_id) || [];
        list.push({
          id: row.id,
          name: row.name,
          color: row.color || 'purple',
          sortOrder: row.sort_order || 0,
        });
        projectStatusesByProject.set(row.project_id, list);
      });
    }

    if (!taskStatusesMissing) {
      (taskStatusRows || []).forEach((row) => {
        const list = taskStatusesByProject.get(row.project_id) || [];
        list.push({
          id: row.id,
          name: row.name,
          color: row.color || 'gray',
          sortOrder: row.sort_order || 0,
        });
        taskStatusesByProject.set(row.project_id, list);
      });
    }

    const mappedProjects = (projectRows || []).map((project) => {
      const projectStatuses = (
        projectStatusesByProject.get(project.id) || []
      ).sort((a, b) => a.sortOrder - b.sortOrder);
      const taskStatuses = (taskStatusesByProject.get(project.id) || []).sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      return {
        ...project,
        clientId: project.client_id || null,
        status: project.status || 'Active',
        projectStatuses:
          projectStatuses.length > 0
            ? projectStatuses
            : DEFAULT_PROJECT_STATUSES.map((item, index) => ({
                id: `local-project-${project.id}-${index}`,
                ...item,
              })),
        taskStatuses:
          taskStatuses.length > 0
            ? taskStatuses
            : DEFAULT_TASK_STATUSES.map((item, index) => ({
                id: `local-task-${project.id}-${index}`,
                ...item,
              })),
        members: effectiveMemberRows
          .filter((member) => member.project_id === project.id)
          .map((member) => {
            const profile = profilesById.get(member.user_id);
            return {
              id: member.user_id,
              name: profile?.full_name || profile?.email || 'Unknown user',
              email: profile?.email || '',
              role: member.role === 'admin' ? 'Admin' : 'Employee',
            };
          }),
        clients: clientsByProject.get(project.id) || [],
        tags: tagsByProject.get(project.id) || [],
      };
    });

    setProjects(mappedProjects);
    setProjectsLoading(false);
  }, []);

  const loadTasks = useCallback(async () => {
    const [
      { data: taskRows, error: tasksError },
      { data: assigneeRows, error: assigneesError },
      { data: followerRows, error: followersError },
      { data: subtaskRows, error: subtasksError },
      { data: subtaskTagRows, error: subtaskTagsError },
      { data: subtaskAssigneeRows, error: subtaskAssigneesError },
      { data: favoriteRows, error: favoritesError },
      { data: clientRows, error: clientsError },
      { data: taskTagRows, error: taskTagsError },
      { data: tagRows, error: tagsError },
      { data: timeRows, error: timeError },
      { data: memberRows, error: membersError },
      { data: profileRows, error: profilesError },
      { data: clientLabelRows },
    ] = await Promise.all([
      supabase
        .from('tasks')
        .select(
          'id, project_id, name, status, estimated_seconds, start_date, end_date, created_at'
        )
        .order('created_at', { ascending: true }),
      supabase
        .from('task_assignees')
        .select('task_id, user_id'),
      supabase
        .from('task_followers')
        .select('task_id, user_id'),
      supabase
        .from('task_subtasks')
        .select(
          'id, task_id, name, is_completed, status, sort_order, start_date, end_date, created_at'
        )
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('task_subtask_tags')
        .select('subtask_id, tag_id'),
      supabase
        .from('task_subtask_assignees')
        .select('subtask_id, user_id'),
      supabase
        .from('task_favorites')
        .select('task_id')
        .eq('user_id', currentUser.id),
      supabase
        .from('task_clients')
        .select('task_id, client_id'),
      supabase
        .from('task_tags')
        .select('task_id, tag_id'),
      supabase
        .from('tags')
        .select('id, name, is_builtin')
        .order('name', { ascending: true }),
      supabase
        .from('time_entries')
        .select('id, task_id, user_id, started_at, ended_at, duration_seconds'),
      supabase
        .from('project_members')
        .select('project_id, user_id, role'),
      supabase
        .from('profiles')
        .select('id, full_name, email'),
      supabase
        .from('clients')
        .select('id, name'),
    ]);

    if (tagsError) {
      setTags(
        BUILTIN_TAG_NAMES.map((name, index) => ({
          id: `local-${index}`,
          name,
          isBuiltin: true,
        }))
      );
    } else {
      setTags(
        (tagRows || []).map((tag) => ({
          id: tag.id,
          name: tag.name,
          isBuiltin: Boolean(tag.is_builtin),
        }))
      );
    }

    let resolvedTaskRows = taskRows;
    let resolvedTasksError = tasksError;
    const estimatedTimeColumnMissing =
      tasksError &&
      /estimated_seconds|estimated_budget|schema cache|does not exist/i.test(
        tasksError.message || ''
      );
    const scheduleColumnsMissing =
      tasksError &&
      /start_date|end_date|schema cache|does not exist/i.test(
        tasksError.message || ''
      );
    if (estimatedTimeColumnMissing || scheduleColumnsMissing) {
      const selectCols = estimatedTimeColumnMissing
        ? 'id, project_id, name, status, created_at'
        : 'id, project_id, name, status, estimated_seconds, created_at';
      const { data: fallbackTasks, error: fallbackError } = await supabase
        .from('tasks')
        .select(selectCols)
        .order('created_at', { ascending: true });
      resolvedTaskRows = fallbackTasks;
      resolvedTasksError = fallbackError;
    }

    let resolvedSubtaskRows = subtaskRows || [];
    let resolvedSubtasksError = subtasksError;
    const subtaskScheduleMissing =
      subtasksError &&
      /start_date|end_date|schema cache|does not exist/i.test(
        subtasksError.message || ''
      );
    if (subtaskScheduleMissing) {
      const { data: fallbackSubtasks, error: fallbackSubError } = await supabase
        .from('task_subtasks')
        .select('id, task_id, name, is_completed, sort_order, created_at')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      resolvedSubtaskRows = fallbackSubtasks || [];
      resolvedSubtasksError = fallbackSubError;
    }

    const followersMissing =
      followersError &&
      /task_followers|schema cache|does not exist/i.test(
        followersError.message || ''
      );
    const subtasksMissing =
      subtasksError &&
      /task_subtasks|schema cache|does not exist/i.test(
        subtasksError.message || ''
      );
    const subtaskTagsMissing =
      subtaskTagsError &&
      /task_subtask_tags|schema cache|does not exist/i.test(
        subtaskTagsError.message || ''
      );
    const subtaskAssigneesMissing =
      subtaskAssigneesError &&
      /task_subtask_assignees|schema cache|does not exist/i.test(
        subtaskAssigneesError.message || ''
      );
    const favoritesMissing =
      favoritesError &&
      /task_favorites|schema cache|does not exist/i.test(
        favoritesError.message || ''
      );

    const error =
      resolvedTasksError ||
      assigneesError ||
      timeError ||
      membersError ||
      profilesError ||
      (followersError && !followersMissing ? followersError : null) ||
      (resolvedSubtasksError && !subtasksMissing ? resolvedSubtasksError : null) ||
      (subtaskTagsError && !subtaskTagsMissing ? subtaskTagsError : null) ||
      (subtaskAssigneesError && !subtaskAssigneesMissing
        ? subtaskAssigneesError
        : null) ||
      (favoritesError && !favoritesMissing ? favoritesError : null);
    if (error) {
      setDatabaseError(error.message);
      return;
    }

    // Only one running timer per user. Close older open entries left behind by
    // interrupted Start/Save flows so the tracker does not show multiple "- now" rows.
    let resolvedTimeRows = timeRows || [];
    const openForUser = resolvedTimeRows
      .filter(
        (entry) => entry.user_id === currentUser.id && !entry.ended_at
      )
      .sort(
        (a, b) =>
          new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
      );
    if (openForUser.length > 1) {
      const endedAt = new Date();
      const orphanIds = new Set(openForUser.slice(1).map((entry) => entry.id));
      await Promise.all(
        openForUser.slice(1).map((row) => {
          const durationSeconds = Math.max(
            0,
            Math.floor(
              (endedAt.getTime() - new Date(row.started_at).getTime()) / 1000
            )
          );
          return supabase
            .from('time_entries')
            .update({
              ended_at: endedAt.toISOString(),
              duration_seconds: durationSeconds,
            })
            .eq('id', row.id);
        })
      );
      resolvedTimeRows = resolvedTimeRows.map((entry) => {
        if (!orphanIds.has(entry.id)) return entry;
        const durationSeconds = Math.max(
          0,
          Math.floor(
            (endedAt.getTime() - new Date(entry.started_at).getTime()) / 1000
          )
        );
        return {
          ...entry,
          ended_at: endedAt.toISOString(),
          duration_seconds: durationSeconds,
        };
      });
    }

    const resolvedFollowerRows = followersMissing ? [] : followerRows || [];
    if (subtasksMissing) {
      resolvedSubtaskRows = [];
    }
    const resolvedSubtaskTagRows = subtaskTagsMissing
      ? []
      : subtaskTagRows || [];
    const resolvedSubtaskAssigneeRows = subtaskAssigneesMissing
      ? []
      : subtaskAssigneeRows || [];
    const favoriteTaskIds = new Set(
      favoritesMissing
        ? []
        : (favoriteRows || []).map((row) => row.task_id)
    );

    const profilesById = new Map(
      (profileRows || []).map((profile) => [profile.id, profile])
    );
    const tagsById = new Map(
      (tagRows || []).map((tag) => [tag.id, tag.name])
    );
    const clientsById = new Map(
      (clientLabelRows || []).map((client) => [client.id, client.name])
    );

    // Anyone who already timed a task should be an assignee + project member.
    const assigneeKeySet = new Set(
      (assigneeRows || []).map((row) => `${row.task_id}:${row.user_id}`)
    );
    const memberKeySet = new Set(
      (memberRows || []).map((row) => `${row.project_id}:${row.user_id}`)
    );
    const tasksById = new Map((resolvedTaskRows || []).map((task) => [task.id, task]));
    const missingAssignees = [];
    const missingMembers = [];

    (resolvedTimeRows || []).forEach((entry) => {
      const task = tasksById.get(entry.task_id);
      if (!task) return;

      const assigneeKey = `${entry.task_id}:${entry.user_id}`;
      if (!assigneeKeySet.has(assigneeKey)) {
        missingAssignees.push({ task_id: entry.task_id, user_id: entry.user_id });
        assigneeKeySet.add(assigneeKey);
      }

      const memberKey = `${task.project_id}:${entry.user_id}`;
      if (!memberKeySet.has(memberKey)) {
        missingMembers.push({
          project_id: task.project_id,
          user_id: entry.user_id,
          role: 'employee',
        });
        memberKeySet.add(memberKey);
      }
    });

    if (missingAssignees.length > 0) {
      await supabase
        .from('task_assignees')
        .upsert(missingAssignees, {
          onConflict: 'task_id,user_id',
          ignoreDuplicates: true,
        });
    }
    if (missingMembers.length > 0) {
      await supabase
        .from('project_members')
        .upsert(missingMembers, {
          onConflict: 'project_id,user_id',
          ignoreDuplicates: true,
        });
    }

    const effectiveAssigneeRows = [
      ...(assigneeRows || []),
      ...missingAssignees,
    ];
    const effectiveMemberRows = [
      ...(memberRows || []),
      ...missingMembers,
    ];

    if (missingMembers.length > 0) {
      setProjects((previousProjects) =>
        previousProjects.map((project) => {
          const additions = missingMembers.filter(
            (member) => member.project_id === project.id
          );
          if (additions.length === 0) return project;

          const existingIds = new Set(
            (project.members || []).map((member) => member.id)
          );
          const newMembers = additions
            .filter((member) => !existingIds.has(member.user_id))
            .map((member) => {
              const profile = profilesById.get(member.user_id);
              return {
                id: member.user_id,
                name: profile?.full_name || profile?.email || 'Unknown user',
                email: profile?.email || '',
                role: 'Employee',
              };
            });

          return newMembers.length > 0
            ? { ...project, members: [...(project.members || []), ...newMembers] }
            : project;
        })
      );
    }

    const mappedTasks = (resolvedTaskRows || []).map((task) => {
      const assigneeUserIds = new Set(
        effectiveAssigneeRows
          .filter((assignee) => assignee.task_id === task.id)
          .map((assignee) => assignee.user_id)
      );
      // Also surface anyone who timed the task, even before DB sync finishes.
      (resolvedTimeRows || [])
        .filter((entry) => entry.task_id === task.id)
        .forEach((entry) => assigneeUserIds.add(entry.user_id));

      const assignees = Array.from(assigneeUserIds).map((userId) => {
        const profile = profilesById.get(userId);
        const membership = effectiveMemberRows.find(
          (member) =>
            member.project_id === task.project_id && member.user_id === userId
        );
        const name = profile?.full_name || profile?.email || 'Unknown user';
        const role = membership?.role === 'admin' ? 'Admin' : 'Employee';
        return `${name} (${role})`;
      });

      const followers = resolvedFollowerRows
        .filter((row) => row.task_id === task.id)
        .map((row) => {
          const profile = profilesById.get(row.user_id);
          const membership = effectiveMemberRows.find(
            (member) =>
              member.project_id === task.project_id &&
              member.user_id === row.user_id
          );
          const name = profile?.full_name || profile?.email || 'Unknown user';
          const role = membership?.role === 'admin' ? 'Admin' : 'Employee';
          return `${name} (${role})`;
        });

      const subtasks = resolvedSubtaskRows
        .filter((row) => row.task_id === task.id)
        .map((row) => {
          const subAssigneeUserIds = Array.from(
            new Set(
              resolvedSubtaskAssigneeRows
                .filter((item) => item.subtask_id === row.id)
                .map((item) => item.user_id)
            )
          );
          const subAssignees = subAssigneeUserIds.map((userId) => {
            const profile = profilesById.get(userId);
            const membership = (memberRows || []).find(
              (member) =>
                member.project_id === task.project_id &&
                member.user_id === userId
            );
            const name = profile?.full_name || profile?.email || 'Unknown user';
            const role = membership?.role === 'admin' ? 'Admin' : 'Employee';
            return `${name} (${role})`;
          });
          return {
            id: row.id,
            name: row.name,
            isCompleted: Boolean(row.is_completed),
            status: normalizeLegacyTaskStatus(
              row.status || (row.is_completed ? 'Done' : 'To Do')
            ),
            sortOrder: row.sort_order || 0,
            startDate: toDateOnlyValue(row.start_date),
            endDate: toDateOnlyValue(row.end_date),
            assignees: subAssignees.length > 0 ? subAssignees : ['Unassigned'],
            tags: resolvedSubtaskTagRows
              .filter((tagRow) => tagRow.subtask_id === row.id)
              .map((tagRow) => ({
                id: tagRow.tag_id,
                name: tagsById.get(tagRow.tag_id) || 'Tag',
              })),
          };
        });

      const taskClients = clientsError
        ? []
        : (clientRows || [])
            .filter((client) => client.task_id === task.id)
            .map((client) => ({
              id: client.client_id,
              name: clientsById.get(client.client_id) || 'Client',
            }));

      const taskTags = taskTagsError
        ? []
        : (taskTagRows || [])
            .filter((row) => row.task_id === task.id)
            .map((row) => ({
              id: row.tag_id,
              name: tagsById.get(row.tag_id) || 'Tag',
            }));

      const userEntries = (resolvedTimeRows || []).filter(
        (entry) => entry.task_id === task.id && entry.user_id === currentUser.id
      );
      const completedSeconds = userEntries
        .filter((entry) => entry.ended_at)
        .reduce(
          (total, entry) => total + (entry.duration_seconds || 0),
          0
        );
      const openEntries = userEntries
        .filter((entry) => !entry.ended_at)
        .sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      const activeEntry = openEntries[0] || null;
      const activeSeconds = activeEntry
        ? Math.max(
            0,
            Math.floor(
              (Date.now() - new Date(activeEntry.started_at).getTime()) / 1000
            )
          )
        : 0;

      const allTaskEntries = (resolvedTimeRows || []).filter(
        (entry) => entry.task_id === task.id
      );
      const totalTrackedCompletedSeconds = allTaskEntries
        .filter((entry) => entry.ended_at)
        .reduce(
          (total, entry) => total + (entry.duration_seconds || 0),
          0
        );
      const totalTrackedOpenStartedAts = allTaskEntries
        .filter((entry) => !entry.ended_at && entry.started_at)
        .map((entry) => entry.started_at);

      const trackerNames = Array.from(
        new Set(
          allTaskEntries
            .map((entry) => {
              const profile = profilesById.get(entry.user_id);
              return profile?.full_name || profile?.email || null;
            })
            .filter(Boolean)
        )
      );

      return {
        id: task.id,
        projectId: task.project_id,
        name: task.name,
        status: normalizeLegacyTaskStatus(task.status),
        estimatedSeconds:
          task.estimated_seconds == null || task.estimated_seconds === ''
            ? null
            : Number(task.estimated_seconds),
        startDate: toDateOnlyValue(task.start_date),
        endDate: toDateOnlyValue(task.end_date),
        assignees: assignees.length > 0 ? assignees : ['Unassigned'],
        followers,
        subtasks,
        isFavorite: favoriteTaskIds.has(task.id),
        clients: taskClients,
        tags: taskTags,
        timeTrackers: trackerNames,
        completedSeconds,
        timeSpent: completedSeconds + activeSeconds,
        totalTrackedCompletedSeconds,
        totalTrackedOpenStartedAts,
        isRunning: Boolean(activeEntry),
        activeTimeEntryId: activeEntry?.id || null,
        activeStartedAt: activeEntry?.started_at || null,
      };
    });

    setTasks(mappedTasks);

    const userTimeRows = (resolvedTimeRows || []).filter(
      (entry) => entry.user_id === currentUser.id
    );
    const now = new Date();
    setTrackerEntries(
      userTimeRows
        .map((entry) => {
          const task = tasksById.get(entry.task_id);
          const start = new Date(entry.started_at);
          const end = entry.ended_at ? new Date(entry.ended_at) : null;
          const durationSeconds = entry.ended_at
            ? entry.duration_seconds ||
              Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))
            : Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000));
          return {
            id: entry.id,
            taskId: entry.task_id,
            taskName: task?.name || 'Untitled',
            projectId: task?.project_id || null,
            startedAt: start,
            endedAt: end,
            durationSeconds,
            isRunning: !entry.ended_at,
            isFavorite: favoriteTaskIds.has(entry.task_id),
          };
        })
        .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
    );
    setWeekTrackedSeconds(
      computeTrackedInRange(
        userTimeRows,
        getStartOfWeek(now, weekStartsMonday),
        now
      )
    );
  }, [currentUser.id, weekStartsMonday]);

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    const { data, error } = await supabase
      .from('clients')
      .select('id, name, created_at, archived_at')
      .order('name', { ascending: true });

    if (error) {
      // Fallback for databases that have not run the archive migration yet.
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('clients')
        .select('id, name, created_at')
        .order('name', { ascending: true });

      if (fallbackError) {
        setClients([]);
        setClientsLoading(false);
        return;
      }

      setClients(
        (fallbackData || []).map((client) => ({
          id: client.id,
          name: client.name,
          archivedAt: null,
        }))
      );
      setClientsLoading(false);
      return;
    }

    setClients(
      (data || []).map((client) => ({
        id: client.id,
        name: client.name,
        archivedAt: client.archived_at || null,
      }))
    );
    setClientsLoading(false);
  }, []);

  const loadAdminReport = useCallback(async () => {
    if (currentUser.role !== 'admin') {
      setReportRows([]);
      return;
    }

    setReportLoading(true);
    const [
      { data: timeRows, error: timeError },
      { data: taskRows, error: tasksError },
      { data: projectRows, error: projectsError },
      { data: profileRows, error: profilesError },
      { data: taskClientRows },
      { data: clientRows },
      { data: taskTagRows },
      { data: tagRows },
      { data: projectTagRows },
      { data: subtaskRows },
      { data: subtaskTagRows },
    ] = await Promise.all([
      supabase
        .from('time_entries')
        .select('id, task_id, user_id, started_at, ended_at, duration_seconds'),
      supabase.from('tasks').select('id, project_id, name'),
      supabase.from('projects').select('id, name, client_id'),
      supabase.from('profiles').select('id, full_name, email'),
      supabase.from('task_clients').select('task_id, client_id'),
      supabase.from('clients').select('id, name'),
      supabase.from('task_tags').select('task_id, tag_id'),
      supabase.from('tags').select('id, name'),
      supabase.from('project_tags').select('project_id, tag_id'),
      supabase.from('task_subtasks').select('id, task_id'),
      supabase.from('task_subtask_tags').select('subtask_id, tag_id'),
    ]);

    const error = timeError || tasksError || projectsError || profilesError;
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not load dashboard report'));
      setReportRows([]);
      setReportLoading(false);
      return;
    }

    const tasksById = new Map((taskRows || []).map((task) => [task.id, task]));
    const projectsById = new Map(
      (projectRows || []).map((project) => [
        project.id,
        { name: project.name, clientId: project.client_id || null },
      ])
    );
    const profilesById = new Map(
      (profileRows || []).map((profile) => [profile.id, profile])
    );
    const clientsById = new Map(
      (clientRows || []).map((client) => [client.id, client.name])
    );
    const tagsById = new Map((tagRows || []).map((tag) => [tag.id, tag.name]));
    const clientsByTask = new Map();
    (taskClientRows || []).forEach((row) => {
      const list = clientsByTask.get(row.task_id) || [];
      list.push({
        id: row.client_id,
        name: clientsById.get(row.client_id) || 'Client',
      });
      clientsByTask.set(row.task_id, list);
    });

    const tagsByTask = new Map();
    (taskTagRows || []).forEach((row) => {
      const list = tagsByTask.get(row.task_id) || [];
      const name = tagsById.get(row.tag_id);
      if (!name) return;
      if (!list.some((tag) => tag.id === row.tag_id)) {
        list.push({ id: row.tag_id, name });
      }
      tagsByTask.set(row.task_id, list);
    });

    // Include subtask tags on the parent task for reporting filters.
    const subtaskParentById = new Map(
      (subtaskRows || []).map((row) => [row.id, row.task_id])
    );
    (subtaskTagRows || []).forEach((row) => {
      const taskId = subtaskParentById.get(row.subtask_id);
      if (!taskId) return;
      const list = tagsByTask.get(taskId) || [];
      const name = tagsById.get(row.tag_id);
      if (!name) return;
      if (!list.some((tag) => tag.id === row.tag_id)) {
        list.push({ id: row.tag_id, name });
      }
      tagsByTask.set(taskId, list);
    });

    // Fall back to project service tags when a task has none.
    const tagsByProject = new Map();
    (projectTagRows || []).forEach((row) => {
      const list = tagsByProject.get(row.project_id) || [];
      const name = tagsById.get(row.tag_id);
      if (!name) return;
      if (!list.some((tag) => tag.id === row.tag_id)) {
        list.push({ id: row.tag_id, name });
      }
      tagsByProject.set(row.project_id, list);
    });

    const now = new Date();
    const mapped = (timeRows || []).map((entry) => {
      const task = tasksById.get(entry.task_id);
      const projectId = task?.project_id || null;
      const project = projectId ? projectsById.get(projectId) : null;
      const profile = profilesById.get(entry.user_id);
      const start = new Date(entry.started_at);
      const end = entry.ended_at ? new Date(entry.ended_at) : now;
      const durationSeconds = entry.ended_at
        ? entry.duration_seconds ||
          Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))
        : Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000));

      const taskClients = clientsByTask.get(entry.task_id) || [];
      const projectClientId = project?.clientId || null;
      const resolvedClients = [...taskClients];
      if (
        projectClientId &&
        !resolvedClients.some((client) => client.id === projectClientId)
      ) {
        resolvedClients.unshift({
          id: projectClientId,
          name: clientsById.get(projectClientId) || 'Client',
        });
      }

      const taskTags = tagsByTask.get(entry.task_id) || [];
      const resolvedTags =
        taskTags.length > 0
          ? taskTags
          : projectId
            ? tagsByProject.get(projectId) || []
            : [];

      return {
        id: entry.id,
        taskId: entry.task_id,
        taskName: task?.name || 'Unknown task',
        projectId,
        projectName: project?.name || (projectId ? 'Project' : 'Without project'),
        userId: entry.user_id,
        userName: profile?.full_name || profile?.email || 'Unknown user',
        startedAt: start,
        endedAt: entry.ended_at ? end : null,
        durationSeconds,
        clients: resolvedClients,
        tags: resolvedTags,
      };
    });

    setReportRows(mapped);
    setReportLoading(false);
  }, [currentUser.role]);

  useEffect(() => {
    loadProjects();
    loadTasks();
    loadClients();
  }, [loadProjects, loadTasks, loadClients]);

  useEffect(() => {
    if (activeMenu === 'dashboard' && currentUser.role === 'admin') {
      loadAdminReport();
    }
  }, [activeMenu, currentUser.role, loadAdminReport]);

  // Employees/clients should not remain on admin-only screens.
  useEffect(() => {
    if (isSystemAdmin) return;
    if (
      activeMenu === 'clients' ||
      activeMenu === 'team' ||
      activeMenu === 'dashboard'
    ) {
      setActiveMenu('time-tracker');
      setActiveProjectId(null);
    }
  }, [activeMenu, isSystemAdmin]);

  // Tick once per second so live timers re-render from wall clock (never +1 counters).
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setClockNow(Date.now());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  const formatTime = (totalSeconds) => {
    const raw = Math.floor(Number(totalSeconds) || 0);
    const sign = raw < 0 ? '-' : '';
    const abs = Math.abs(raw);
    const hours = Math.floor(abs / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((abs % 3600) / 60).toString().padStart(2, '0');
    const seconds = (abs % 60).toString().padStart(2, '0');
    return `${sign}${hours}:${minutes}:${seconds}`;
  };

  /** Live task total from completed entries + wall-clock active session (accurate; Strict Mode safe). */
  const getTaskLiveSeconds = (task, nowMs = clockNow) => {
    if (!task) return 0;
    const completed = Math.floor(
      Number(
        task.completedSeconds != null
          ? task.completedSeconds
          : task.isRunning
            ? 0
            : task.timeSpent || 0
      ) || 0
    );
    if (task.isRunning && task.activeStartedAt) {
      const started = new Date(task.activeStartedAt).getTime();
      if (!Number.isNaN(started)) {
        return (
          completed + Math.max(0, Math.floor((nowMs - started) / 1000))
        );
      }
    }
    if (task.completedSeconds != null) return completed;
    return Math.floor(Number(task.timeSpent) || 0);
  };

  /** Team total tracked time on a task (all members), wall-clock accurate for open timers. */
  const getTaskTotalTrackedSeconds = (task, nowMs = clockNow) => {
    if (!task) return 0;
    const completed = Math.floor(
      Number(task.totalTrackedCompletedSeconds) || 0
    );
    const openStarts = Array.isArray(task.totalTrackedOpenStartedAts)
      ? task.totalTrackedOpenStartedAts
      : [];
    if (openStarts.length === 0) {
      // Fallback when older task objects lack total fields.
      return task.totalTrackedCompletedSeconds != null
        ? completed
        : getTaskLiveSeconds(task, nowMs);
    }
    const openSeconds = openStarts.reduce((total, startedAt) => {
      const started = new Date(startedAt).getTime();
      if (Number.isNaN(started)) return total;
      return total + Math.max(0, Math.floor((nowMs - started) / 1000));
    }, 0);
    return completed + openSeconds;
  };

  const formatActivityTime = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 60_000) return 'Just now';
    if (diffMs < 3_600_000) return `${Math.max(1, Math.floor(diffMs / 60_000))}m ago`;
    if (diffMs < 86_400_000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return `${date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const ACTIVITY_MAX_FILES = 5;
  const ACTIVITY_MAX_FILE_BYTES = 10 * 1024 * 1024;
  const ACTIVITY_ALLOWED_MIME = new Set([
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
  ]);

  const revokePendingFileUrls = (items) => {
    (items || []).forEach((item) => {
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
  };

  const mapAttachmentRow = (row, signedUrl = null) => ({
    id: row.id,
    fileName: row.file_name,
    mimeType: row.mime_type || 'application/octet-stream',
    sizeBytes: row.size_bytes || 0,
    filePath: row.file_path,
    url: signedUrl,
    isImage: String(row.mime_type || '').startsWith('image/'),
  });

  const mapActivityRow = (row, extras = {}) => ({
    id: row.id,
    text: row.action || row.details?.message || 'Activity',
    time: formatActivityTime(row.created_at),
    createdAt: new Date(row.created_at),
    actorId: row.actor_id || null,
    parentId: row.parent_id || null,
    taskId: row.task_id || row.details?.task_id || null,
    taskName: extras.taskName || row.details?.task_name || null,
    linkUrl: row.details?.link_url || extras.linkUrl || null,
    kind: row.details?.kind || extras.kind || null,
    editedAt: row.details?.edited_at || null,
    details: row.details && typeof row.details === 'object' ? row.details : {},
    mentions: Array.isArray(row.details?.mentions)
      ? row.details.mentions
      : Array.isArray(extras.mentions)
        ? extras.mentions
        : [],
    attachments: extras.attachments || [],
    replies: extras.replies || [],
  });

  const signActivityAttachments = async (rows) => {
    if (!rows?.length) return [];
    return Promise.all(
      rows.map(async (row) => {
        const { data } = await supabase.storage
          .from('activity-attachments')
          .createSignedUrl(row.file_path, 60 * 60);
        return mapAttachmentRow(row, data?.signedUrl || null);
      })
    );
  };

  const loadAttachmentsForActivities = async (activityIds) => {
    if (!activityIds.length) return {};
    const { data, error } = await supabase
      .from('activity_attachments')
      .select('id, activity_id, file_path, file_name, mime_type, size_bytes')
      .in('activity_id', activityIds);
    if (error || !data?.length) return {};

    const signed = await signActivityAttachments(data);
    return signed.reduce((acc, attachment, index) => {
      const activityId = data[index].activity_id;
      if (!acc[activityId]) acc[activityId] = [];
      acc[activityId].push(attachment);
      return acc;
    }, {});
  };

  const nestActivities = (rows, attachmentsByActivityId = {}) => {
    const mapped = (rows || []).map((row) =>
      mapActivityRow(row, {
        attachments: attachmentsByActivityId[row.id] || [],
        replies: [],
      })
    );
    const byId = new Map(mapped.map((item) => [item.id, item]));
    const roots = [];

    mapped.forEach((item) => {
      if (item.parentId && byId.has(item.parentId)) {
        byId.get(item.parentId).replies.push(item);
      } else if (!item.parentId) {
        roots.push(item);
      } else {
        // Orphan reply (parent outside limit) — still show as root.
        roots.push(item);
      }
    });

    roots.forEach((root) => {
      root.replies.sort((a, b) => a.createdAt - b.createdAt);
    });
    roots.sort((a, b) => b.createdAt - a.createdAt);
    return roots;
  };

  const loadProjectActivities = useCallback(
    async (projectId) => {
      if (!projectId) {
        setActivities([]);
        return;
      }
      setActivitiesLoading(true);
      const { data, error } = await supabase
        .from('activities')
        .select('id, action, details, created_at, actor_id, parent_id, task_id')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        setActivities([]);
        setActivitiesLoading(false);
        return;
      }

      const rows = data || [];
      const attachmentsByActivityId = await loadAttachmentsForActivities(
        rows.map((row) => row.id)
      );
      setActivities(nestActivities(rows, attachmentsByActivityId));
      setActivitiesLoading(false);
    },
    []
  );

  const sanitizeActivityFileName = (name) =>
    String(name || 'file')
      .replace(/[^\w.\-]+/g, '_')
      .slice(0, 120);

  const validateActivityFiles = (files) => {
    const accepted = [];
    for (const file of files) {
      if (!file) continue;
      if (file.size > ACTIVITY_MAX_FILE_BYTES) {
        setDatabaseError(`"${file.name}" is larger than 10MB.`);
        continue;
      }
      const mime = file.type || 'application/octet-stream';
      if (!ACTIVITY_ALLOWED_MIME.has(mime) && !mime.startsWith('image/')) {
        setDatabaseError(`"${file.name}" type is not supported.`);
        continue;
      }
      accepted.push(file);
    }
    return accepted;
  };

  const addPendingActivityFiles = (incomingFiles, setter) => {
    const validated = validateActivityFiles(incomingFiles);
    if (!validated.length) return;
    setter((prev) => {
      const room = Math.max(0, ACTIVITY_MAX_FILES - prev.length);
      const nextFiles = validated.slice(0, room).map((file) => ({
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        previewUrl: file.type?.startsWith('image/')
          ? URL.createObjectURL(file)
          : null,
      }));
      if (validated.length > room) {
        setDatabaseError(`You can attach up to ${ACTIVITY_MAX_FILES} files per post.`);
      }
      return [...prev, ...nextFiles];
    });
  };

  const removePendingActivityFile = (id, setter) => {
    setter((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleActivityPaste = (e, setter) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter((item) => item.type?.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean)
      .map(
        (file, index) =>
          new File(
            [file],
            `screenshot-${Date.now()}-${index + 1}.${(file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`,
            { type: file.type }
          )
      );
    if (!imageFiles.length) return;
    e.preventDefault();
    addPendingActivityFiles(imageFiles, setter);
  };

  const uploadActivityAttachments = async (projectId, activityId, pendingFiles) => {
    if (!pendingFiles?.length || !projectId || !activityId || !currentUser?.id) {
      return [];
    }

    const uploaded = [];
    for (const pending of pendingFiles) {
      const file = pending.file;
      const safeName = sanitizeActivityFileName(file.name);
      const storagePath = `${projectId}/${activityId}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('activity-attachments')
        .upload(storagePath, file, {
          contentType: file.type || 'application/octet-stream',
          upsert: false,
        });
      if (uploadError) {
        setDatabaseError(uploadError.message || 'Could not upload attachment');
        continue;
      }

      const { data: row, error: insertError } = await supabase
        .from('activity_attachments')
        .insert({
          activity_id: activityId,
          project_id: projectId,
          uploaded_by: currentUser.id,
          file_path: storagePath,
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size || 0,
        })
        .select('id, activity_id, file_path, file_name, mime_type, size_bytes')
        .single();

      if (insertError || !row) {
        setDatabaseError(insertError?.message || 'Could not save attachment');
        continue;
      }

      const signed = await signActivityAttachments([row]);
      uploaded.push(signed[0]);
    }
    return uploaded;
  };

  const logActivity = async (text, options = {}) => {
    const message = String(text || '').trim();
    const linkUrl = String(
      options.linkUrl || options.details?.link_url || ''
    ).trim();
    if (!message && !(options.files || []).length && !linkUrl) return null;

    const projectId = options.projectId || activeProjectId || null;
    const taskId = options.taskId || null;
    const parentId = options.parentId || null;
    const pendingFiles = options.files || [];
    const taskName =
      options.taskName ||
      tasks.find((task) => task.id === taskId)?.name ||
      null;
    const displayMessage =
      message ||
      (pendingFiles.length
        ? `${currentUser?.name || 'Someone'} shared ${pendingFiles.length} attachment${pendingFiles.length === 1 ? '' : 's'}`
        : linkUrl
          ? `${currentUser?.name || 'Someone'} shared a link`
          : '');

    const details = {
      message: displayMessage,
      ...(options.details && typeof options.details === 'object'
        ? options.details
        : {}),
      ...(parentId ? { parent_id: parentId } : {}),
      ...(pendingFiles.length ? { has_attachments: true } : {}),
      ...(taskId ? { task_id: taskId, task_name: taskName } : {}),
      ...(linkUrl ? { link_url: linkUrl } : {}),
    };
    const optimisticId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimisticAttachments = pendingFiles.map((pending) => ({
      id: pending.id,
      fileName: pending.file.name,
      mimeType: pending.file.type || 'application/octet-stream',
      sizeBytes: pending.file.size || 0,
      filePath: null,
      url: pending.previewUrl,
      isImage: Boolean(pending.file.type?.startsWith('image/')),
    }));

    if (projectId && projectId === activeProjectId) {
      const optimistic = {
        id: optimisticId,
        text: displayMessage,
        time: 'Just now',
        createdAt: new Date(),
        actorId: currentUser.id,
        parentId,
        taskId,
        taskName,
        linkUrl: linkUrl || null,
        mentions: Array.isArray(details.mentions) ? details.mentions : [],
        kind: details.kind || null,
        editedAt: details.edited_at || null,
        details,
        attachments: optimisticAttachments,
        replies: [],
      };
      if (parentId) {
        setActivities((prev) =>
          prev.map((item) =>
            item.id === parentId
              ? { ...item, replies: [...item.replies, optimistic] }
              : item
          )
        );
      } else {
        setActivities((prev) => [optimistic, ...prev]);
      }
    }

    if (!projectId || !currentUser?.id) return null;

    const { data, error } = await supabase
      .from('activities')
      .insert({
        project_id: projectId,
        task_id: taskId,
        actor_id: currentUser.id,
        parent_id: parentId,
        action: displayMessage,
        details,
      })
      .select('id, action, details, created_at, actor_id, parent_id, task_id')
      .single();

    if (error || !data) {
      // Keep optimistic entry; feed still works locally if insert is blocked.
      return null;
    }

    const uploaded = await uploadActivityAttachments(
      projectId,
      data.id,
      pendingFiles
    );
    const mapped = mapActivityRow(data, {
      attachments: uploaded,
      replies: [],
      taskName,
      linkUrl: linkUrl || null,
    });

    if (projectId === activeProjectId) {
      setActivities((prev) => {
        const replaceOptimistic = (list) =>
          list
            .filter((item) => item.id !== optimisticId)
            .map((item) =>
              item.id === data.id
                ? { ...mapped, replies: item.replies || [] }
                : item
            );

        if (parentId) {
          return prev.map((item) => {
            if (item.id !== parentId) return item;
            const replies = replaceOptimistic(item.replies);
            if (!replies.some((reply) => reply.id === data.id)) {
              replies.push(mapped);
              replies.sort((a, b) => a.createdAt - b.createdAt);
            }
            return { ...item, replies };
          });
        }

        const withoutOptimistic = replaceOptimistic(prev);
        if (withoutOptimistic.some((item) => item.id === data.id)) {
          return withoutOptimistic;
        }
        return [mapped, ...withoutOptimistic];
      });
    }

    const notifyType = resolveNotifyEventType(options, details, taskId);
    if (notifyType) {
      void dispatchProjectNotification({
        eventType: notifyType,
        projectId,
        taskId,
        message: displayMessage,
        title: options.notifyTitle,
        recipientUserIds: options.notifyUserIds,
      });
    }

    return mapped;
  };

  const resolveNotifyEventType = (options = {}, details = {}, taskId = null) => {
    if (options.notify === false) return null;
    if (typeof options.notify === 'string' && options.notify.trim()) {
      return options.notify.trim();
    }
    if (details?.kind === 'post') return 'feed_post';
    if (details?.kind === 'reply') return 'feed_reply';
    if (details?.source === 'client' || details?.time_entry_id) return null;
    if (details?.skipNotify) return null;
    if (taskId) return 'task_update';
    return 'project_update';
  };

  const dispatchProjectNotification = async ({
    eventType,
    projectId,
    taskId = null,
    message,
    title,
    recipientUserIds,
  }) => {
    if (!eventType || !projectId || !message || !currentUser?.id) return;
    try {
      const { data, error } = await supabase.functions.invoke(
        'notify-project-event',
        {
          body: {
            eventType,
            projectId,
            taskId,
            message,
            title: title || undefined,
            recipientUserIds: recipientUserIds || undefined,
            actorId: currentUser.id,
            excludeUserIds: [currentUser.id],
          },
        }
      );
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('Project notification failed:', error.message || error);
        return;
      }
      if (data?.error) {
        // eslint-disable-next-line no-console
        console.warn('Project notification failed:', data.error);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Project notification failed:',
        error?.message || error
      );
    }
  };

  // Keep Settings toggle in sync with persisted profile preference.
  useEffect(() => {
    if (currentUser) {
      setNotificationsEnabled(currentUser.emailNotificationsEnabled !== false);
    }
  }, [currentUser?.id, currentUser?.emailNotificationsEnabled]);

  const handleToggleEmailNotifications = async () => {
    const next = !notificationsEnabled;
    setNotificationsEnabled(next);
    try {
      await updateEmailNotificationsEnabled(next);
    } catch (error) {
      setNotificationsEnabled(!next);
      setDatabaseError(
        toUserAlert(error, 'Could not update email notification preference')
      );
    }
  };

  const logSavedHoursActivity = async ({
    projectId,
    taskId,
    taskName,
    durationSeconds,
    timeEntryId,
  }) => {
    const seconds = Math.max(0, Math.floor(Number(durationSeconds) || 0));
    if (!projectId || seconds <= 0) return;

    // DB trigger may already have written this row; skip a duplicate insert.
    if (timeEntryId) {
      const { data: existing } = await supabase
        .from('activities')
        .select('id, action, details, created_at, actor_id, parent_id')
        .eq('project_id', projectId)
        .filter('details->>time_entry_id', 'eq', String(timeEntryId))
        .limit(1)
        .maybeSingle();

      if (existing) {
        if (projectId === activeProjectId) {
          setActivities((prev) => {
            if (prev.some((item) => item.id === existing.id)) return prev;
            const withoutLocalDupes = prev.filter((item) => {
              if (!String(item.id).startsWith('local-')) return true;
              return item.text !== (existing.action || existing.details?.message);
            });
            return [mapActivityRow(existing), ...withoutLocalDupes];
          });
        }
        return;
      }
    }

    const label = String(taskName || 'task').trim() || 'task';
    await logActivity(
      `${currentUser.name} saved ${formatTime(seconds)} on "${label}"`,
      {
        projectId,
        taskId: taskId || null,
        notify: false,
        details: {
          duration_seconds: seconds,
          ...(timeEntryId ? { time_entry_id: timeEntryId } : {}),
          source: 'client',
        },
      }
    );
  };

  useEffect(() => {
    loadProjectActivities(activeProjectId);
    setActivityFeedFilterTask(false);
    setActivityFeedFilterFiles(false);
    setActivityFeedSort('newest');
    setEditingActivityId(null);
    setEditActivityText('');
    setEditActivityTaskId('');
    setEditActivityLinkUrl('');
    setReplyToId(null);
  }, [activeProjectId, loadProjectActivities]);

  useEffect(() => {
    if (!activeProjectId) return undefined;

    const channel = supabase
      .channel(`project-activities-${activeProjectId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities',
          filter: `project_id=eq.${activeProjectId}`,
        },
        async (payload) => {
          const row = payload.new;
          if (!row?.id) return;

          const attachmentsByActivityId = await loadAttachmentsForActivities([
            row.id,
          ]);
          const mapped = mapActivityRow(row, {
            attachments: attachmentsByActivityId[row.id] || [],
            replies: [],
          });

          setActivities((prev) => {
            const existsRoot = prev.some((item) => item.id === row.id);
            const existsReply = prev.some((item) =>
              item.replies?.some((reply) => reply.id === row.id)
            );
            if (existsRoot || existsReply) return prev;

            const withoutLocalDupes = prev
              .map((item) => ({
                ...item,
                replies: (item.replies || []).filter((reply) => {
                  if (!String(reply.id).startsWith('local-')) return true;
                  return reply.text !== mapped.text;
                }),
              }))
              .filter((item) => {
                if (!String(item.id).startsWith('local-')) return true;
                return item.text !== mapped.text;
              });

            if (row.parent_id) {
              return withoutLocalDupes.map((item) => {
                if (item.id !== row.parent_id) return item;
                return {
                  ...item,
                  replies: [...(item.replies || []), mapped].sort(
                    (a, b) => a.createdAt - b.createdAt
                  ),
                };
              });
            }

            return [mapped, ...withoutLocalDupes];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'activities',
          filter: `project_id=eq.${activeProjectId}`,
        },
        async (payload) => {
          const row = payload.new;
          if (!row?.id) return;
          const attachmentsByActivityId = await loadAttachmentsForActivities([
            row.id,
          ]);
          const mapped = mapActivityRow(row, {
            attachments: attachmentsByActivityId[row.id] || [],
          });
          setActivities((prev) =>
            prev.map((item) => {
              if (item.id === row.id) {
                return {
                  ...mapped,
                  attachments:
                    mapped.attachments.length > 0
                      ? mapped.attachments
                      : item.attachments || [],
                  replies: item.replies || [],
                };
              }
              return {
                ...item,
                replies: (item.replies || []).map((reply) =>
                  reply.id === row.id
                    ? {
                        ...mapped,
                        attachments:
                          mapped.attachments.length > 0
                            ? mapped.attachments
                            : reply.attachments || [],
                      }
                    : reply
                ),
              };
            })
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_attachments',
          filter: `project_id=eq.${activeProjectId}`,
        },
        async (payload) => {
          const row = payload.new;
          if (!row?.id || !row.activity_id) return;
          const [attachment] = await signActivityAttachments([row]);
          if (!attachment) return;
          setActivities((prev) =>
            prev.map((item) => {
              if (item.id === row.activity_id) {
                if (item.attachments.some((a) => a.id === attachment.id)) {
                  return item;
                }
                return {
                  ...item,
                  attachments: [...item.attachments, attachment],
                };
              }
              return {
                ...item,
                replies: (item.replies || []).map((reply) => {
                  if (reply.id !== row.activity_id) return reply;
                  if (reply.attachments.some((a) => a.id === attachment.id)) {
                    return reply;
                  }
                  return {
                    ...reply,
                    attachments: [...reply.attachments, attachment],
                  };
                }),
              };
            })
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeProjectId]);

  useEffect(() => {
    return () => {
      revokePendingFileUrls(commentFiles);
      revokePendingFileUrls(replyFiles);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 5. System Execution Action Operations Handlers
  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!canCreateProjects) return;
    if (!newProjectName.trim()) return;

    setProjectSaving(true);
    setDatabaseError('');

    let clientId = newProjectClientId;
    let clientName = activeClients.find((client) => client.id === clientId)?.name || '';

    if (newProjectClientMode === 'create') {
      if (!isSystemAdmin) {
        setDatabaseError({
          title: 'Admin required to create clients',
          message:
            'Only Team admins can create new clients. Select an existing client for this project.',
          steps: [
            'Choose a client from the dropdown.',
            'Ask a Team admin if you need a new client added first.',
          ],
        });
        setProjectSaving(false);
        return;
      }
      const createdName = newProjectClientName.trim();
      if (!createdName) {
        setDatabaseError({
          title: 'Client required',
          message: 'Enter a client name before creating this project space.',
          steps: [
            'Type the new client name in the Client field.',
            'Or open the Client dropdown and create a new client at the bottom.',
            'Then click Create a Project again.',
          ],
        });
        setProjectSaving(false);
        return;
      }

      const existing = activeClients.find(
        (client) => client.name.toLowerCase() === createdName.toLowerCase()
      );
      if (existing) {
        clientId = existing.id;
        clientName = existing.name;
      } else {
        const { data: createdClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            name: createdName,
            created_by: currentUser.id,
          })
          .select('id, name')
          .single();

        if (clientError) {
          setDatabaseError(toUserAlert(clientError, 'Could not create client'));
          setProjectSaving(false);
          return;
        }
        clientId = createdClient.id;
        clientName = createdClient.name;
        await loadClients();
      }
    } else if (!clientId) {
      setDatabaseError({
        title: 'Client required',
        message: 'Select a client before creating this project space.',
        steps: [
          'Choose an existing client from the dropdown.',
          isSystemAdmin
            ? 'Or use “Create a new client” at the bottom of the Client dropdown.'
            : 'Ask a Team admin if you need a new client added first.',
          'Then click Create a Project again.',
        ],
      });
      setProjectSaving(false);
      return;
    }

    const projectName = newProjectName.trim();
    const { data: createdProject, error } = await supabase
      .from('projects')
      .insert({
        name: projectName,
        description: newProjectDesc.trim() || 'No description provided.',
        created_by: currentUser.id,
        client_id: clientId,
      })
      .select('id')
      .single();

    let projectId = createdProject?.id || null;
    if (error || !projectId) {
      const lower = normalizeErrorText(error).toLowerCase();
      const maybeSelectBlocked =
        !error ||
        lower.includes('row-level security') ||
        lower.includes('pgrst116') ||
        lower.includes('0 rows') ||
        lower.includes('permission');
      if (maybeSelectBlocked) {
        const { data: ownedRows } = await supabase
          .from('projects')
          .select('id')
          .eq('created_by', currentUser.id)
          .eq('name', projectName)
          .order('created_at', { ascending: false })
          .limit(1);
        projectId = ownedRows?.[0]?.id || null;
      }
      if (!projectId) {
        setDatabaseError(
          toUserAlert(
            error || {
              message:
                'Could not create project. Run 20260720212000_project_creator_can_select.sql in Supabase.',
            },
            'Could not create project'
          )
        );
        setProjectSaving(false);
        return;
      }
    }

    if (clientId && projectId) {
      await supabase.from('project_clients').upsert(
        { project_id: projectId, client_id: clientId },
        { onConflict: 'project_id,client_id', ignoreDuplicates: true }
      );
    }

    if (projectId) {
      await supabase.from('project_status_options').upsert(
        DEFAULT_PROJECT_STATUSES.map((item) => ({
          project_id: projectId,
          name: item.name,
          color: item.color,
          sort_order: item.sortOrder,
        })),
        { onConflict: 'project_id,name', ignoreDuplicates: true }
      );
      await supabase.from('task_status_options').upsert(
        DEFAULT_TASK_STATUSES.map((item) => ({
          project_id: projectId,
          name: item.name,
          color: item.color,
          sort_order: item.sortOrder,
        })),
        { onConflict: 'project_id,name', ignoreDuplicates: true }
      );
      await supabase
        .from('projects')
        .update({ status: 'Active' })
        .eq('id', projectId);
    }

    if (selectedProjectServices.length > 0 && projectId) {
      const tagIds = [];
      for (const serviceName of selectedProjectServices) {
        const existing = tags.find(
          (tag) =>
            tag.name.toLowerCase() === serviceName.toLowerCase() &&
            !String(tag.id).startsWith('local-')
        );
        if (existing) {
          tagIds.push(existing.id);
          continue;
        }
        const isMatrixService = MASTER_TASK_MATRIX.some((group) =>
          group.services.some(
            (service) => service.toLowerCase() === serviceName.toLowerCase()
          )
        );
        const { data: upserted, error: tagError } = await supabase
          .from('tags')
          .upsert(
            {
              name: serviceName,
              is_builtin: isMatrixService,
              created_by: isMatrixService ? null : currentUser.id,
            },
            { onConflict: 'name' }
          )
          .select('id')
          .single();
        if (tagError) {
          setDatabaseError(
            toUserAlert(tagError, `Could not prepare tag "${serviceName}"`)
          );
          setProjectSaving(false);
          await loadProjects();
          return;
        }
        tagIds.push(upserted.id);
      }

      if (tagIds.length > 0) {
        let { error: projectTagsError } = await supabase
          .from('project_tags')
          .upsert(
            tagIds.map((tagId) => ({
              project_id: projectId,
              tag_id: tagId,
            })),
            { onConflict: 'project_id,tag_id', ignoreDuplicates: true }
          );

        // Membership trigger should already have run; one short retry covers edge timing.
        if (projectTagsError) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          const retry = await supabase.from('project_tags').upsert(
            tagIds.map((tagId) => ({
              project_id: projectId,
              tag_id: tagId,
            })),
            { onConflict: 'project_id,tag_id', ignoreDuplicates: true }
          );
          projectTagsError = retry.error;
        }

        if (projectTagsError) {
          const missingTable = /project_tags|schema cache|does not exist/i.test(
            projectTagsError.message || ''
          );
          setDatabaseError(
            missingTable
              ? {
                  title: 'Database table missing: project_tags',
                  message:
                    'Project was created, but service tags need the project_tags migration.',
                  steps: [
                    'Open the Supabase SQL editor.',
                    'Run frontend/supabase/migrations/20260720193000_project_tags_master_matrix.sql',
                    'Also run 20260721170000_project_creator_can_manage_project_tags.sql',
                    'Edit the project later to re-apply tags if needed.',
                  ],
                }
              : toUserAlert(projectTagsError, 'Project created, but tags failed')
          );
          setProjectSaving(false);
          await loadProjects();
          return;
        }
      }
    }

    await loadProjects();
    // Keep catalog in sync so new custom tags appear in dropdowns immediately.
    await loadTasks();
    const serviceSummary =
      selectedProjectServices.length > 0
        ? selectedProjectServices.join(', ')
        : null;
    logActivity(
      `${currentUser.name} deployed project: "${projectName}" for client "${clientName}"` +
        (serviceSummary ? ` · services: ${serviceSummary}` : ''),
      { projectId }
    );
    setNewProjectName('');
    setNewProjectDesc('');
    setNewProjectClientId('');
    setNewProjectClientName('');
    setNewProjectClientMode('select');
    setSelectedProjectServices([]);
    setCustomProjectServices([]);
    setNewProjectTagName('');
    setProjectServiceMenuOpen(false);
    setProjectClientMenuOpen(false);
    setProjectSaving(false);
  };

  const toggleProjectServiceSelection = (serviceName) => {
    setSelectedProjectServices((selected) =>
      selected.includes(serviceName)
        ? selected.filter((name) => name !== serviceName)
        : [...selected, serviceName]
    );
  };

  const handleAddCustomProjectTag = (event) => {
    event?.preventDefault?.();
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from the Service Tags checklist.',
        ],
      });
      return;
    }
    const name = newProjectTagName.trim();
    if (!name) return;

    const matrixNames = MASTER_TASK_MATRIX.flatMap((group) => group.services);
    const existingMatch = [...matrixNames, ...customProjectServices].find(
      (service) => service.toLowerCase() === name.toLowerCase()
    );
    const resolvedName = existingMatch || name;

    if (!existingMatch) {
      setCustomProjectServices((prev) => [...prev, resolvedName]);
    }
    setSelectedProjectServices((selected) =>
      selected.includes(resolvedName) ? selected : [...selected, resolvedName]
    );
    setNewProjectTagName('');
  };

  const handleRenameProject = async (id) => {
    if (!editProjectName.trim()) return false;

    setDatabaseError('');
    const projectName = editProjectName.trim();
    const { error } = await supabase
      .from('projects')
      .update({ name: projectName })
      .eq('id', id);

    if (error) {
      setDatabaseError(error.message);
      return false;
    }

    setProjects(projects.map(p => p.id === id ? { ...p, name: projectName } : p));
    logActivity(`${currentUser.name} renamed project to "${projectName}"`, {
      projectId: id,
    });
    return true;
  };

  const beginInlineProjectEdit = (project) => {
    if (!canManageProject(project.id)) return;
    setInlineEditProjectId(project.id);
    setEditingProjectId(null);
    setEditProjectName(project.name || '');
    setEditProjectDescription(project.description || '');
    setInlineMemberUserId('');
    setInlineMemberRole('Employee');
    setInlineProjectServiceMenuOpen(false);
    setInlineProjectTagName('');
    setProjectDetailsSavedId(null);
    clearUserAlert();
  };

  const cancelInlineProjectEdit = () => {
    setInlineEditProjectId(null);
    setEditProjectName('');
    setEditProjectDescription('');
    setInlineMemberUserId('');
    setInlineMemberRole('Employee');
    setInlineProjectServiceMenuOpen(false);
    setInlineProjectTagName('');
    setProjectDetailsSavingId(null);
    setProjectDetailsSavedId(null);
  };

  const ensureServiceTagId = async (serviceName) => {
    const normalized = String(serviceName || '').trim();
    if (!normalized) throw new Error('Service tag name is required.');

    const existing = tags.find(
      (tag) =>
        tag.name.toLowerCase() === normalized.toLowerCase() &&
        !String(tag.id).startsWith('local-')
    );
    if (existing) return { id: existing.id, name: existing.name };

    const isMatrixService = MASTER_TASK_MATRIX.some((group) =>
      group.services.some(
        (service) => service.toLowerCase() === normalized.toLowerCase()
      )
    );
    if (!isMatrixService && !isSystemAdmin) {
      const alert = {
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from Service Tags on this project or task.',
        ],
      };
      setDatabaseError(alert);
      const err = new Error(alert.message);
      err.userAlert = alert;
      throw err;
    }
    const { data: upserted, error } = await supabase
      .from('tags')
      .upsert(
        {
          name: normalized,
          is_builtin: isMatrixService,
          created_by: isMatrixService ? null : currentUser.id,
        },
        { onConflict: 'name' }
      )
      .select('id, name, is_builtin')
      .single();
    if (error) throw error;

    setTags((prev) =>
      prev.some((tag) => tag.id === upserted.id) ? prev : [...prev, upserted]
    );
    return { id: upserted.id, name: upserted.name };
  };

  const toggleInlineProjectServiceTag = async (projectId, serviceName) => {
    if (!canManageProject(projectId) || inlineProjectTagSaving) return;
    const project = projects.find((item) => item.id === projectId);
    if (!project) return;

    const normalized = String(serviceName || '').trim();
    if (!normalized) return;

    const existingTag = (project.tags || []).find(
      (tag) => tag.name.toLowerCase() === normalized.toLowerCase()
    );

    setInlineProjectTagSaving(true);
    setDatabaseError('');
    try {
      if (existingTag) {
        const { error } = await supabase
          .from('project_tags')
          .delete()
          .eq('project_id', projectId)
          .eq('tag_id', existingTag.id);
        if (error) throw error;

        setProjects((prev) =>
          prev.map((item) =>
            item.id === projectId
              ? {
                  ...item,
                  tags: (item.tags || []).filter(
                    (tag) => tag.id !== existingTag.id
                  ),
                }
              : item
          )
        );
        logActivity(
          `${currentUser.name} removed service tag "${existingTag.name}"`,
          { projectId }
        );
      } else {
        const tag = await ensureServiceTagId(normalized);
        const { error } = await supabase.from('project_tags').upsert(
          { project_id: projectId, tag_id: tag.id },
          { onConflict: 'project_id,tag_id', ignoreDuplicates: true }
        );
        if (error) throw error;

        setProjects((prev) =>
          prev.map((item) =>
            item.id === projectId
              ? {
                  ...item,
                  tags: (item.tags || []).some((entry) => entry.id === tag.id)
                    ? item.tags
                    : [...(item.tags || []), { id: tag.id, name: tag.name }],
                }
              : item
          )
        );
        logActivity(
          `${currentUser.name} added service tag "${tag.name}"`,
          { projectId }
        );
      }
    } catch (error) {
      const missingTable = /project_tags|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? {
              title: 'Database table missing: project_tags',
              message:
                'Service tags need the project_tags table. Run the latest migration, then try again.',
              steps: [
                'Open the Supabase SQL editor.',
                'Run frontend/supabase/migrations/20260720193000_project_tags_master_matrix.sql',
                'Reload and edit service tags again.',
              ],
            }
          : toUserAlert(error, 'Could not update service tags')
      );
    } finally {
      setInlineProjectTagSaving(false);
    }
  };

  const handleAddInlineCustomProjectTag = async (projectId, event) => {
    event?.preventDefault?.();
    const name = inlineProjectTagName.trim();
    if (!name || !canManageProject(projectId)) return;

    const matrixNames = MASTER_TASK_MATRIX.flatMap((group) => group.services);
    const existingMatch = matrixNames.find(
      (service) => service.toLowerCase() === name.toLowerCase()
    );
    const existingCatalogTag = tags.find(
      (tag) => tag.name.toLowerCase() === name.toLowerCase()
    );
    if (!existingMatch && !existingCatalogTag && !isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then assign it from Service Tags on this project.',
        ],
      });
      return;
    }
    const project = projects.find((item) => item.id === projectId);
    const existingOnProject = (project?.tags || []).find(
      (tag) => tag.name.toLowerCase() === name.toLowerCase()
    );
    if (existingOnProject) {
      setInlineProjectTagName('');
      return;
    }

    await toggleInlineProjectServiceTag(
      projectId,
      existingMatch || existingCatalogTag?.name || name
    );
    setInlineProjectTagName('');
  };

  const handleUpdateProjectDescription = async (projectId) => {
    if (!canManageProject(projectId)) return;
    const description = editProjectDescription.trim() || 'No description provided.';

    setDatabaseError('');
    setProjectDetailsSavingId(projectId);
    setProjectDetailsSavedId(null);
    const { error } = await supabase
      .from('projects')
      .update({ description })
      .eq('id', projectId);

    if (error) {
      setProjectDetailsSavingId(null);
      setDatabaseError(toUserAlert(error, 'Could not update project details'));
      return;
    }

    setProjects((previous) =>
      previous.map((project) =>
        project.id === projectId ? { ...project, description } : project
      )
    );
    logActivity(`${currentUser.name} updated project details`, {
      projectId,
    });
    setProjectDetailsSavingId(null);
    setProjectDetailsSavedId(projectId);
    window.setTimeout(() => {
      setProjectDetailsSavedId((current) =>
        current === projectId ? null : current
      );
    }, 2500);
  };

  const handleUpdateProjectStatus = async (projectId, nextStatus) => {
    if (!canManageProject(projectId) || !nextStatus) return;
    setDatabaseError('');
    const { error } = await supabase
      .from('projects')
      .update({ status: nextStatus })
      .eq('id', projectId);
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not update project status'));
      return;
    }
    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId ? { ...project, status: nextStatus } : project
      )
    );
    setProjectStatusMenuOpen(false);
    logActivity(
      `${currentUser.name} set project status to "${nextStatus}"`,
      { projectId }
    );
  };

  const handleAddProjectStatusOption = async (projectId, name) => {
    if (!canManageProject(projectId)) return;
    const normalized = String(name || '').trim();
    if (!normalized) return;
    const project = projects.find((item) => item.id === projectId);
    if (
      (project?.projectStatuses || []).some(
        (item) => item.name.toLowerCase() === normalized.toLowerCase()
      )
    ) {
      setNewProjectStatusName('');
      return;
    }

    setStatusOptionSaving(true);
    setDatabaseError('');
    try {
      const { data, error } = await supabase
        .from('project_status_options')
        .upsert(
          {
            project_id: projectId,
            name: normalized,
            color: 'purple',
            sort_order: (project?.projectStatuses || []).length,
          },
          { onConflict: 'project_id,name' }
        )
        .select('id, name, color, sort_order')
        .single();
      if (error) throw error;

      setProjects((prev) =>
        prev.map((item) =>
          item.id === projectId
            ? {
                ...item,
                projectStatuses: [
                  ...(item.projectStatuses || []),
                  {
                    id: data.id,
                    name: data.name,
                    color: data.color || 'purple',
                    sortOrder: data.sort_order || 0,
                  },
                ],
              }
            : item
        )
      );
      setNewProjectStatusName('');
      await handleUpdateProjectStatus(projectId, data.name);
    } catch (error) {
      setDatabaseError(
        toUserAlert(error, 'Could not add project status. Run the statuses migration.')
      );
    } finally {
      setStatusOptionSaving(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, nextStatus) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task || !nextStatus) return;
    setDatabaseError('');
    const { error } = await supabase
      .from('tasks')
      .update({ status: nextStatus })
      .eq('id', taskId);
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not update task status'));
      return;
    }
    setTasks((prev) =>
      prev.map((item) =>
        item.id === taskId ? { ...item, status: nextStatus } : item
      )
    );
    logActivity(
      `${currentUser.name} set task "${task.name}" status to "${nextStatus}"`,
      { projectId: task.projectId, taskId }
    );
  };

  const handleUpdateSubtaskStatus = async (taskId, subtaskId, nextStatus) => {
    if (!nextStatus) return;
    const isDone = nextStatus.toLowerCase() === 'done';
    setDatabaseError('');
    const { error } = await supabase
      .from('task_subtasks')
      .update({
        status: nextStatus,
        is_completed: isDone,
        completed_at: isDone ? new Date().toISOString() : null,
      })
      .eq('id', subtaskId);
    if (error) {
      // Fallback if status column missing: keep checkbox behavior.
      if (/status|schema cache|column/i.test(error.message || '')) {
        const { error: fallbackError } = await supabase
          .from('task_subtasks')
          .update({
            is_completed: isDone,
            completed_at: isDone ? new Date().toISOString() : null,
          })
          .eq('id', subtaskId);
        if (fallbackError) {
          setDatabaseError(
            toUserAlert(fallbackError, 'Could not update subtask status')
          );
          return;
        }
      } else {
        setDatabaseError(toUserAlert(error, 'Could not update subtask status'));
        return;
      }
    }
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? {
              ...task,
              subtasks: (task.subtasks || []).map((sub) =>
                sub.id === subtaskId
                  ? {
                      ...sub,
                      status: nextStatus,
                      isCompleted: isDone,
                    }
                  : sub
              ),
            }
          : task
      )
    );
  };

  const handleAddTaskStatusOption = async (projectId, name) => {
    if (!canManageProject(projectId)) return;
    const normalized = String(name || '').trim();
    if (!normalized) return;
    const project = projects.find((item) => item.id === projectId);
    if (
      (project?.taskStatuses || []).some(
        (item) => item.name.toLowerCase() === normalized.toLowerCase()
      )
    ) {
      setNewTaskStatusName('');
      return;
    }

    setStatusOptionSaving(true);
    setDatabaseError('');
    try {
      const { data, error } = await supabase
        .from('task_status_options')
        .upsert(
          {
            project_id: projectId,
            name: normalized,
            color: 'gray',
            sort_order: (project?.taskStatuses || []).length,
          },
          { onConflict: 'project_id,name' }
        )
        .select('id, name, color, sort_order')
        .single();
      if (error) throw error;

      setProjects((prev) =>
        prev.map((item) =>
          item.id === projectId
            ? {
                ...item,
                taskStatuses: [
                  ...(item.taskStatuses || []),
                  {
                    id: data.id,
                    name: data.name,
                    color: data.color || 'gray',
                    sortOrder: data.sort_order || 0,
                  },
                ],
              }
            : item
        )
      );
      setNewTaskStatusName('');
    } catch (error) {
      setDatabaseError(
        toUserAlert(error, 'Could not add task status. Run the statuses migration.')
      );
    } finally {
      setStatusOptionSaving(false);
    }
  };

  const handleAddProjectMember = async (projectId) => {
    if (!canManageProject(projectId) || !projectId || !inlineMemberUserId) return;

    setDatabaseError('');
    const profile = teamMembers.find((member) => member.id === inlineMemberUserId);
    if (!profile) {
      setDatabaseError({
        title: 'Member not found',
        message: 'Select a registered teammate from the list.',
        steps: ['Open Team to invite them first if they are missing.', 'Then add them here again.'],
      });
      return;
    }

    const { error } = await supabase.from('project_members').insert({
      project_id: projectId,
      user_id: profile.id,
      role: inlineMemberRole === 'Admin' ? 'admin' : 'employee',
    });

    if (error) {
      setDatabaseError(
        error.code === '23505'
          ? {
              title: 'Already a member',
              message: 'That user is already on this project roster.',
              steps: ['No further action needed.'],
            }
          : toUserAlert(error, 'Could not add staff member')
      );
      return;
    }

    setInlineMemberUserId('');
    await loadProjects();
    logActivity(
      `${currentUser.name} added "${profile.name}" as [${inlineMemberRole === 'Admin' ? 'Project Admin' : 'Ordinary User'}]`,
      {
        projectId,
        notify: 'member_added',
        notifyTitle: 'Added to a project',
        notifyUserIds: [profile.id],
      }
    );
  };

  const handleRemoveProjectMember = async (projectId, member) => {
    if (!canManageProject(projectId) || !projectId || !member?.id) return;

    setDatabaseError('');
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', member.id);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not remove staff member'));
      return;
    }

    await loadProjects();
    logActivity(
      `${currentUser.name} removed "${member.name}" from the project`,
      { projectId }
    );
  };

  const handleUpdateProjectMemberRole = async (projectId, member, nextDisplayRole) => {
    if (!canManageProject(projectId) || !projectId || !member?.id) return;

    const nextRole = nextDisplayRole === 'Admin' ? 'admin' : 'employee';
    const currentRole = member.role === 'Admin' ? 'admin' : 'employee';
    if (nextRole === currentRole) return;

    if (member.id === currentUser.id && nextRole === 'employee') {
      const project = projects.find((item) => item.id === projectId);
      const adminCount = (project?.members || []).filter(
        (item) => item.role === 'Admin'
      ).length;
      if (adminCount <= 1 && !isSystemAdmin) {
        setDatabaseError({
          title: 'Cannot demote yourself',
          message:
            'You are the only project admin. Promote another member first, or ask a system admin.',
          steps: [
            'Change another member to Project Admin.',
            'Then you can switch your own role to Ordinary User.',
          ],
        });
        return;
      }
    }

    setDatabaseError('');
    const { error } = await supabase
      .from('project_members')
      .update({ role: nextRole })
      .eq('project_id', projectId)
      .eq('user_id', member.id);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not update project permission'));
      return;
    }

    const roleLabel =
      nextDisplayRole === 'Admin' ? 'Project Admin' : 'Ordinary User';
    await loadProjects();
    logActivity(
      `${currentUser.name} set "${member.name}" to [${roleLabel}] on this project`,
      { projectId }
    );
  };

  const handleUpdateProjectClient = async (projectId, clientId) => {
    if (!canManageProject(projectId) || !projectId) return;

    setDatabaseError('');
    const nextClientId = clientId || null;

    // Keep legacy primary client_id in sync for older queries/filters.
    const { error } = await supabase
      .from('projects')
      .update({ client_id: nextClientId })
      .eq('id', projectId);

    if (error) {
      const lower = normalizeErrorText(error).toLowerCase();
      if (lower.includes('client_id') && lower.includes('schema cache')) {
        setDatabaseError({
          title: 'Database column missing: projects.client_id',
          message:
            'The clients table exists, but projects still needs a client_id column to store the project–client link.',
          steps: [
            'Open Supabase → SQL Editor.',
            'Run the SQL from frontend/supabase/migrations/20260720180000_projects_client_id.sql',
            'Refresh this page, then assign the client again.',
          ],
        });
        return;
      }
      setDatabaseError(toUserAlert(error, 'Could not update project client'));
      return;
    }

    // Also upsert into project_clients when the multi-client table exists.
    if (nextClientId) {
      const { error: junctionError } = await supabase
        .from('project_clients')
        .upsert(
          { project_id: projectId, client_id: nextClientId },
          { onConflict: 'project_id,client_id', ignoreDuplicates: true }
        );
      if (
        junctionError &&
        !/project_clients|schema cache|does not exist/i.test(
          junctionError.message || ''
        )
      ) {
        setDatabaseError(
          toUserAlert(junctionError, 'Could not update project clients')
        );
        return;
      }
    }

    await loadProjects();
    const clientName =
      activeClients.find((client) => client.id === nextClientId)?.name || 'none';
    logActivity(
      `${currentUser.name} set project client to "${clientName}"`,
      { projectId }
    );
  };

  const toggleProjectClient = async (projectId, clientId) => {
    if (!canManageProject(projectId) || !projectId || !clientId) return;
    const project = projects.find((item) => item.id === projectId);
    if (!project) return;

    const alreadyAssigned = (project.clients || []).some(
      (client) => client.id === clientId
    );
    setDatabaseError('');

    try {
      if (alreadyAssigned) {
        const { error } = await supabase
          .from('project_clients')
          .delete()
          .eq('project_id', projectId)
          .eq('client_id', clientId);
        if (error) {
          if (/project_clients|schema cache|does not exist/i.test(error.message || '')) {
            // Fallback: clear single client_id when junction table is missing.
            if (project.clientId === clientId) {
              await handleUpdateProjectClient(projectId, '');
            }
            return;
          }
          throw error;
        }

        const remaining = (project.clients || []).filter(
          (client) => client.id !== clientId
        );
        const nextPrimary = remaining[0]?.id || null;
        await supabase
          .from('projects')
          .update({ client_id: nextPrimary })
          .eq('id', projectId);

        setProjects((prev) =>
          prev.map((item) =>
            item.id === projectId
              ? {
                  ...item,
                  clientId: nextPrimary,
                  clients: remaining,
                }
              : item
          )
        );
        const removedName =
          activeClients.find((client) => client.id === clientId)?.name ||
          'client';
        logActivity(
          `${currentUser.name} removed client "${removedName}" from project`,
          { projectId }
        );
      } else {
        const { error } = await supabase.from('project_clients').upsert(
          { project_id: projectId, client_id: clientId },
          { onConflict: 'project_id,client_id', ignoreDuplicates: true }
        );
        if (error) {
          if (/project_clients|schema cache|does not exist/i.test(error.message || '')) {
            await handleUpdateProjectClient(projectId, clientId);
            return;
          }
          throw error;
        }

        if (!project.clientId) {
          await supabase
            .from('projects')
            .update({ client_id: clientId })
            .eq('id', projectId);
        }

        const clientName =
          activeClients.find((client) => client.id === clientId)?.name ||
          'Client';
        setProjects((prev) =>
          prev.map((item) => {
            if (item.id !== projectId) return item;
            const clients = item.clients || [];
            if (clients.some((entry) => entry.id === clientId)) return item;
            return {
              ...item,
              clientId: item.clientId || clientId,
              clients: [...clients, { id: clientId, name: clientName }],
            };
          })
        );
        logActivity(
          `${currentUser.name} added client "${clientName}" to project`,
          { projectId }
        );
      }
    } catch (error) {
      setDatabaseError(toUserAlert(error, 'Could not update project clients'));
    }
  };

  // NEW OPERATOR: Project Removal Logic (project admin protected)
  const handleDeleteProject = async (id, name) => {
    if (!canManageProject(id)) return;

    setDatabaseError('');
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) {
      setDatabaseError(error.message);
      return;
    }

    setProjects(projects.filter((p) => p.id !== id));
    setTasks(tasks.filter((t) => t.projectId !== id)); // Cascading Task Cleanup
    if (activeProjectId === id) setActiveProjectId(null);
    logActivity(`${currentUser.name} deleted project: "${name}"`);
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!newMemberName.trim() || !activeProjectId) return;
    if (!canManageProject(activeProjectId)) {
      setDatabaseError({
        title: 'Project admin required',
        message:
          'Only a project admin (or system admin) can invite members to this project.',
        steps: [
          'Ask a project admin to invite the member, or to promote your project role.',
        ],
      });
      return;
    }

    setDatabaseError('');
    const memberLookup = newMemberName.trim();
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('id, full_name, email');

    if (profileError) {
      setDatabaseError(profileError.message);
      return;
    }

    const normalizedLookup = memberLookup.toLowerCase();
    const profile = profiles?.find(
      (candidate) =>
        candidate.email?.toLowerCase() === normalizedLookup ||
        candidate.full_name?.toLowerCase() === normalizedLookup
    );
    if (!profile) {
      setDatabaseError('No registered user was found with that exact name or email.');
      return;
    }

    const { error } = await supabase.from('project_members').insert({
      project_id: activeProjectId,
      user_id: profile.id,
      role: newMemberRole === 'Admin' ? 'admin' : 'employee',
    });

    if (error) {
      setDatabaseError(
        error.code === '23505' ? 'That user is already a project member.' : error.message
      );
      return;
    }

    await loadProjects();
    logActivity(
      `${currentUser.name} added "${profile.full_name || profile.email}" as [${newMemberRole === 'Admin' ? 'Project Admin' : 'Ordinary User'}]`,
      {
        projectId: activeProjectId,
        notify: 'member_added',
        notifyTitle: 'Added to a project',
        notifyUserIds: [profile.id],
      }
    );
    setNewMemberName('');
    setNewMemberRole('Employee');
    setInviteMemberOpen(false);
  };

  // NEW OPERATOR: Roster Pool Eviction Logic (Admin Protected)
  const handleRemoveMember = async (member) => {
    if (!activeProjectId) return;
    await handleRemoveProjectMember(activeProjectId, member);
  };

  // Ensure the signed-in user is on the project roster before creating/assigning tasks.
  const ensureCurrentUserProjectMember = async (projectId) => {
    if (!projectId || !currentUser?.id) {
      return USER_ALERTS.noProjectSelected;
    }

    if (projects.length === 0) {
      return USER_ALERTS.noProjects;
    }

    const project = projects.find((item) => item.id === projectId);
    if (!project) {
      return USER_ALERTS.noProjects;
    }

    const alreadyMember = (project.members || []).some(
      (member) => member.id === currentUser.id
    );
    if (alreadyMember) return null;

    const { error } = await supabase.from('project_members').insert({
      project_id: projectId,
      user_id: currentUser.id,
      role: currentUser.role === 'admin' ? 'admin' : 'employee',
    });

    if (error && error.code !== '23505') {
      const lower = normalizeErrorText(error).toLowerCase();
      if (
        lower.includes('row-level security') ||
        lower.includes('permission denied') ||
        lower.includes('not authorized')
      ) {
        return USER_ALERTS.notProjectMember;
      }
      return toUserAlert(error, 'Could not join this project');
    }

    return null;
  };

  // When someone runs a timer, they become a task assignee automatically.
  const ensureCurrentUserAssignedToTask = async (task) => {
    if (!task?.id || !currentUser?.id) {
      return USER_ALERTS.noTasksAvailable;
    }

    const membershipError = await ensureCurrentUserProjectMember(task.projectId);
    if (membershipError) return membershipError;

    const { error } = await supabase.from('task_assignees').upsert(
      { task_id: task.id, user_id: currentUser.id },
      { onConflict: 'task_id,user_id', ignoreDuplicates: true }
    );

    if (error) {
      return toUserAlert(error, 'Could not assign you to this task');
    }

    return null;
  };

  // Close every open timer for the signed-in user (optionally keep one entry).
  const closeOpenTimeEntriesForCurrentUser = async ({
    excludeEntryId = null,
  } = {}) => {
    if (!currentUser?.id) return { closed: [], error: null };

    const { data: openRows, error } = await supabase
      .from('time_entries')
      .select('id, task_id, started_at')
      .eq('user_id', currentUser.id)
      .is('ended_at', null);

    if (error) {
      return { closed: [], error };
    }

    const endedAt = new Date();
    const closed = [];
    for (const row of openRows || []) {
      if (excludeEntryId && row.id === excludeEntryId) continue;
      const durationSeconds = Math.max(
        0,
        Math.floor(
          (endedAt.getTime() - new Date(row.started_at).getTime()) / 1000
        )
      );
      const { error: updateError } = await supabase
        .from('time_entries')
        .update({
          ended_at: endedAt.toISOString(),
          duration_seconds: durationSeconds,
        })
        .eq('id', row.id);
      if (!updateError) {
        closed.push({
          id: row.id,
          taskId: row.task_id,
          durationSeconds,
        });
      }
    }

    return { closed, error: null };
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) return;

    setDatabaseError('');
    const taskName = newTaskName.trim();
    const estimateRaw = String(newTaskEstimatedHours || '').trim();
    const parsedEstimateHours =
      estimateRaw === ''
        ? null
        : Number.isFinite(Number(estimateRaw)) && Number(estimateRaw) >= 0
          ? Number(estimateRaw)
          : null;
    if (estimateRaw !== '' && parsedEstimateHours == null) {
      setDatabaseError({
        title: 'Invalid estimated time',
        message: 'Estimated time must be a non-negative number of hours (e.g. 2.5).',
        steps: ['Clear the estimated time field or enter valid hours.', 'Then create the task again.'],
      });
      return;
    }
    const parsedEstimatedSeconds =
      parsedEstimateHours == null
        ? null
        : Math.round(parsedEstimateHours * 3600);

    if (
      newTaskStartDate &&
      newTaskEndDate &&
      newTaskEndDate < newTaskStartDate
    ) {
      setDatabaseError({
        title: 'Invalid schedule',
        message: 'Duration date must be on or after the start date.',
        steps: ['Adjust Start date or Duration date, then create the task again.'],
      });
      return;
    }

    const membershipError = await ensureCurrentUserProjectMember(activeProjectId);
    if (membershipError) {
      setDatabaseError(membershipError);
      return;
    }

    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .insert({
        project_id: activeProjectId,
        name: taskName,
        status: 'To Do',
        created_by: currentUser.id,
        ...(parsedEstimatedSeconds != null
          ? { estimated_seconds: parsedEstimatedSeconds }
          : {}),
        ...(newTaskStartDate ? { start_date: newTaskStartDate } : {}),
        ...(newTaskEndDate ? { end_date: newTaskEndDate } : {}),
      })
      .select('id')
      .single();

    if (taskError) {
      const estimateMissing = /estimated_seconds|estimated_budget|schema cache|does not exist/i.test(
        taskError.message || ''
      );
      const scheduleMissing = /start_date|end_date|schema cache|does not exist/i.test(
        taskError.message || ''
      );
      if (estimateMissing && parsedEstimatedSeconds != null) {
        setDatabaseError({
          title: 'Database column missing: tasks.estimated_seconds',
          message:
            'Task was not created because Estimated Time needs a migration.',
          steps: [
            'Open the Supabase SQL editor.',
            'Run frontend/supabase/migrations/20260720197000_tasks_estimated_seconds.sql',
            'Create the task again.',
          ],
        });
        return;
      }
      if (scheduleMissing && (newTaskStartDate || newTaskEndDate)) {
        setDatabaseError({
          title: 'Database columns missing: start_date / end_date',
          message:
            'Task was not created because Start/Duration dates need a migration.',
          steps: [
            'Open the Supabase SQL editor.',
            'Run frontend/supabase/migrations/20260720199000_task_schedule_dates.sql',
            'Create the task again.',
          ],
        });
        return;
      }
      setDatabaseError(taskError.message);
      return;
    }

    const selectedMemberIds = (currentProject?.members || [])
      .filter((member) =>
        selectedAssignees.includes(`${member.name} (${member.role})`)
      )
      .map((member) => member.id);

    // Always include the creating user as an assignee.
    const assigneeIds = Array.from(
      new Set([currentUser.id, ...selectedMemberIds])
    );

    const { error: assigneesError } = await supabase
      .from('task_assignees')
      .upsert(
        assigneeIds.map((userId) => ({
          task_id: task.id,
          user_id: userId,
        })),
        { onConflict: 'task_id,user_id', ignoreDuplicates: true }
      );

    if (assigneesError) {
      setDatabaseError(assigneesError.message);
      return;
    }

    const selectedFollowerIds = (currentProject?.members || [])
      .filter((member) =>
        selectedFollowers.includes(`${member.name} (${member.role})`)
      )
      .map((member) => member.id);

    if (selectedFollowerIds.length > 0) {
      const { error: followersError } = await supabase
        .from('task_followers')
        .upsert(
          selectedFollowerIds.map((userId) => ({
            task_id: task.id,
            user_id: userId,
          })),
          { onConflict: 'task_id,user_id', ignoreDuplicates: true }
        );

      if (followersError) {
        const missingTable = /task_followers|schema cache|does not exist/i.test(
          followersError.message || ''
        );
        setDatabaseError(
          missingTable
            ? {
                title: 'Database table missing: task_followers',
                message:
                  'Followers need the task_followers table. Run the latest migration in Supabase, then try again.',
                steps: [
                  'Open the Supabase SQL editor for this project.',
                  'Run frontend/supabase/migrations/20260720190000_task_followers.sql',
                  'Reload the app and add followers again.',
                ],
              }
            : toUserAlert(followersError, 'Could not add task followers')
        );
        return;
      }
    }

    if (selectedTaskClientIds.length > 0) {
      const { error: clientsError } = await supabase
        .from('task_clients')
        .insert(
          selectedTaskClientIds.map((clientId) => ({
            task_id: task.id,
            client_id: clientId,
          }))
        );

      if (clientsError) {
        setDatabaseError(clientsError.message);
        return;
      }
    }

    const persistableTagIds = [];
    for (const serviceName of selectedTaskServices) {
      const existing = tags.find(
        (tag) =>
          tag.name.toLowerCase() === serviceName.toLowerCase() &&
          !String(tag.id).startsWith('local-')
      );
      if (existing) {
        persistableTagIds.push(existing.id);
        continue;
      }
      const isMatrixService = MASTER_TASK_MATRIX.some((group) =>
        group.services.some(
          (service) => service.toLowerCase() === serviceName.toLowerCase()
        )
      );
      const { data: upserted, error: tagError } = await supabase
        .from('tags')
        .upsert(
          {
            name: serviceName,
            is_builtin: isMatrixService,
            created_by: isMatrixService ? null : currentUser.id,
          },
          { onConflict: 'name' }
        )
        .select('id')
        .single();
      if (tagError) {
        setDatabaseError(toUserAlert(tagError, `Could not prepare tag "${serviceName}"`));
        return;
      }
      persistableTagIds.push(upserted.id);
    }
    if (persistableTagIds.length > 0) {
      const { error: tagsError } = await supabase
        .from('task_tags')
        .upsert(
          persistableTagIds.map((tagId) => ({
            task_id: task.id,
            tag_id: tagId,
          })),
          { onConflict: 'task_id,tag_id', ignoreDuplicates: true }
        );

      if (tagsError) {
        setDatabaseError(tagsError.message);
        return;
      }
    }

    await Promise.all([loadProjects(), loadTasks()]);
    const assigneeSummary =
      selectedAssignees.length > 0
        ? Array.from(new Set([currentUser.name, ...selectedAssignees])).join(', ')
        : currentUser.name;
    const clientSummary = clients
      .filter((client) => selectedTaskClientIds.includes(client.id))
      .map((client) => client.name)
      .join(', ');
    const tagSummary = selectedTaskServices.join(', ');
    logActivity(
      `Created task "${taskName}" assigned to: ${assigneeSummary}` +
        (selectedFollowers.length > 0
          ? `; followers: ${selectedFollowers.join(', ')}`
          : '') +
        (clientSummary ? `; clients: ${clientSummary}` : '') +
        (tagSummary ? `; tags: ${tagSummary}` : ''),
      {
        projectId: activeProjectId,
        taskId: task.id,
        notify: 'task_created',
        notifyTitle: 'New task',
        notifyUserIds: selectedMemberIds.filter((id) => id !== currentUser.id),
      }
    );
    setNewTaskName('');
    setNewTaskEstimatedHours('');
    setNewTaskStartDate('');
    setNewTaskEndDate('');
    setSelectedAssignees([]);
    setSelectedFollowers([]);
    setSelectedTaskServices([]);
    setCustomTaskServices([]);
    setNewTaskTagName('');
    setTaskServiceMenuOpen(false);
    setCreateTaskTagMenuOpen(false);
    setCreateTaskFollowerMenuOpen(false);
    setSelectedTaskClientIds([]);
    setSelectedTagIds([]);
  };

  const toggleAssigneeSelection = (memberLabel) => {
    setSelectedAssignees(prev => 
      prev.includes(memberLabel)
        ? prev.filter(item => item !== memberLabel)
        : [...prev, memberLabel]
    );
  };

  const toggleFollowerSelection = (memberLabel) => {
    setSelectedFollowers((prev) =>
      prev.includes(memberLabel)
        ? prev.filter((item) => item !== memberLabel)
        : [...prev, memberLabel]
    );
  };

  const toggleTaskClientSelection = (clientId) => {
    setSelectedTaskClientIds((selected) =>
      selected.includes(clientId)
        ? selected.filter((id) => id !== clientId)
        : [...selected, clientId]
    );
  };

  const toggleTagSelection = (tagId) => {
    setSelectedTagIds((selected) =>
      selected.includes(tagId)
        ? selected.filter((id) => id !== tagId)
        : [...selected, tagId]
    );
  };

  const toggleTaskServiceSelection = (serviceName) => {
    setSelectedTaskServices((selected) =>
      selected.includes(serviceName)
        ? selected.filter((name) => name !== serviceName)
        : [...selected, serviceName]
    );
  };

  const handleAddCustomTaskTag = (event) => {
    event?.preventDefault?.();
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from the Service Tags checklist.',
        ],
      });
      return;
    }
    const name = newTaskTagName.trim();
    if (!name) return;
    const matrixNames = MASTER_TASK_MATRIX.flatMap((group) => group.services);
    const existingMatch = [...matrixNames, ...customTaskServices].find(
      (service) => service.toLowerCase() === name.toLowerCase()
    );
    const resolvedName = existingMatch || name;
    if (!existingMatch) {
      setCustomTaskServices((prev) => [...prev, resolvedName]);
    }
    setSelectedTaskServices((selected) =>
      selected.includes(resolvedName) ? selected : [...selected, resolvedName]
    );
    setNewTaskTagName('');
  };

  const toggleTaskTag = async (taskId, tag) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task || !tag?.id) return;
    const isTagged = (task.tags || []).some((item) => item.id === tag.id);
    setDatabaseError('');
    const query = isTagged
      ? supabase
          .from('task_tags')
          .delete()
          .eq('task_id', taskId)
          .eq('tag_id', tag.id)
      : supabase.from('task_tags').insert({ task_id: taskId, tag_id: tag.id });
    const { error } = await query;
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not update task tags'));
      return;
    }
    await loadTasks();
    logActivity(
      `${currentUser.name} ${isTagged ? 'removed' : 'added'} tag "${tag.name}" on "${task.name}"`,
      { projectId: task.projectId, taskId }
    );
  };

  const toggleSubtaskTag = async (taskId, subtaskId, tag) => {
    if (!subtaskId || !tag?.id) return;
    const task = tasks.find((item) => item.id === taskId);
    const subtask = (task?.subtasks || []).find((item) => item.id === subtaskId);
    if (!subtask) return;
    const isTagged = (subtask.tags || []).some((item) => item.id === tag.id);
    setDatabaseError('');
    const query = isTagged
      ? supabase
          .from('task_subtask_tags')
          .delete()
          .eq('subtask_id', subtaskId)
          .eq('tag_id', tag.id)
      : supabase
          .from('task_subtask_tags')
          .insert({ subtask_id: subtaskId, tag_id: tag.id });
    const { error } = await query;
    if (error) {
      const missingTable = /task_subtask_tags|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? {
              title: 'Database table missing: task_subtask_tags',
              message:
                'Subtask tags need the task_subtask_tags table. Run the latest migration in Supabase.',
              steps: [
                'Open the Supabase SQL editor.',
                'Run frontend/supabase/migrations/20260720194000_task_subtask_tags.sql',
                'Reload and tag the subtask again.',
              ],
            }
          : toUserAlert(error, 'Could not update subtask tags')
      );
      return;
    }
    await loadTasks();
    logActivity(
      `${currentUser.name} ${isTagged ? 'removed' : 'added'} tag "${tag.name}" on subtask "${subtask.name}"`,
      { projectId: task.projectId, taskId }
    );
  };

  const ensureTagByName = async (name) => {
    const existing = tags.find(
      (tag) =>
        tag.name.toLowerCase() === name.toLowerCase() &&
        !String(tag.id).startsWith('local-')
    );
    if (existing) return existing;
    const isMatrixService = MASTER_TASK_MATRIX.some((group) =>
      group.services.some(
        (service) => service.toLowerCase() === name.toLowerCase()
      )
    );
    if (!isMatrixService && !isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from Service Tags on this project or task.',
        ],
      });
      return null;
    }
    const { data, error } = await supabase
      .from('tags')
      .upsert(
        {
          name,
          is_builtin: isMatrixService,
          created_by: isMatrixService ? null : currentUser.id,
        },
        { onConflict: 'name' }
      )
      .select('id, name, is_builtin')
      .single();
    if (error) {
      setDatabaseError(toUserAlert(error, `Could not create tag "${name}"`));
      return null;
    }
    const created = {
      id: data.id,
      name: data.name,
      isBuiltin: Boolean(data.is_builtin),
    };
    setTags((prev) =>
      prev.some((tag) => tag.id === created.id)
        ? prev
        : [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
    );
    return created;
  };

  /** Team admins can create a catalog tag and assign it on a project task. */
  const createProjectAdminTagForTask = async (taskId, projectId, rawName) => {
    if (!taskId || !projectId) return null;
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from Service Tags on this task.',
        ],
      });
      return null;
    }

    const name = String(rawName || '').trim();
    if (!name) return null;

    setTrackerNewTagSaving(true);
    setDatabaseError('');
    try {
      const tag = await ensureTagByName(name);
      if (!tag) return null;

      const { error: projectTagError } = await supabase
        .from('project_tags')
        .upsert(
          { project_id: projectId, tag_id: tag.id },
          { onConflict: 'project_id,tag_id', ignoreDuplicates: true }
        );
      if (projectTagError) {
        const missingTable = /project_tags|schema cache|does not exist/i.test(
          projectTagError.message || ''
        );
        if (!missingTable) {
          setDatabaseError(
            toUserAlert(projectTagError, 'Tag created, but could not link to project')
          );
        }
      } else {
        await loadProjects();
      }

      await toggleTaskTag(taskId, tag);
      setTrackerNewTagName('');
      setTrackerTagSearch('');
      setNewTaskTagName('');
      return tag;
    } finally {
      setTrackerNewTagSaving(false);
    }
  };

  /** Team admins can create a catalog tag and assign it on a subtask. */
  const createProjectAdminTagForSubtask = async (
    taskId,
    subtaskId,
    projectId,
    rawName
  ) => {
    if (!taskId || !subtaskId || !projectId) return null;
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: [
          'Ask a Team admin to create the tag.',
          'Then select it from Service Tags on this subtask.',
        ],
      });
      return null;
    }

    const name = String(rawName || '').trim();
    if (!name) return null;

    setTrackerNewTagSaving(true);
    setDatabaseError('');
    try {
      const tag = await ensureTagByName(name);
      if (!tag) return null;

      const { error: projectTagError } = await supabase
        .from('project_tags')
        .upsert(
          { project_id: projectId, tag_id: tag.id },
          { onConflict: 'project_id,tag_id', ignoreDuplicates: true }
        );
      if (projectTagError) {
        const missingTable = /project_tags|schema cache|does not exist/i.test(
          projectTagError.message || ''
        );
        if (!missingTable) {
          setDatabaseError(
            toUserAlert(projectTagError, 'Tag created, but could not link to project')
          );
        }
      } else {
        await loadProjects();
      }

      await toggleSubtaskTag(taskId, subtaskId, tag);
      setNewTaskTagName('');
      return tag;
    } finally {
      setTrackerNewTagSaving(false);
    }
  };

  const handleCreateTag = async (event) => {
    event.preventDefault();
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Team admin required',
        message:
          'Only Team admins can create new service tags for the account catalog.',
        steps: ['Ask a Team admin to create the tag.'],
      });
      return;
    }
    const tagName = newTagName.trim();
    if (!tagName) return;

    const existing = tags.find(
      (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
    );
    if (existing) {
      setSelectedTagIds((selected) =>
        selected.includes(existing.id) ? selected : [...selected, existing.id]
      );
      setNewTagName('');
      return;
    }

    setDatabaseError('');
    const { data, error } = await supabase
      .from('tags')
      .insert({
        name: tagName,
        is_builtin: false,
        created_by: currentUser.id,
      })
      .select('id, name, is_builtin')
      .single();

    if (error) {
      const localTag = {
        id: `local-${Date.now()}`,
        name: tagName,
        isBuiltin: false,
      };
      setTags((previous) =>
        [...previous, localTag].sort((a, b) => a.name.localeCompare(b.name))
      );
      setSelectedTagIds((selected) => [...selected, localTag.id]);
      setNewTagName('');
      setDatabaseError(
        `${error.message}. Tag added locally until the tags migration is applied.`
      );
      return;
    }

    const createdTag = {
      id: data.id,
      name: data.name,
      isBuiltin: Boolean(data.is_builtin),
    };
    setTags((previous) =>
      [...previous, createdTag].sort((a, b) => a.name.localeCompare(b.name))
    );
    setSelectedTagIds((selected) => [...selected, createdTag.id]);
    setNewTagName('');
  };

  const toggleTaskAssignee = async (taskId, memberLabel) => {
    const task = tasks.find((item) => item.id === taskId);
    const member = currentProject?.members.find(
      (item) => `${item.name} (${item.role})` === memberLabel
    );
    if (!task || !member) return;

    setDatabaseError('');
    const isAssigned = task.assignees.includes(memberLabel);
    const query = isAssigned
      ? supabase
          .from('task_assignees')
          .delete()
          .eq('task_id', taskId)
          .eq('user_id', member.id)
      : supabase
          .from('task_assignees')
          .insert({ task_id: taskId, user_id: member.id });
    const { error } = await query;

    if (error) {
      setDatabaseError(error.message);
      return;
    }

    await loadTasks();
    logActivity(
      `${currentUser.name} ${isAssigned ? 'unassigned' : 'assigned'} ${member.name} on "${task.name}"`,
      {
        projectId: task.projectId,
        taskId,
        notify: isAssigned ? 'task_update' : 'task_assigned',
        notifyTitle: isAssigned ? 'Task update' : 'Task assignment',
        notifyUserIds: isAssigned ? undefined : [member.id],
      }
    );
  };

  const toggleSubtaskAssignee = async (taskId, subtaskId, memberLabel) => {
    const task = tasks.find((item) => item.id === taskId);
    const subtask = (task?.subtasks || []).find((item) => item.id === subtaskId);
    const member = currentProject?.members.find(
      (item) => `${item.name} (${item.role})` === memberLabel
    );
    if (!task || !subtask || !member) return;

    setDatabaseError('');
    const isAssigned = (subtask.assignees || []).includes(memberLabel);
    const query = isAssigned
      ? supabase
          .from('task_subtask_assignees')
          .delete()
          .eq('subtask_id', subtaskId)
          .eq('user_id', member.id)
      : supabase
          .from('task_subtask_assignees')
          .insert({ subtask_id: subtaskId, user_id: member.id });
    const { error } = await query;

    if (error) {
      const missingTable =
        /task_subtask_assignees|schema cache|does not exist/i.test(
          error.message || ''
        );
      setDatabaseError(
        missingTable
          ? {
              title: 'Database table missing: task_subtask_assignees',
              message:
                'Subtask assignees need the task_subtask_assignees table. Run the latest migration in Supabase.',
              steps: [
                'Run frontend/supabase/migrations/20260722130000_task_subtask_assignees.sql',
                'Reload and assign the subtask again.',
              ],
            }
          : toUserAlert(error, 'Could not update subtask assignees')
      );
      return;
    }

    await loadTasks();
    logActivity(
      `${currentUser.name} ${isAssigned ? 'unassigned' : 'assigned'} ${member.name} on subtask "${subtask.name}"`,
      {
        projectId: task.projectId,
        taskId,
        notify: isAssigned ? 'task_update' : 'task_assigned',
        notifyTitle: isAssigned ? 'Subtask update' : 'Subtask assignment',
        notifyUserIds: isAssigned ? undefined : [member.id],
      }
    );
  };

  const toggleTaskFollower = async (taskId, memberLabel) => {
    const task = tasks.find((item) => item.id === taskId);
    const member = currentProject?.members.find(
      (item) => `${item.name} (${item.role})` === memberLabel
    );
    if (!task || !member) return;

    setDatabaseError('');
    const isFollowing = (task.followers || []).includes(memberLabel);
    const query = isFollowing
      ? supabase
          .from('task_followers')
          .delete()
          .eq('task_id', taskId)
          .eq('user_id', member.id)
      : supabase
          .from('task_followers')
          .insert({ task_id: taskId, user_id: member.id });
    const { error } = await query;

    if (error) {
      const missingTable = /task_followers|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? {
              title: 'Database table missing: task_followers',
              message:
                'Followers need the task_followers table. Run the latest migration in Supabase, then try again.',
              steps: [
                'Open the Supabase SQL editor for this project.',
                'Run frontend/supabase/migrations/20260720190000_task_followers.sql',
                'Reload the app and add followers again.',
              ],
            }
          : toUserAlert(error, 'Could not update task followers')
      );
      return;
    }

    await loadTasks();
    logActivity(
      `${currentUser.name} ${isFollowing ? 'removed' : 'added'} follower ${member.name} on "${task.name}"`,
      { projectId: task.projectId, taskId }
    );
  };

  const subtasksMissingAlert = {
    title: 'Database table missing: task_subtasks',
    message:
      'Subtasks need the task_subtasks table. Run the latest migration in Supabase, then try again.',
    steps: [
      'Open the Supabase SQL editor for this project.',
      'Run frontend/supabase/migrations/20260720191000_task_subtasks.sql',
      'Reload the app and manage subtasks again.',
    ],
  };

  const handleAddSubtask = async (taskId, event) => {
    event?.preventDefault?.();
    const draft = (newSubtaskByTaskId[taskId] || '').trim();
    if (!draft) return;

    const task = tasks.find((item) => item.id === taskId);
    if (!task) return;

    setDatabaseError('');
    setSubtaskSavingTaskId(taskId);
    try {
      const nextOrder = (task.subtasks || []).length;
      const { error } = await supabase.from('task_subtasks').insert({
        task_id: taskId,
        name: draft,
        sort_order: nextOrder,
        created_by: currentUser.id,
      });

      if (error) {
        const missingTable = /task_subtasks|schema cache|does not exist/i.test(
          error.message || ''
        );
        setDatabaseError(
          missingTable
            ? subtasksMissingAlert
            : toUserAlert(error, 'Could not add subtask')
        );
        return;
      }

      setNewSubtaskByTaskId((prev) => ({ ...prev, [taskId]: '' }));
      await loadTasks();
      logActivity(
        `${currentUser.name} added subtask "${draft}" on "${task.name}"`,
        { projectId: task.projectId, taskId }
      );
    } finally {
      setSubtaskSavingTaskId(null);
    }
  };

  const toggleSubtaskComplete = async (taskId, subtask) => {
    if (!subtask?.id) return;
    setDatabaseError('');
    const nextCompleted = !subtask.isCompleted;
    const nextStatus = nextCompleted
      ? 'Done'
      : subtask.status && String(subtask.status).toLowerCase() !== 'done'
        ? subtask.status
        : 'To Do';
    let { error } = await supabase
      .from('task_subtasks')
      .update({
        is_completed: nextCompleted,
        status: nextStatus,
        completed_at: nextCompleted ? new Date().toISOString() : null,
      })
      .eq('id', subtask.id)
      .eq('task_id', taskId);

    if (error && /status|schema cache|column/i.test(error.message || '')) {
      ({ error } = await supabase
        .from('task_subtasks')
        .update({
          is_completed: nextCompleted,
          completed_at: nextCompleted ? new Date().toISOString() : null,
        })
        .eq('id', subtask.id)
        .eq('task_id', taskId));
    }

    if (error) {
      const missingTable = /task_subtasks|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? subtasksMissingAlert
          : toUserAlert(error, 'Could not update subtask')
      );
      return;
    }

    setTasks((prev) =>
      prev.map((task) =>
        task.id !== taskId
          ? task
          : {
              ...task,
              subtasks: (task.subtasks || []).map((item) =>
                item.id === subtask.id
                  ? {
                      ...item,
                      isCompleted: nextCompleted,
                      status: nextStatus,
                    }
                  : item
              ),
            }
      )
    );
    logActivity(
      `${currentUser.name} marked subtask "${subtask.name}" as ${
        nextCompleted ? 'complete' : 'incomplete'
      }`,
      {
        projectId: tasks.find((item) => item.id === taskId)?.projectId,
        taskId,
      }
    );
  };

  const handleDeleteSubtask = async (taskId, subtask) => {
    if (!subtask?.id) return;
    const confirmed = window.confirm(
      `Delete subtask "${subtask.name}"?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDatabaseError('');
    const { error } = await supabase
      .from('task_subtasks')
      .delete()
      .eq('id', subtask.id)
      .eq('task_id', taskId);

    if (error) {
      const missingTable = /task_subtasks|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? subtasksMissingAlert
          : toUserAlert(error, 'Could not delete subtask')
      );
      return;
    }

    setTasks((prev) =>
      prev.map((task) =>
        task.id !== taskId
          ? task
          : {
              ...task,
              subtasks: (task.subtasks || []).filter(
                (item) => item.id !== subtask.id
              ),
            }
      )
    );
    logActivity(
      `${currentUser.name} deleted subtask "${subtask.name}"`,
      {
        projectId: tasks.find((item) => item.id === taskId)?.projectId,
        taskId,
      }
    );
  };

  const handleRenameTask = async (id) => {
    if (!editTaskName.trim()) return;

    setDatabaseError('');
    const taskName = editTaskName.trim();
    const { error } = await supabase
      .from('tasks')
      .update({ name: taskName })
      .eq('id', id);

    if (error) {
      setDatabaseError(error.message);
      return;
    }

    setTasks(tasks.map(t => t.id === id ? { ...t, name: taskName } : t));
    setTrackerEntries((prev) =>
      prev.map((entry) =>
        entry.taskId === id ? { ...entry, taskName } : entry
      )
    );
    const renamed = tasks.find((item) => item.id === id);
    logActivity(`Renamed objective line item to "${taskName}"`, {
      projectId: renamed?.projectId,
      taskId: id,
    });
    setEditingTaskId(null);
  };

  const handleRenameSubtask = async (taskId, subtaskId) => {
    if (!editSubtaskName.trim()) return;

    setDatabaseError('');
    const nextName = editSubtaskName.trim();
    const { error } = await supabase
      .from('task_subtasks')
      .update({ name: nextName })
      .eq('id', subtaskId)
      .eq('task_id', taskId);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not rename subtask'));
      return;
    }

    setTasks((prev) =>
      prev.map((task) =>
        task.id !== taskId
          ? task
          : {
              ...task,
              subtasks: (task.subtasks || []).map((sub) =>
                sub.id === subtaskId ? { ...sub, name: nextName } : sub
              ),
            }
      )
    );
    const parent = tasks.find((item) => item.id === taskId);
    logActivity(
      `${currentUser.name} renamed subtask to "${nextName}"`,
      { projectId: parent?.projectId, taskId }
    );
    setEditingSubtaskKey(null);
  };

  const formatEstimatedHours = (seconds) => {
    if (seconds == null || Number.isNaN(Number(seconds))) return '—';
    const hours = Number(seconds) / 3600;
    const rounded = Math.round(hours * 100) / 100;
    return `${rounded}h`;
  };

  const secondsToHoursInput = (seconds) => {
    if (seconds == null || Number.isNaN(Number(seconds))) return '';
    const hours = Number(seconds) / 3600;
    return String(Math.round(hours * 100) / 100);
  };

  const parseHoursToSeconds = (rawValue) => {
    const trimmed = String(rawValue ?? '').trim().replace(/h$/i, '');
    if (trimmed === '') return { seconds: null, error: null };
    const hours = Number(trimmed);
    if (!Number.isFinite(hours) || hours < 0) {
      return {
        seconds: NaN,
        error: 'Estimated time must be a non-negative number of hours (e.g. 2.5).',
      };
    }
    return { seconds: Math.round(hours * 3600), error: null };
  };

  const updateTaskEstimatedTime = async (taskId, rawValue) => {
    const { seconds: parsed, error: parseError } = parseHoursToSeconds(rawValue);
    if (parseError) {
      setDatabaseError({
        title: 'Invalid estimated time',
        message: parseError,
        steps: ['Enter hours like 1, 2.5, or clear the field.'],
      });
      return;
    }

    setDatabaseError('');
    const { error } = await supabase
      .from('tasks')
      .update({ estimated_seconds: parsed })
      .eq('id', taskId);

    if (error) {
      const missing = /estimated_seconds|estimated_budget|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missing
          ? {
              title: 'Database column missing: tasks.estimated_seconds',
              message: 'Estimated Time needs a migration before it can be saved.',
              steps: [
                'Open the Supabase SQL editor.',
                'Run frontend/supabase/migrations/20260720197000_tasks_estimated_seconds.sql',
                'Reload and set the estimated time again.',
              ],
            }
          : toUserAlert(error, 'Could not update estimated time')
      );
      return;
    }

    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId ? { ...task, estimatedSeconds: parsed } : task
      )
    );
    setEstimatedHoursDraftByTaskId((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    const task = tasks.find((item) => item.id === taskId);
    logActivity(
      `${currentUser.name} set estimated time on "${task?.name || 'task'}" to ${
        parsed == null ? 'none' : formatEstimatedHours(parsed)
      }`,
      { projectId: task?.projectId, taskId }
    );
  };

  const scheduleColumnsMissingAlert = {
    title: 'Database columns missing: start_date / end_date',
    message:
      'Start Date and Duration Date need a migration before they can be saved.',
    steps: [
      'Open the Supabase SQL editor.',
      'Run frontend/supabase/migrations/20260720199000_task_schedule_dates.sql',
      'Reload and set the dates again.',
    ],
  };

  const updateTaskScheduleDate = async (taskId, field, value) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) return;

    const nextStart = field === 'startDate' ? value || '' : task.startDate || '';
    const nextEnd = field === 'endDate' ? value || '' : task.endDate || '';
    if (nextStart && nextEnd && nextEnd < nextStart) {
      setDatabaseError({
        title: 'Invalid schedule',
        message: 'Duration date must be on or after the start date.',
        steps: ['Adjust Start date or Duration date, then try again.'],
      });
      return;
    }

    setDatabaseError('');
    const payload =
      field === 'startDate'
        ? { start_date: value || null }
        : { end_date: value || null };
    const { error } = await supabase
      .from('tasks')
      .update(payload)
      .eq('id', taskId);

    if (error) {
      const missing = /start_date|end_date|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missing
          ? scheduleColumnsMissingAlert
          : toUserAlert(error, 'Could not update task schedule')
      );
      return;
    }

    setTasks((prev) =>
      prev.map((item) =>
        item.id === taskId
          ? {
              ...item,
              startDate: field === 'startDate' ? value || '' : item.startDate,
              endDate: field === 'endDate' ? value || '' : item.endDate,
            }
          : item
      )
    );
    logActivity(
      `${currentUser.name} updated ${
        field === 'startDate' ? 'start date' : 'duration date'
      } on "${task.name}"`,
      { projectId: task.projectId, taskId }
    );
  };

  const updateSubtaskScheduleDate = async (taskId, subtaskId, field, value) => {
    const task = tasks.find((item) => item.id === taskId);
    const subtask = (task?.subtasks || []).find((item) => item.id === subtaskId);
    if (!task || !subtask) return;

    const nextStart =
      field === 'startDate' ? value || '' : subtask.startDate || '';
    const nextEnd = field === 'endDate' ? value || '' : subtask.endDate || '';
    if (nextStart && nextEnd && nextEnd < nextStart) {
      setDatabaseError({
        title: 'Invalid schedule',
        message: 'Duration date must be on or after the start date.',
        steps: ['Adjust Start date or Duration date, then try again.'],
      });
      return;
    }

    setDatabaseError('');
    const payload =
      field === 'startDate'
        ? { start_date: value || null }
        : { end_date: value || null };
    const { error } = await supabase
      .from('task_subtasks')
      .update(payload)
      .eq('id', subtaskId);

    if (error) {
      const missing = /start_date|end_date|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missing
          ? scheduleColumnsMissingAlert
          : toUserAlert(error, 'Could not update subtask schedule')
      );
      return;
    }

    setTasks((prev) =>
      prev.map((item) =>
        item.id === taskId
          ? {
              ...item,
              subtasks: (item.subtasks || []).map((sub) =>
                sub.id === subtaskId
                  ? {
                      ...sub,
                      startDate:
                        field === 'startDate' ? value || '' : sub.startDate,
                      endDate: field === 'endDate' ? value || '' : sub.endDate,
                    }
                  : sub
              ),
            }
          : item
      )
    );
  };

  const getAssigneeInitials = (label) => {
    const name = String(label || '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim();
    if (!name || name === 'Unassigned') return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  };

  const toggleListOptionalColumn = (key) => {
    setListColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem('tikix.projectListColumns.v3', JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // NEW OPERATOR: Custom Task Deletion Logic (project admin protected)
  const handleDeleteTask = async (id, name) => {
    const task = tasks.find((item) => item.id === id);
    if (!task || !canManageProject(task.projectId)) return;

    setDatabaseError('');
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);

    if (error) {
      setDatabaseError(error.message);
      return;
    }

    setTasks(tasks.filter((t) => t.id !== id));
    logActivity(`${currentUser.name} deleted task item: "${name}"`, {
      projectId: task.projectId,
      taskId: id,
    });
  };

  const normalizeActivityLinkUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^[\w.-]+\.[a-z]{2,}([/:?#].*)?$/i.test(raw)) return `https://${raw}`;
    return raw;
  };

  const resolveActivityTaskName = (activity) => {
    if (!activity) return null;
    if (activity.taskName) return activity.taskName;
    if (!activity.taskId) return null;
    return tasks.find((task) => task.id === activity.taskId)?.name || null;
  };

  const activityMentionMembers = getMentionableMembers(
    projects.find((project) => project.id === activeProjectId)?.members || []
  );
  const activityMentionNames = [
    ...new Set([
      ...activityMentionMembers.map((member) => member.name),
      ...activities.flatMap((item) => [
        ...(item.mentions || []).map((mention) => mention.name),
        ...(item.replies || []).flatMap((reply) =>
          (reply.mentions || []).map((mention) => mention.name)
        ),
      ]),
    ]),
  ];

  const mentionSuggestions =
    mentionMenu &&
    filterMentionMembers(activityMentionMembers, mentionMenu.query);

  const syncMentionMenu = (target, value, caret) => {
    const active = getActiveMentionQuery(value, caret);
    if (!active) {
      setMentionMenu(null);
      return;
    }
    setMentionMenu({
      target,
      query: active.query,
      startIndex: active.startIndex,
      endIndex: active.endIndex,
      selectedIndex: 0,
    });
  };

  const applyMentionSelection = (member) => {
    if (!mentionMenu || !member?.name) return;
    const isComment = mentionMenu.target === 'comment';
    const current = isComment ? newComment : replyText;
    const { text, caret } = insertMentionAt(
      current,
      mentionMenu.startIndex,
      mentionMenu.endIndex,
      member.name
    );
    if (isComment) setNewComment(text);
    else setReplyText(text);
    setMentionMenu(null);
    requestAnimationFrame(() => {
      const el = isComment
        ? commentInputRef.current
        : replyTextInputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleMentionKeyDown = (event) => {
    if (!mentionMenu || !mentionSuggestions?.length) {
      if (event.key === 'Escape' && mentionMenu) {
        event.preventDefault();
        setMentionMenu(null);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionMenu((prev) =>
        prev
          ? {
              ...prev,
              selectedIndex:
                (prev.selectedIndex + 1) % mentionSuggestions.length,
            }
          : prev
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionMenu((prev) =>
        prev
          ? {
              ...prev,
              selectedIndex:
                (prev.selectedIndex - 1 + mentionSuggestions.length) %
                mentionSuggestions.length,
            }
          : prev
      );
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      applyMentionSelection(
        mentionSuggestions[mentionMenu.selectedIndex] || mentionSuggestions[0]
      );
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setMentionMenu(null);
    }
  };

  const renderMentionSuggestionMenu = (target) => {
    if (!mentionMenu || mentionMenu.target !== target) return null;
    if (!mentionSuggestions?.length) {
      return (
        <div className="absolute left-0 right-0 bottom-full mb-1 z-40 rounded-xl border border-gray-200 bg-white shadow-lg px-3 py-2">
          <p className="text-[11px] text-gray-400">
            No project members match “{mentionMenu.query}”
          </p>
        </div>
      );
    }
    return (
      <div className="absolute left-0 right-0 bottom-full mb-1 z-40 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
        <p className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-purple-700 border-b border-gray-100 bg-purple-50/60">
          Mention a teammate
        </p>
        <div className="max-h-40 overflow-y-auto py-1">
          {mentionSuggestions.map((member, index) => {
            const isActive = index === mentionMenu.selectedIndex;
            return (
              <button
                key={`mention-${target}-${member.id}`}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  applyMentionSelection(member);
                }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                  isActive ? 'bg-purple-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-800 text-[10px] font-black flex items-center justify-center shrink-0">
                  {getAssigneeInitials(member.name)}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-gray-900 truncate">
                    {member.name}
                  </span>
                  <span className="block text-[10px] text-gray-400 truncate">
                    {member.role || member.email || 'Teammate'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const notifyActivityMentions = ({
    mentions,
    message,
    projectId,
    taskId,
  }) => {
    const recipientUserIds = [
      ...new Set(
        (mentions || [])
          .map((mention) => mention.user_id)
          .filter((id) => id && id !== currentUser.id)
      ),
    ];
    if (!recipientUserIds.length || !projectId || !message) return;
    void dispatchProjectNotification({
      eventType: 'mention',
      projectId,
      taskId: taskId || null,
      message,
      title: 'You were mentioned',
      recipientUserIds,
    });
  };

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (activityPosting || !activeProjectId) return;
    const linkUrl = normalizeActivityLinkUrl(commentLinkUrl);
    if (!newComment.trim() && commentFiles.length === 0 && !linkUrl) return;

    setActivityPosting(true);
    setDatabaseError('');
    setMentionMenu(null);
    const files = commentFiles;
    const selectedTaskId = commentTaskId || null;
    const selectedTaskName =
      tasks.find((task) => task.id === selectedTaskId)?.name || null;
    const bodyText = newComment.trim();
    const mentions = extractActivityMentions(
      bodyText,
      projects.find((project) => project.id === activeProjectId)?.members || []
    );
    const text = bodyText ? `${currentUser.name}: ${bodyText}` : '';
    setNewComment('');
    setCommentFiles([]);
    setCommentTaskId('');
    setCommentLinkUrl('');
    await logActivity(text, {
      projectId: activeProjectId,
      taskId: selectedTaskId,
      taskName: selectedTaskName,
      files,
      linkUrl: linkUrl || undefined,
      details: {
        kind: 'post',
        ...(mentions.length ? { mentions } : {}),
        ...(selectedTaskId
          ? { task_id: selectedTaskId, task_name: selectedTaskName }
          : {}),
        ...(linkUrl ? { link_url: linkUrl } : {}),
      },
    });
    if (text && mentions.length) {
      notifyActivityMentions({
        mentions,
        message: text,
        projectId: activeProjectId,
        taskId: selectedTaskId,
      });
    }
    revokePendingFileUrls(files);
    setActivityPosting(false);
  };

  const handlePostReply = async (e, parentId, parentTaskId = null) => {
    e.preventDefault();
    if (activityPosting || !activeProjectId || !parentId) return;
    if (!replyText.trim() && replyFiles.length === 0) return;

    setActivityPosting(true);
    setDatabaseError('');
    setMentionMenu(null);
    const files = replyFiles;
    const selectedTaskId = replyTaskId || parentTaskId || null;
    const selectedTaskName =
      tasks.find((task) => task.id === selectedTaskId)?.name || null;
    const bodyText = replyText.trim();
    const mentions = extractActivityMentions(
      bodyText,
      projects.find((project) => project.id === activeProjectId)?.members || []
    );
    const text = bodyText ? `${currentUser.name}: ${bodyText}` : '';
    setReplyText('');
    setReplyFiles([]);
    setReplyTaskId('');
    setReplyToId(null);
    await logActivity(text, {
      projectId: activeProjectId,
      parentId,
      taskId: selectedTaskId,
      taskName: selectedTaskName,
      files,
      details: {
        kind: 'reply',
        ...(mentions.length ? { mentions } : {}),
        ...(selectedTaskId
          ? { task_id: selectedTaskId, task_name: selectedTaskName }
          : {}),
      },
    });
    if (text && mentions.length) {
      notifyActivityMentions({
        mentions,
        message: text,
        projectId: activeProjectId,
        taskId: selectedTaskId,
      });
    }
    revokePendingFileUrls(files);
    setActivityPosting(false);
  };

  const getActivityBodyText = (activity) => {
    const text = String(activity?.text || '');
    const namedPrefix = `${currentUser.name}: `;
    if (text.startsWith(namedPrefix)) return text.slice(namedPrefix.length);
    const match = text.match(/^[^:\n]+:\s([\s\S]*)$/);
    return match ? match[1] : text;
  };

  const canEditActivityPost = (activity) => {
    if (!activity?.id || !currentUser?.id) return false;
    if (String(activity.id).startsWith('local-')) return false;
    if (activity.actorId !== currentUser.id) return false;
    if (activity.kind === 'post' || activity.kind === 'reply') return true;
    // Legacy user feed posts before kind was stored.
    const details = activity.details || {};
    if (details.time_entry_id || details.source === 'client') return false;
    if (details.kind === 'time_adjustment') return false;
    return String(activity.text || '').startsWith(`${currentUser.name}:`);
  };

  const beginEditActivity = (activity) => {
    if (!canEditActivityPost(activity)) return;
    setReplyToId(null);
    setMentionMenu(null);
    setEditingActivityId(activity.id);
    setEditActivityText(getActivityBodyText(activity));
    setEditActivityTaskId(activity.taskId || '');
    setEditActivityLinkUrl(activity.linkUrl || '');
  };

  const cancelEditActivity = () => {
    setEditingActivityId(null);
    setEditActivityText('');
    setEditActivityTaskId('');
    setEditActivityLinkUrl('');
    setMentionMenu(null);
  };

  const applyActivityUpdateLocally = (activityId, patch) => {
    setActivities((prev) =>
      prev.map((item) => {
        if (item.id === activityId) {
          return { ...item, ...patch, replies: item.replies || [] };
        }
        return {
          ...item,
          replies: (item.replies || []).map((reply) =>
            reply.id === activityId ? { ...reply, ...patch } : reply
          ),
        };
      })
    );
  };

  const handleSaveActivityEdit = async (activity) => {
    if (!canEditActivityPost(activity) || activityEditSaving) return;
    const bodyText = editActivityText.trim();
    const isReply = Boolean(activity.parentId) || activity.kind === 'reply';
    const linkUrl = isReply
      ? ''
      : normalizeActivityLinkUrl(editActivityLinkUrl);
    if (!bodyText && !(activity.attachments || []).length && !linkUrl) {
      setDatabaseError({
        title: 'Nothing to save',
        message: 'Enter a message, or keep an attachment / link on the post.',
        steps: ['Add text to your post, then Save.'],
      });
      return;
    }

    const members =
      projects.find((project) => project.id === activeProjectId)?.members || [];
    const mentions = extractActivityMentions(bodyText, members);
    const selectedTaskId = editActivityTaskId || null;
    const selectedTaskName =
      tasks.find((task) => task.id === selectedTaskId)?.name || null;
    const displayMessage = bodyText
      ? `${currentUser.name}: ${bodyText}`
      : activity.text;
    const nextKind =
      activity.kind ||
      (isReply ? 'reply' : 'post');
    const nextDetails = {
      ...(activity.details || {}),
      kind: nextKind,
      message: displayMessage,
      ...(mentions.length ? { mentions } : { mentions: [] }),
      ...(selectedTaskId
        ? { task_id: selectedTaskId, task_name: selectedTaskName }
        : { task_id: null, task_name: null }),
      ...(linkUrl ? { link_url: linkUrl } : { link_url: null }),
      edited_at: new Date().toISOString(),
    };

    setActivityEditSaving(true);
    setDatabaseError('');
    const { data, error } = await supabase
      .from('activities')
      .update({
        action: displayMessage,
        task_id: selectedTaskId,
        details: nextDetails,
      })
      .eq('id', activity.id)
      .eq('actor_id', currentUser.id)
      .select('id, action, details, created_at, actor_id, parent_id, task_id')
      .maybeSingle();

    if (error || !data) {
      const denied = /policy|permission|rls/i.test(error?.message || '');
      setDatabaseError(
        denied
          ? {
              title: 'Database update required',
              message:
                'Editing your posts needs a policy that allows authors to update feed posts.',
              steps: [
                'Open the Supabase SQL editor.',
                'Run frontend/supabase/migrations/20260723110000_authors_can_update_own_feed_posts.sql',
                'Reload and try saving again.',
              ],
            }
          : toUserAlert(error || 'Update failed', 'Could not update your post')
      );
      setActivityEditSaving(false);
      return;
    }

    const mapped = mapActivityRow(data, {
      attachments: activity.attachments || [],
      replies: activity.replies || [],
      taskName: selectedTaskName,
      linkUrl: linkUrl || null,
    });
    applyActivityUpdateLocally(activity.id, {
      text: mapped.text,
      taskId: mapped.taskId,
      taskName: mapped.taskName,
      linkUrl: mapped.linkUrl,
      kind: mapped.kind,
      editedAt: mapped.editedAt,
      details: mapped.details,
      mentions: mapped.mentions,
    });
    cancelEditActivity();
    setActivityEditSaving(false);
  };

  const renderActivityAttachments = (attachments) => {
    if (!attachments?.length) return null;
    return (
      <div className="mt-2 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
          Linked files
        </p>
        <div className="flex flex-wrap gap-2">
          {attachments
            .filter((file) => file.isImage && file.url)
            .map((file) => (
              <a
                key={file.id}
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="block"
                title={file.fileName}
              >
                <img
                  src={file.url}
                  alt={file.fileName}
                  className="h-20 w-auto max-w-[140px] rounded-lg border border-gray-200 object-cover bg-white"
                />
              </a>
            ))}
        </div>
        <div className="flex flex-col gap-1">
          {attachments
            .filter((file) => !file.isImage || !file.url)
            .map((file) => (
              <a
                key={file.id}
                href={file.url || undefined}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${
                  file.url
                    ? 'text-purple-700 hover:underline'
                    : 'text-gray-400 pointer-events-none'
                }`}
              >
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {file.fileName}
              </a>
            ))}
        </div>
      </div>
    );
  };

  const renderPendingFiles = (files, onRemove) => {
    if (!files.length) return null;
    return (
      <div className="flex flex-wrap gap-2 mb-2">
        {files.map((item) => (
          <div
            key={item.id}
            className="relative group rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
          >
            {item.previewUrl ? (
              <img
                src={item.previewUrl}
                alt={item.file.name}
                className="h-14 w-14 object-cover"
              />
            ) : (
              <div className="h-14 px-2 flex items-center text-[10px] text-gray-600 max-w-[120px] truncate">
                {item.file.name}
              </div>
            )}
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 text-[10px] leading-none"
              aria-label="Remove attachment"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  const toggleTimer = async (taskId, taskName) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    setDatabaseError('');
    if (task.isRunning && task.activeTimeEntryId) {
      const { closed, error } = await closeOpenTimeEntriesForCurrentUser();
      if (error) {
        setDatabaseError(toUserAlert(error, 'Could not stop the timer'));
        return;
      }

      const primary =
        closed.find((item) => item.id === task.activeTimeEntryId) || closed[0];
      const durationSeconds = primary?.durationSeconds || 0;

      setTasks((previousTasks) =>
        previousTasks.map((item) =>
          item.isRunning
            ? {
                ...item,
                isRunning: false,
                activeTimeEntryId: null,
                activeStartedAt: null,
              }
            : item
        )
      );
      await loadTasks();
      await ensureCurrentUserProjectMember(task.projectId);
      if (primary) {
        await logSavedHoursActivity({
          projectId: task.projectId,
          taskId,
          taskName,
          durationSeconds,
          timeEntryId: primary.id,
        });
      }
      return;
    }

    const assignError = await ensureCurrentUserAssignedToTask(task);
    if (assignError) {
      setDatabaseError(assignError);
      return;
    }

    // Enforce a single running timer: close any leftover open entries first.
    await closeOpenTimeEntriesForCurrentUser();

    const { data: timeEntry, error } = await supabase
      .from('time_entries')
      .insert({ task_id: taskId, user_id: currentUser.id })
      .select('id, started_at')
      .single();

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not start the timer'));
      return;
    }

    await Promise.all([loadProjects(), loadTasks()]);
    // Keep the just-started timer visible even if loadTasks races.
    setTasks((previousTasks) =>
      previousTasks.map((item) =>
        item.id === taskId
          ? {
              ...item,
              completedSeconds:
                item.completedSeconds != null
                  ? item.completedSeconds
                  : Math.max(0, item.timeSpent || 0),
              isRunning: true,
              activeTimeEntryId: timeEntry.id,
              activeStartedAt: timeEntry.started_at,
            }
          : item
      )
    );
    logActivity(
      `${currentUser.name} started timer on "${taskName}" and was auto-assigned`,
      { projectId: task.projectId, taskId, notify: false }
    );
  };

  const startQuickTimer = async (taskId) => {
    if (projects.length === 0) {
      setDatabaseError(USER_ALERTS.noProjects);
      return;
    }
    if (!taskId || tasks.length === 0) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    const task = tasks.find((item) => item.id === taskId);
    if (!task) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    const runningTask = tasks.find((item) => item.isRunning);
    if (runningTask && runningTask.id !== taskId) {
      await toggleTimer(runningTask.id, runningTask.name);
    }
    if (!task.isRunning) {
      await toggleTimer(taskId, task.name);
    }
    setSelectedQuickTaskId('');
    setTimerMenuOpen(false);
  };

  const handleQuickTimerStart = async (event) => {
    event?.preventDefault?.();

    if (projects.length === 0) {
      setDatabaseError(USER_ALERTS.noProjects);
      return;
    }

    if (!quickTimerProjectId) {
      setDatabaseError(USER_ALERTS.noProjectSelected);
      return;
    }

    if (!quickTimerTaskId && !quickTimerDescription.trim()) {
      setDatabaseError({
        title: 'Description or task required',
        message:
          'Enter what you are working on, or select an existing task for this project.',
        steps: [
          'Type a description, or pick a task from the Task dropdown.',
          'Confirm the project is selected.',
          'Press Start.',
        ],
      });
      return;
    }

    setQuickTimerStarting(true);
    setDatabaseError('');

    try {
      const membershipError = await ensureCurrentUserProjectMember(quickTimerProjectId);
      if (membershipError) {
        setDatabaseError(membershipError);
        return;
      }

      // One timer at a time — close any open session before starting.
      await closeOpenTimeEntriesForCurrentUser();

      const resolved = await resolveOrCreateQuickTask({
        projectId: quickTimerProjectId,
        description: quickTimerDescription,
        clientId: quickTimerClientId,
        existingTaskId: quickTimerTaskId || null,
      });
      if (resolved.error) {
        setDatabaseError(resolved.error);
        return;
      }

      const { error: timeError } = await supabase
        .from('time_entries')
        .insert({ task_id: resolved.taskId, user_id: currentUser.id });

      if (timeError) {
        setDatabaseError(toUserAlert(timeError, 'Could not start the timer'));
        await Promise.all([loadProjects(), loadTasks()]);
        return;
      }

      await Promise.all([loadProjects(), loadTasks()]);
      setQuickTimerDescription('');
      setQuickTimerTaskId('');
      logActivity(
        `${currentUser.name} started timer on "${resolved.taskName}" (auto-assigned)`,
        { projectId: quickTimerProjectId, taskId: resolved.taskId, notify: false }
      );
    } finally {
      setQuickTimerStarting(false);
    }
  };

  const resolveOrCreateQuickTask = async ({
    projectId,
    description,
    clientId,
    existingTaskId,
  }) => {
    if (existingTaskId) {
      const existing = tasks.find((item) => item.id === existingTaskId);
      if (!existing || existing.projectId !== projectId) {
        return { error: USER_ALERTS.noTasksAvailable };
      }
      const assignError = await ensureCurrentUserAssignedToTask(existing);
      if (assignError) return { error: assignError };
      return { taskId: existing.id, taskName: existing.name, created: false };
    }

    const taskName =
      description.trim() ||
      projects.find((project) => project.id === projectId)?.name ||
      'Untitled';

    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .insert({
        project_id: projectId,
        name: taskName,
        created_by: currentUser.id,
      })
      .select('id')
      .single();

    if (taskError) {
      return { error: toUserAlert(taskError, 'Could not create the task') };
    }

    const { error: assigneeError } = await supabase
      .from('task_assignees')
      .upsert(
        { task_id: task.id, user_id: currentUser.id },
        { onConflict: 'task_id,user_id', ignoreDuplicates: true }
      );

    if (assigneeError) {
      return {
        error: toUserAlert(assigneeError, 'Could not assign you to the task'),
      };
    }

    if (clientId) {
      const { error: clientError } = await supabase
        .from('task_clients')
        .insert({ task_id: task.id, client_id: clientId });

      if (clientError) {
        return {
          error: toUserAlert(clientError, 'Could not link the client'),
        };
      }
    }

    return { taskId: task.id, taskName, created: true };
  };

  const handleManualTimeLog = async (event) => {
    event?.preventDefault?.();

    if (projects.length === 0) {
      setDatabaseError(USER_ALERTS.noProjects);
      return;
    }

    if (!quickTimerProjectId) {
      setDatabaseError({
        ...USER_ALERTS.noProjectSelected,
        message: 'A project is required before you can add a manual time entry.',
        steps: [
          'Choose a project from the Project dropdown.',
          'Optionally pick an existing task or enter a description.',
          'Set the date, start, and end times, then press Add.',
        ],
      });
      return;
    }

    if (!quickTimerTaskId && !quickTimerDescription.trim()) {
      setDatabaseError({
        title: 'Description or task required',
        message:
          'Enter what you worked on, or select an existing assigned task.',
        steps: [
          'Type a description to create a new task entry, or',
          'Select an existing task from the Task dropdown for this project.',
          'Then set the time range and press Add.',
        ],
      });
      return;
    }

    const range = parseManualTimeRange(
      manualLogDate,
      manualLogStart,
      manualLogEnd
    );
    if (range.error) {
      setDatabaseError({
        title: 'Invalid time range',
        message: range.error,
        steps: [
          'Choose a date.',
          'Set start and end times (end can be next day if overnight).',
          'Press Add again.',
        ],
      });
      return;
    }

    setManualLogSaving(true);
    setDatabaseError('');

    try {
      const membershipError = await ensureCurrentUserProjectMember(
        quickTimerProjectId
      );
      if (membershipError) {
        setDatabaseError(membershipError);
        return;
      }

      const resolved = await resolveOrCreateQuickTask({
        projectId: quickTimerProjectId,
        description: quickTimerDescription,
        clientId: quickTimerClientId,
        existingTaskId: quickTimerTaskId || null,
      });
      if (resolved.error) {
        setDatabaseError(resolved.error);
        return;
      }

      const { data: timeEntry, error: timeError } = await supabase
        .from('time_entries')
        .insert({
          task_id: resolved.taskId,
          user_id: currentUser.id,
          started_at: range.startedAt.toISOString(),
          ended_at: range.endedAt.toISOString(),
          duration_seconds: range.durationSeconds,
        })
        .select('id')
        .single();

      if (timeError) {
        setDatabaseError(toUserAlert(timeError, 'Could not save manual time'));
        return;
      }

      await Promise.all([loadProjects(), loadTasks()]);
      setQuickTimerDescription('');
      setQuickTimerTaskId('');
      setTimerStatus(
        `Logged ${formatTime(range.durationSeconds)} on "${resolved.taskName}".`
      );
      await logSavedHoursActivity({
        projectId: quickTimerProjectId,
        taskId: resolved.taskId,
        taskName: resolved.taskName,
        durationSeconds: range.durationSeconds,
        timeEntryId: timeEntry?.id,
      });
    } finally {
      setManualLogSaving(false);
    }
  };

  const handleTaskManualTimeLog = async (taskId, event) => {
    event?.preventDefault?.();
    const task = tasks.find((item) => item.id === taskId);
    if (!task) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    const range = parseManualTimeRange(
      taskManualDate,
      taskManualStart,
      taskManualEnd
    );
    if (range.error) {
      setDatabaseError({
        title: 'Invalid time range',
        message: range.error,
        steps: [
          'Choose a date.',
          'Set start and end times.',
          'Press Add time again.',
        ],
      });
      return;
    }

    setManualLogSaving(true);
    setDatabaseError('');

    try {
      const assignError = await ensureCurrentUserAssignedToTask(task);
      if (assignError) {
        setDatabaseError(assignError);
        return;
      }

      const { data: timeEntry, error: timeError } = await supabase
        .from('time_entries')
        .insert({
          task_id: task.id,
          user_id: currentUser.id,
          started_at: range.startedAt.toISOString(),
          ended_at: range.endedAt.toISOString(),
          duration_seconds: range.durationSeconds,
        })
        .select('id')
        .single();

      if (timeError) {
        setDatabaseError(toUserAlert(timeError, 'Could not save manual time'));
        return;
      }

      await loadTasks();
      setLoggingTimeTaskId(null);
      setTimerStatus(
        `Logged ${formatTime(range.durationSeconds)} on "${task.name}".`
      );
      await logSavedHoursActivity({
        projectId: task.projectId,
        taskId: task.id,
        taskName: task.name,
        durationSeconds: range.durationSeconds,
        timeEntryId: timeEntry?.id,
      });
    } finally {
      setManualLogSaving(false);
    }
  };

  const saveTimer = async (taskId) => {
    const task = tasks.find((item) => item.id === taskId);
    if (!task) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    setDatabaseError('');
    setTimerStatus('Saving…');

    try {
      let sessionSeconds = 0;
      let closedEntryId = null;

      // Persist any open entry first (and clear orphan "- now" rows).
      if (task.isRunning || task.activeTimeEntryId) {
        const { closed, error: stopError } =
          await closeOpenTimeEntriesForCurrentUser();
        if (stopError) {
          setDatabaseError(toUserAlert(stopError, 'Could not save the timer'));
          setTimerStatus('');
          return;
        }
        const primary =
          closed.find((item) => item.id === task.activeTimeEntryId) ||
          closed[0];
        sessionSeconds = primary?.durationSeconds || 0;
        closedEntryId = primary?.id || null;
      }

      const membershipError = await ensureCurrentUserProjectMember(task.projectId);
      if (membershipError) {
        setDatabaseError(membershipError);
        setTimerStatus('');
        return;
      }

      const { error: assigneeError } = await supabase
        .from('task_assignees')
        .upsert(
          { task_id: task.id, user_id: currentUser.id },
          { onConflict: 'task_id,user_id', ignoreDuplicates: true }
        );

      if (assigneeError) {
        setDatabaseError(
          toUserAlert(assigneeError, 'Could not assign you on the project task')
        );
        setTimerStatus('');
        return;
      }

      await Promise.all([loadProjects(), loadTasks()]);

      const projectName =
        projects.find((project) => project.id === task.projectId)?.name ||
        'the linked project';
      const savedSeconds =
        sessionSeconds > 0
          ? sessionSeconds
          : Math.max(0, getTaskLiveSeconds(task));
      setTimerStatus(
        `Saved ${formatTime(savedSeconds)} on "${task.name}" in ${projectName}. Tracked by ${currentUser.name}.`
      );

      // Only feed-log when this Save closed a running session (hours just written).
      if (sessionSeconds > 0 && closedEntryId) {
        await logSavedHoursActivity({
          projectId: task.projectId,
          taskId: task.id,
          taskName: task.name,
          durationSeconds: sessionSeconds,
          timeEntryId: closedEntryId,
        });
      }
    } catch (error) {
      setDatabaseError(toUserAlert(error, 'Could not save the timer'));
      setTimerStatus('');
    }
  };

  const resetTimer = async (taskId, taskName) => {
    const confirmed = window.confirm(
      `Reset all of your tracked time on "${taskName}"?\n\nThis deletes your time entries for this task and cannot be undone.`
    );
    if (!confirmed) return;

    setDatabaseError('');
    setTimerStatus('');
    const { error } = await supabase
      .from('time_entries')
      .delete()
      .eq('task_id', taskId)
      .eq('user_id', currentUser.id);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not reset the timer'));
      return;
    }

    await loadTasks();
    setTimerStatus(`Timer reset for "${taskName}".`);
    const resetTask = tasks.find((item) => item.id === taskId);
    logActivity(`${currentUser.name} reset time counter for "${taskName}"`, {
      projectId: resetTask?.projectId,
      taskId,
      notify: false,
    });
  };

  const continueTimerFromEntry = async (entry) => {
    setTrackerEntryMenuId(null);
    if (!entry?.taskId) return;
    const task = tasks.find((item) => item.id === entry.taskId);
    if (!task) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }
    if (task.isRunning) return;
    await toggleTimer(task.id, task.name);
  };

  const getClientsForProject = (projectId) => {
    if (!projectId) return [];
    const project = projects.find((item) => item.id === projectId);
    if (!project) return [];
    const fromProject = (project.clients || []).filter((client) => client?.id);
    if (fromProject.length > 0) return fromProject;
    if (project.clientId) {
      const legacy = clients.find(
        (client) => client.id === project.clientId && !client.archivedAt
      );
      if (legacy) return [{ id: legacy.id, name: legacy.name }];
    }
    return [];
  };

  const setTaskClientsForTask = async (taskId, nextClientIds) => {
    const uniqueIds = Array.from(
      new Set((nextClientIds || []).filter(Boolean))
    );
    const { error: deleteError } = await supabase
      .from('task_clients')
      .delete()
      .eq('task_id', taskId);
    if (deleteError) {
      return toUserAlert(deleteError, 'Could not update task client');
    }
    if (uniqueIds.length === 0) return null;
    const { error: insertError } = await supabase.from('task_clients').insert(
      uniqueIds.map((clientId) => ({
        task_id: taskId,
        client_id: clientId,
      }))
    );
    if (insertError) {
      return toUserAlert(insertError, 'Could not update task client');
    }
    return null;
  };

  const handleTrackerEntryClientChange = async (entry, nextClientId) => {
    if (!entry?.taskId) return;
    const projectClients = getClientsForProject(entry.projectId);
    const allowedIds = new Set(projectClients.map((client) => client.id));
    const safeClientId =
      nextClientId && allowedIds.has(nextClientId) ? nextClientId : '';

    setDatabaseError('');
    const error = await setTaskClientsForTask(
      entry.taskId,
      safeClientId ? [safeClientId] : []
    );
    if (error) {
      setDatabaseError(error);
      return;
    }

    await loadTasks();
    const clientName =
      projectClients.find((client) => client.id === safeClientId)?.name ||
      'No client';
    logActivity(
      `${currentUser.name} set client on "${entry.taskName}" to "${clientName}"`,
      { projectId: entry.projectId, taskId: entry.taskId, notify: false }
    );
  };

  const handleTrackerEntryProjectChange = async (entry, nextProjectId) => {
    if (!entry?.taskId || !nextProjectId || nextProjectId === entry.projectId) {
      return;
    }
    const task = tasks.find((item) => item.id === entry.taskId);
    if (!task) return;

    const projectClients = getClientsForProject(nextProjectId);
    const allowedIds = new Set(projectClients.map((client) => client.id));
    const currentClientIds = (task.clients || [])
      .map((client) => client.id)
      .filter(Boolean);
    const retained = currentClientIds.filter((id) => allowedIds.has(id));
    const nextClientIds =
      retained.length > 0
        ? retained
        : projectClients[0]?.id
          ? [projectClients[0].id]
          : [];

    setDatabaseError('');
    const { error: moveError } = await supabase
      .from('tasks')
      .update({ project_id: nextProjectId })
      .eq('id', entry.taskId);
    if (moveError) {
      setDatabaseError(toUserAlert(moveError, 'Could not change project'));
      return;
    }

    const clientError = await setTaskClientsForTask(entry.taskId, nextClientIds);
    if (clientError) {
      setDatabaseError(clientError);
      return;
    }

    await Promise.all([loadTasks(), loadProjects()]);
    const projectName =
      projects.find((item) => item.id === nextProjectId)?.name || 'project';
    logActivity(
      `${currentUser.name} moved "${entry.taskName}" to "${projectName}"`,
      { projectId: nextProjectId, taskId: entry.taskId }
    );
  };

  const deleteTimeEntry = async (entry) => {
    setTrackerEntryMenuId(null);
    if (!entry?.id || entry.isRunning) return;
    const confirmed = window.confirm(
      `Delete this time entry for "${entry.taskName}"?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDatabaseError('');
    const { error } = await supabase
      .from('time_entries')
      .delete()
      .eq('id', entry.id)
      .eq('user_id', currentUser.id);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not delete the time entry'));
      return;
    }

    await loadTasks();
    setTimerStatus(`Deleted time entry for "${entry.taskName}".`);
    logActivity(`${currentUser.name} deleted a time entry on "${entry.taskName}"`, {
      projectId: entry.projectId,
      taskId: entry.taskId,
      notify: false,
    });
  };

  const beginEditTimeEntry = (entry) => {
    if (!entry?.id) return;
    setTrackerEntryMenuId(null);
    setTrackerTagMenuEntryId(null);
    setTrackerClientMenuEntryId(null);
    setTrackerTagSearch('');
    setTrackerEditingEntryId(entry.id);
    setEditTaskName(entry.taskName);
    setEditEntryAdjustInput('');
    setEditEntryDurationInput(
      secondsToDurationHoursInput(
        entry.isRunning
          ? Math.max(
              0,
              Math.floor(
                (Date.now() - new Date(entry.startedAt).getTime()) / 1000
              )
            )
          : entry.durationSeconds
      )
    );
  };

  const persistEntryDurationSeconds = async (entry, nextSeconds, modeLabel) => {
    const currentSeconds = Math.floor(Number(entry.durationSeconds) || 0);
    if (nextSeconds === currentSeconds) {
      setTimerStatus('Duration is already set to that value.');
      return false;
    }

    setEditEntryDurationSaving(true);
    setDatabaseError('');

    const patch = { duration_seconds: nextSeconds };
    // Keep clock-range constraint valid; for deductions collapse end to start.
    if (nextSeconds <= 0) {
      const startedIso =
        entry.startedAt instanceof Date
          ? entry.startedAt.toISOString()
          : new Date(entry.startedAt).toISOString();
      patch.ended_at = startedIso;
    } else if (entry.startedAt) {
      const startedMs = new Date(entry.startedAt).getTime();
      if (!Number.isNaN(startedMs)) {
        patch.ended_at = new Date(startedMs + nextSeconds * 1000).toISOString();
      }
    }

    let query = supabase.from('time_entries').update(patch).eq('id', entry.id);
    if (!isSystemAdmin) {
      query = query.eq('user_id', currentUser.id);
    }
    const { error } = await query;

    if (error) {
      const blockedNegative =
        nextSeconds < 0 &&
        /duration_seconds|check constraint/i.test(error.message || '');
      setDatabaseError(
        blockedNegative
          ? {
              title: 'Database update required',
              message:
                'Negative time adjustments need a migration that allows signed duration_seconds.',
              steps: [
                'Open the Supabase SQL editor.',
                'Run frontend/supabase/migrations/20260723100000_allow_negative_time_entry_duration.sql',
                'Reload and save the duration again.',
              ],
            }
          : toUserAlert(error, 'Could not update the time entry')
      );
      setEditEntryDurationSaving(false);
      return false;
    }

    await loadTasks();
    setEditEntryDurationInput(secondsToDurationHoursInput(nextSeconds));
    setEditEntryAdjustInput('');
    setTimerStatus(
      nextSeconds < 0
        ? `Deducted ${formatTime(Math.abs(nextSeconds))} from "${entry.taskName}".`
        : `Updated logged time on "${entry.taskName}" to ${formatTime(nextSeconds)}.`
    );
    logActivity(
      `${currentUser.name} ${modeLabel} time on "${entry.taskName}" to ${formatTime(nextSeconds)}` +
        (nextSeconds < 0 ? ' (deduction)' : ''),
      {
        projectId: entry.projectId,
        taskId: entry.taskId,
        notify: false,
        details: {
          kind: 'time_adjustment',
          time_entry_id: entry.id,
          previous_duration_seconds: currentSeconds,
          duration_seconds: nextSeconds,
          skipNotify: true,
        },
      }
    );
    setEditEntryDurationSaving(false);
    return true;
  };

  /** Set this entry's logged duration to an absolute value (e.g. 13 hours). */
  const handleSaveEntryDuration = async (entry) => {
    if (!entry?.id || entry.isRunning) {
      setDatabaseError({
        title: 'Stop the timer first',
        message: 'Pause or save the running timer before editing logged duration.',
        steps: [
          'Pause or Save this entry.',
          'Then open Edit and adjust the logged hours.',
        ],
      });
      return;
    }

    const parsed = parseSignedDurationInput(editEntryDurationInput);
    if (parsed.error) {
      setDatabaseError({
        title: 'Invalid duration',
        message: parsed.error,
        steps: [
          'To change 15h → 13h, enter 13 (not -13).',
          'Or use HH:MM:SS like 13:00:00.',
        ],
      });
      return;
    }

    await persistEntryDurationSeconds(entry, parsed.durationSeconds, 'set');
  };

  /** Add/subtract hours from the current logged duration (e.g. -2 on 15h → 13h). */
  const handleAdjustEntryDuration = async (entry) => {
    if (!entry?.id || entry.isRunning) {
      setDatabaseError({
        title: 'Stop the timer first',
        message: 'Pause or save the running timer before adjusting logged duration.',
        steps: [
          'Pause or Save this entry.',
          'Then open Edit and use Adjust by.',
        ],
      });
      return;
    }

    const parsed = parseSignedDurationInput(editEntryAdjustInput);
    if (parsed.error) {
      setDatabaseError({
        title: 'Invalid adjustment',
        message: parsed.error,
        steps: [
          'Enter how many hours to add or subtract, e.g. -2 or 0.5.',
          'Example: 15h entry + (-2) = 13h.',
        ],
      });
      return;
    }

    if (parsed.durationSeconds === 0) {
      setTimerStatus('Enter a non-zero adjustment.');
      return;
    }

    const currentSeconds = Math.floor(Number(entry.durationSeconds) || 0);
    const nextSeconds = currentSeconds + parsed.durationSeconds;
    await persistEntryDurationSeconds(entry, nextSeconds, 'adjusted');
  };

  const favoritesMissingAlert = {
    title: 'Database table missing: task_favorites',
    message:
      'Favorites need the task_favorites table. Run the latest migration in Supabase, then try again.',
    steps: [
      'Open the Supabase SQL editor for this project.',
      'Run frontend/supabase/migrations/20260720192000_task_favorites.sql',
      'Reload the app and try again.',
    ],
  };

  const duplicateTaskFromEntry = async (entry) => {
    setTrackerEntryMenuId(null);
    if (!entry?.taskId || !entry?.projectId) return;

    const sourceTask = tasks.find((item) => item.id === entry.taskId);
    if (!sourceTask) {
      setDatabaseError(USER_ALERTS.noTasksAvailable);
      return;
    }

    setDatabaseError('');
    setTimerStatus('Duplicating task…');

    const membershipError = await ensureCurrentUserProjectMember(entry.projectId);
    if (membershipError) {
      setDatabaseError(membershipError);
      setTimerStatus('');
      return;
    }

    const copyName = `${sourceTask.name} (copy)`;
    const { data: created, error: taskError } = await supabase
      .from('tasks')
      .insert({
        project_id: entry.projectId,
        name: copyName,
        created_by: currentUser.id,
      })
      .select('id')
      .single();

    if (taskError) {
      setDatabaseError(toUserAlert(taskError, 'Could not duplicate the task'));
      setTimerStatus('');
      return;
    }

    const { error: assigneeError } = await supabase
      .from('task_assignees')
      .upsert(
        { task_id: created.id, user_id: currentUser.id },
        { onConflict: 'task_id,user_id', ignoreDuplicates: true }
      );

    if (assigneeError) {
      setDatabaseError(
        toUserAlert(assigneeError, 'Task duplicated, but could not assign you')
      );
    }

    const clientIds = (sourceTask.clients || []).map((client) => client.id);
    if (clientIds.length > 0) {
      await supabase.from('task_clients').insert(
        clientIds.map((clientId) => ({
          task_id: created.id,
          client_id: clientId,
        }))
      );
    }

    const tagIds = (sourceTask.tags || [])
      .map((tag) => tag.id)
      .filter((id) => !String(id).startsWith('local-'));
    if (tagIds.length > 0) {
      await supabase.from('task_tags').insert(
        tagIds.map((tagId) => ({
          task_id: created.id,
          tag_id: tagId,
        }))
      );
    }

    await Promise.all([loadProjects(), loadTasks()]);
    setTimerStatus(`Duplicated task as "${copyName}".`);
    logActivity(
      `${currentUser.name} duplicated task "${sourceTask.name}" as "${copyName}"`,
      { projectId: sourceTask.projectId, taskId: created.id }
    );
  };

  const toggleTaskFavoriteFromEntry = async (entry) => {
    setTrackerEntryMenuId(null);
    if (!entry?.taskId) return;

    setDatabaseError('');
    const isFavorite = Boolean(entry.isFavorite);
    const query = isFavorite
      ? supabase
          .from('task_favorites')
          .delete()
          .eq('task_id', entry.taskId)
          .eq('user_id', currentUser.id)
      : supabase.from('task_favorites').insert({
          task_id: entry.taskId,
          user_id: currentUser.id,
        });

    const { error } = await query;
    if (error) {
      const missingTable = /task_favorites|schema cache|does not exist/i.test(
        error.message || ''
      );
      setDatabaseError(
        missingTable
          ? favoritesMissingAlert
          : toUserAlert(
              error,
              isFavorite
                ? 'Could not remove favorite'
                : 'Could not add as favorite'
            )
      );
      return;
    }

    setTasks((prev) =>
      prev.map((task) =>
        task.id === entry.taskId
          ? { ...task, isFavorite: !isFavorite }
          : task
      )
    );
    setTrackerEntries((prev) =>
      prev.map((item) =>
        item.taskId === entry.taskId
          ? { ...item, isFavorite: !isFavorite }
          : item
      )
    );

    setTimerStatus(
      isFavorite
        ? `Removed "${entry.taskName}" from favorites.`
        : `Added "${entry.taskName}" as a favorite for this project.`
    );
    logActivity(
      isFavorite
        ? `${currentUser.name} removed favorite "${entry.taskName}"`
        : `${currentUser.name} favorited "${entry.taskName}"`,
      { projectId: entry.projectId, taskId: entry.taskId, notify: false }
    );
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const currentProject = projects.find((p) => p.id === activeProjectId);
  const canManageActiveProject = canManageProject(activeProjectId);
  const filteredTasks = tasks.filter((t) => t.projectId === activeProjectId);
  const listGridTemplate = [
    'minmax(0, 1.15fr)', // Task title
    'minmax(7.25rem, 0.7fr)', // Task status (default column)
    'minmax(3.25rem, 0.5fr)', // Assignee
    'minmax(3.5rem, 0.5fr)', // Hours
    showDetailedColumns ? 'minmax(3rem, 0.4fr)' : null, // Estimated time
    showDetailedColumns ? 'minmax(5.25rem, 0.55fr)' : null, // Start date
    showDetailedColumns ? 'minmax(5.25rem, 0.55fr)' : null, // Duration date
    listColumns.tags ? 'minmax(0, 0.75fr)' : null, // Service tags
    listColumns.followers ? 'minmax(0, 0.65fr)' : null, // Followers
    listColumns.totalTrackedHours ? 'minmax(4.5rem, 0.55fr)' : null, // Total tracked
    '2rem', // Actions
  ]
    .filter(Boolean)
    .join(' ');

  const closeListMenus = () => {
    setListRowMenuId(null);
    setListColumnPickerOpen(false);
    setTaggingTaskId(null);
    setTaskServiceMenuOpen(false);
    setTaggingSubtaskKey(null);
    setCreateTaskTagMenuOpen(false);
  };

  useEffect(() => {
    if (!listColumnPickerOpen) return undefined;

    const handlePointerDown = (event) => {
      const root = listColumnPickerRef.current;
      if (root && !root.contains(event.target)) {
        setListColumnPickerOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setListColumnPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [listColumnPickerOpen]);

  const openProject = (projectId) => {
    setDatabaseError('');
    setActiveMenu('projects');
    setActiveProjectId(projectId);
    setInviteMemberOpen(false);
    setProjectStatusMenuOpen(false);
    setNewProjectStatusName('');
    setNewTaskStatusName('');
    setNewMemberName('');
    setNewMemberRole('Employee');
    setListRowMenuId(null);
    setListColumnPickerOpen(false);
    setExpandedTaskIds(new Set());
    setAssigningTaskId(null);
    setFollowingTaskId(null);
    setTaggingTaskId(null);
    setTaggingSubtaskKey(null);
    setLoggingTimeTaskId(null);
    setSidebarOpen(false);
  };

  const goToOverview = () => {
    setDatabaseError('');
    setActiveMenu('projects');
    setActiveProjectId(null);
    setInviteMemberOpen(false);
    setProjectStatusMenuOpen(false);
    setSidebarOpen(false);
  };

  const openMenu = (menu) => {
    setDatabaseError('');
    setTimerStatus('');
    setActiveMenu(menu);
    if (menu !== 'projects') setActiveProjectId(null);
    setSidebarOpen(false);
  };

  const toggleClientSelection = (memberId) => {
    setSelectedClientIds((selected) =>
      selected.includes(memberId)
        ? selected.filter((id) => id !== memberId)
        : [...selected, memberId]
    );
  };

  const explainEdgeFunctionError = (error) => {
    const msg = normalizeErrorText(error);
    if (
      /Failed to send a request to the Edge Function|FunctionsFetchError|Function not found|404|CORS|Failed to fetch/i.test(
        msg
      )
    ) {
      return {
        title: 'Edge Function not available',
        message:
          'Inviting or deleting portal users requires the invite-portal-user Edge Function in Supabase. It is not reachable right now.',
        steps: [
          'Open Supabase Dashboard → Edge Functions.',
          'Deploy the function from frontend/supabase/functions/invite-portal-user (or run: npx supabase functions deploy invite-portal-user).',
          'Confirm your app .env points at this Supabase project URL.',
          'Reload the Team page and try again.',
        ],
      };
    }
    return null;
  };

  const readFunctionsErrorBody = async (error) => {
    const context = error?.context;
    if (!context) return '';
    try {
      if (typeof context.json === 'function') {
        const body = await context.json();
        if (typeof body?.error === 'string' && body.error.trim()) {
          return body.error.trim();
        }
        if (typeof body?.message === 'string' && body.message.trim()) {
          return body.message.trim();
        }
      }
    } catch {
      // ignore parse failures
    }
    try {
      if (typeof context.text === 'function') {
        const text = String(await context.text() || '').trim();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            if (typeof parsed?.error === 'string') return parsed.error;
          } catch {
            return text;
          }
        }
      }
    } catch {
      // ignore
    }
    return '';
  };

  const invokePortalUserFunction = async (body) => {
    const { data, error } = await supabase.functions.invoke('invite-portal-user', {
      body,
    });

    if (data?.error) {
      throw new Error(String(data.error));
    }

    if (error) {
      const explained = explainEdgeFunctionError(error);
      if (explained) {
        const err = new Error(explained.message);
        err.userAlert = explained;
        throw err;
      }

      const bodyMessage = await readFunctionsErrorBody(error);
      const fallback = normalizeErrorText(error);
      const message =
        bodyMessage ||
        (fallback && fallback !== 'Edge Function returned a non-2xx status code'
          ? fallback
          : 'The Team Edge Function failed. Redeploy invite-portal-user and check Supabase Function logs.');

      const err = new Error(message);
      err.userAlert = {
        title: 'Could not complete Team action',
        message,
        steps: [
          'Confirm you are a system Admin in Team.',
          'Redeploy: npx supabase functions deploy invite-portal-user',
          'Open Supabase → Edge Functions → invite-portal-user → Logs for details.',
          'Try the action again.',
        ],
      };
      throw err;
    }

    return data;
  };

  const invitePortalUser = async ({ email, fullName, role }) => {
    if (currentUser.role !== 'admin') {
      throw new Error('Only administrators can invite users.');
    }

    await invokePortalUserFunction({
      action: 'invite',
      email,
      fullName,
      role,
    });
    await loadProjects();
  };

  const handleUpdateTeamMember = async (memberId) => {
    if (currentUser.role !== 'admin' || !memberId) return;
    const fullName = editTeamMemberName.trim();
    const role = editTeamMemberRole;
    if (!fullName) {
      setDatabaseError({
        title: 'Name required',
        message: 'Enter a display name before saving.',
        steps: ['Type the colleague’s name.', 'Click Save.'],
      });
      return;
    }

    const target = teamMembers.find((member) => member.id === memberId);
    if (target && isTeamAdminOwner(target) && !isTeamAdminOwner(currentUser)) {
      setDatabaseError({
        title: 'Protected Admin Owner',
        message:
          'Team admins cannot edit or modify the Admin Owner account. You can still configure other admins and employees.',
        steps: [
          'Choose a different teammate to edit.',
          'Ask Vanessa Lim if the Admin Owner profile needs a change.',
        ],
      });
      setEditingTeamMemberId(null);
      return;
    }

    setTeamSaving(true);
    setInviteStatus('');
    setDatabaseError('');
    try {
      // Prefer direct profile update (works once admin RLS migration is applied).
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName, role })
        .eq('id', memberId);

      if (error) {
        // Fallback to Edge Function if RLS blocks the update.
        await invokePortalUserFunction({
          action: 'update',
          userId: memberId,
          fullName,
          role,
        });
      }

      setTeamMembers((prev) =>
        prev.map((member) =>
          member.id === memberId
            ? { ...member, name: fullName, role }
            : member
        )
      );
      setEditingTeamMemberId(null);
      setInviteStatus(`Updated ${fullName}.`);
      await loadProjects();
    } catch (error) {
      if (error?.userAlert) {
        setDatabaseError(error.userAlert);
      } else {
        setDatabaseError(toUserAlert(error, 'Could not update team member'));
      }
    } finally {
      setTeamSaving(false);
    }
  };

  const handleDeleteTeamMember = async (member) => {
    if (currentUser.role !== 'admin' || !member?.id) return;
    if (member.id === currentUser.id) {
      setDatabaseError({
        title: 'Cannot delete yourself',
        message: 'Ask another admin to remove your account if needed.',
        steps: ['Select a different teammate to delete.'],
      });
      return;
    }

    if (isTeamAdminOwner(member) && !isTeamAdminOwner(currentUser)) {
      setDatabaseError({
        title: 'Protected Admin Owner',
        message:
          'Vanessa Lim is the Team Admin Owner and cannot be removed by other admins.',
        steps: [
          'You can still delete other employees and admins.',
          'Only the Admin Owner can remove accounts if ownership ever needs to change.',
        ],
      });
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete "${member.name}" (${member.email})?\n\nThis removes their login and profile. It cannot be undone.`
    );
    if (!confirmed) return;

    setTeamSaving(true);
    setInviteStatus('');
    setDatabaseError('');
    try {
      await invokePortalUserFunction({
        action: 'delete',
        userId: member.id,
      });
      setTeamMembers((prev) => prev.filter((item) => item.id !== member.id));
      if (editingTeamMemberId === member.id) setEditingTeamMemberId(null);
      setInviteStatus(`Deleted ${member.name}.`);
      await loadProjects();
    } catch (error) {
      if (error?.userAlert) {
        setDatabaseError(error.userAlert);
      } else {
        setDatabaseError(toUserAlert(error, 'Could not delete team member'));
      }
    } finally {
      setTeamSaving(false);
    }
  };

  const handleCreateClient = async (event) => {
    event.preventDefault();
    const name = newClientName.trim();
    if (!name || currentUser.role !== 'admin') return;

    setClientInviteStatus('Saving…');
    setDatabaseError('');
    const { error } = await supabase.from('clients').insert({
      name,
      created_by: currentUser.id,
    });

    if (error) {
      setClientInviteStatus(error.message || 'Could not add client.');
      return;
    }

    setClientInviteStatus(`Client "${name}" added.`);
    setNewClientName('');
    await loadClients();
  };

  const createClientFromTracker = async () => {
    if (!isSystemAdmin) {
      setDatabaseError({
        title: 'Admin required',
        message: 'Only system admins can create clients.',
        steps: ['Ask an admin to add the client, then select it here.'],
      });
      setQuickCreatingClient(false);
      return;
    }
    const name = quickNewClientName.trim();
    if (!name) {
      setDatabaseError({
        title: 'Client name required',
        message: 'Enter a name for the new client.',
        steps: ['Type the client name.', 'Click Create client.'],
      });
      return;
    }

    setQuickCreateSaving(true);
    setDatabaseError('');
    const existing = activeClients.find(
      (client) => client.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setQuickTimerClientId(existing.id);
      if (quickTimerProjectId) {
        const alreadyLinked = getClientsForProject(quickTimerProjectId).some(
          (client) => client.id === existing.id
        );
        if (!alreadyLinked) {
          const { error: linkError } = await supabase
            .from('project_clients')
            .upsert(
              {
                project_id: quickTimerProjectId,
                client_id: existing.id,
              },
              { onConflict: 'project_id,client_id', ignoreDuplicates: true }
            );
          if (
            !linkError ||
            /project_clients|schema cache|does not exist/i.test(
              linkError.message || ''
            )
          ) {
            if (!linkError) await loadProjects();
          }
        }
      }
      setQuickCreatingClient(false);
      setQuickNewClientName('');
      setQuickCreateSaving(false);
      setTimerStatus(`Selected existing client "${existing.name}".`);
      return;
    }

    const { data, error } = await supabase
      .from('clients')
      .insert({ name, created_by: currentUser.id })
      .select('id, name')
      .single();

    setQuickCreateSaving(false);
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not create client'));
      return;
    }

    await loadClients();
    setQuickTimerClientId(data.id);
    if (quickTimerProjectId) {
      const { error: linkError } = await supabase.from('project_clients').upsert(
        {
          project_id: quickTimerProjectId,
          client_id: data.id,
        },
        { onConflict: 'project_id,client_id', ignoreDuplicates: true }
      );
      if (
        linkError &&
        !/project_clients|schema cache|does not exist/i.test(linkError.message || '')
      ) {
        setDatabaseError(toUserAlert(linkError, 'Client created, but could not link to project'));
      } else if (!linkError) {
        await loadProjects();
      } else {
        // Legacy fallback: set projects.client_id when multi-client table is missing.
        await supabase
          .from('projects')
          .update({ client_id: data.id })
          .eq('id', quickTimerProjectId);
        await loadProjects();
      }
    } else {
      setQuickTimerProjectId('');
      setQuickTimerTaskId('');
    }
    setQuickCreatingClient(false);
    setQuickNewClientName('');
    setTimerStatus(`Client "${data.name}" created.`);
  };

  const createProjectFromTracker = async () => {
    if (!canCreateProjects) {
      setDatabaseError({
        title: 'Cannot create projects',
        message: 'Your account type cannot create projects.',
        steps: ['Ask a Team admin if you need a project created for you.'],
      });
      setQuickCreatingProject(false);
      return;
    }
    const name = quickNewProjectName.trim();
    if (!name) {
      setDatabaseError({
        title: 'Project name required',
        message: 'Enter a name for the new project.',
        steps: ['Type the project name.', 'Click Create project.'],
      });
      return;
    }
    if (!quickTimerClientId) {
      setDatabaseError({
        title: 'Select a client first',
        message: 'New projects need a client. Pick or create a client, then add the project.',
        steps: [
          'Choose a client' + (isSystemAdmin ? ' (or use + Client)' : '') + '.',
          'Then use + Add New Project again.',
        ],
      });
      return;
    }

    setQuickCreateSaving(true);
    setDatabaseError('');
    const { data, error } = await supabase
      .from('projects')
      .insert({
        name,
        description: 'No description provided.',
        created_by: currentUser.id,
        client_id: quickTimerClientId,
      })
      .select('id, name')
      .single();

    setQuickCreateSaving(false);
    if (error) {
      setDatabaseError(
        toUserAlert(
          error,
          'Could not create project. If this mentions policy/permission, run 20260720210000_authenticated_users_can_create_projects.sql in Supabase.'
        )
      );
      return;
    }

    await loadProjects();
    setQuickTimerProjectId(data.id);
    setQuickTimerTaskId('');
    setQuickCreatingProject(false);
    setQuickNewProjectName('');
    setTimerStatus(
      `Project "${data.name}" created. You are Project Admin for this project.`
    );
  };

  const beginRenameClient = (client) => {
    if (currentUser.role !== 'admin') return;
    setEditingClientId(client.id);
    setEditClientName(client.name);
    setClientInviteStatus('');
    clearUserAlert();
  };

  const handleRenameClient = async (clientId) => {
    if (currentUser.role !== 'admin') return;
    const name = editClientName.trim();
    if (!name) {
      setDatabaseError({
        title: 'Client name required',
        message: 'Enter a new client name before saving.',
        steps: ['Type the updated client name.', 'Click Save.'],
      });
      return;
    }

    setDatabaseError('');
    const { error } = await supabase
      .from('clients')
      .update({ name })
      .eq('id', clientId);

    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not rename client'));
      return;
    }

    setEditingClientId(null);
    setEditClientName('');
    setClientInviteStatus(`Client renamed to "${name}".`);
    await Promise.all([loadClients(), loadTasks()]);
  };

  const handleArchiveClient = async (clientId, archive = true) => {
    if (currentUser.role !== 'admin') return;

    setDatabaseError('');
    const { error } = await supabase
      .from('clients')
      .update({ archived_at: archive ? new Date().toISOString() : null })
      .eq('id', clientId);

    if (error) {
      setDatabaseError(
        toUserAlert(
          error,
          archive ? 'Could not archive client' : 'Could not restore client'
        )
      );
      return;
    }

    if (archive) {
      setSelectedClientIds((selected) => selected.filter((id) => id !== clientId));
      setSelectedTaskClientIds((selected) => selected.filter((id) => id !== clientId));
      if (quickTimerClientId === clientId) setQuickTimerClientId('');
      if (editingClientId === clientId) {
        setEditingClientId(null);
        setEditClientName('');
      }
    }

    setClientInviteStatus(
      archive ? 'Client archived. It is hidden from new task assignment.' : 'Client restored.'
    );
    await loadClients();
  };

  const handleDeleteClient = async (clientId) => {
    if (currentUser.role !== 'admin') return;

    const client = clients.find((item) => item.id === clientId);
    const confirmed = window.confirm(
      `Permanently delete client "${client?.name || 'this client'}"?\n\nThis removes the label from tasks. Prefer Archive if you may need it later.`
    );
    if (!confirmed) return;

    setDatabaseError('');
    const { error } = await supabase.from('clients').delete().eq('id', clientId);
    if (error) {
      setDatabaseError(toUserAlert(error, 'Could not delete client'));
      return;
    }

    setSelectedClientIds((selected) => selected.filter((id) => id !== clientId));
    setSelectedTaskClientIds((selected) => selected.filter((id) => id !== clientId));
    if (quickTimerClientId === clientId) setQuickTimerClientId('');
    if (editingClientId === clientId) {
      setEditingClientId(null);
      setEditClientName('');
    }
    setClientInviteStatus('Client permanently deleted.');
    await loadClients();
    await loadTasks();
  };

  const handleInviteColleague = async (event) => {
    event.preventDefault();
    const email = inviteEmail.trim();
    const fullName = inviteName.trim();
    if (!email || !fullName) return;

    setInviteStatus('Sending invitation…');
    setDatabaseError('');
    try {
      await invitePortalUser({
        email,
        fullName,
        role: inviteRole === 'admin' ? 'admin' : 'employee',
      });
      setInviteStatus(`Invitation sent to ${email}.`);
      setInviteName('');
      setInviteEmail('');
      setInviteRole('employee');
    } catch (error) {
      if (error?.userAlert) {
        setDatabaseError(error.userAlert);
        setInviteStatus(error.userAlert.message);
      } else {
        setInviteStatus(error.message || 'Could not invite employee.');
        setDatabaseError(toUserAlert(error, 'Could not invite colleague'));
      }
    }
  };

  const activeTimerTask = tasks.find((task) => task.isRunning);
  const activeSessionSeconds = activeTimerTask?.activeStartedAt
    ? Math.max(
        0,
        Math.floor(
          (clockNow - new Date(activeTimerTask.activeStartedAt).getTime()) / 1000
        )
      )
    : 0;
  const liveWeekTrackedSeconds = computeTrackedInRange(
    trackerEntries.map((entry) => ({
      started_at: entry.startedAt,
      ended_at: entry.endedAt,
    })),
    getStartOfWeek(new Date(clockNow), weekStartsMonday),
    new Date(clockNow)
  );
  // Prefer live wall-clock week total; fall back to last loaded seed if entries empty.
  const weekSecondsDisplay =
    trackerEntries.length > 0 ? liveWeekTrackedSeconds : weekTrackedSeconds;
  const activeClients = clients.filter((client) => !client.archivedAt);
  const visibleClients = showArchivedClients ? clients : activeClients;
  // Project comes first in the Quick Timer bar; show all projects.
  const quickTimerProjects = projects;
  const quickProjectTasks = quickTimerProjectId
    ? tasks.filter((task) => task.projectId === quickTimerProjectId)
    : [];
  const quickTimerProjectClients = getClientsForProject(quickTimerProjectId);
  const resolveClientLabel = (taskLike, projectId) => {
    const fromTask = (taskLike?.clients || [])
      .map((client) => client.name)
      .filter(Boolean);
    if (fromTask.length > 0) return fromTask.join(', ');
    const project = projects.find(
      (item) => item.id === (projectId || taskLike?.projectId)
    );
    const fromProject = (project?.clients || [])
      .map((client) => client.name)
      .filter(Boolean);
    if (fromProject.length > 0) return fromProject.join(', ');
    return 'No client';
  };
  const manualRangePreview = parseManualTimeRange(
    manualLogDate,
    manualLogStart,
    manualLogEnd
  );
  const manualPreviewSeconds = manualRangePreview.durationSeconds || 0;
  const trackerDayGroups = (() => {
    const groups = new Map();
    trackerEntries.forEach((entry) => {
      const project = projects.find((item) => item.id === entry.projectId);
      const dayKey = getStartOfDay(entry.startedAt).toISOString();
      const durationSeconds = entry.isRunning
        ? Math.max(
            0,
            Math.floor((clockNow - entry.startedAt.getTime()) / 1000)
          )
        : entry.durationSeconds;
      const enriched = {
        ...entry,
        projectName: project?.name || 'Project',
        durationSeconds,
        tags: tasks.find((task) => task.id === entry.taskId)?.tags || [],
      };
      const existing = groups.get(dayKey) || {
        key: dayKey,
        date: getStartOfDay(entry.startedAt),
        totalSeconds: 0,
        entries: [],
      };
      existing.totalSeconds += durationSeconds;
      existing.entries.push(enriched);
      groups.set(dayKey, existing);
    });
    return Array.from(groups.values()).sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  })();
  const quickTaskOptions = tasks.map((task) => {
    const project = projects.find((item) => item.id === task.projectId);
    const projectName = project?.name || 'Project';
    const clientLabel = resolveClientLabel(task, task.projectId);
    return {
      ...task,
      projectName,
      clientLabel,
      menuLabel: `${task.name} • ${projectName} (Client: ${clientLabel})`,
    };
  });

  const reportBounds = getReportRangeBounds(
    reportRangePreset,
    reportCustomFrom,
    reportCustomTo,
    weekStartsMonday
  );

  const filteredReportRows = reportRows.filter((row) => {
    const overlaps =
      row.startedAt.getTime() <= reportBounds.to.getTime() &&
      (row.endedAt || reportBounds.to).getTime() >= reportBounds.from.getTime();
    if (!overlaps) return false;
    if (reportProjectId && row.projectId !== reportProjectId) return false;
    if (reportMemberId && row.userId !== reportMemberId) return false;
    if (
      reportClientId &&
      !(row.clients || []).some((client) => client.id === reportClientId)
    ) {
      return false;
    }
    if (
      reportTagId &&
      !(row.tags || []).some((tag) => tag.id === reportTagId)
    ) {
      return false;
    }
    // Attribute duration to overlap within range for fair filtering
    return true;
  }).map((row) => {
    const overlapStart = Math.max(
      row.startedAt.getTime(),
      reportBounds.from.getTime()
    );
    const overlapEnd = Math.min(
      (row.endedAt || new Date()).getTime(),
      reportBounds.to.getTime()
    );
    const secondsInRange = Math.max(
      0,
      Math.floor((overlapEnd - overlapStart) / 1000)
    );
    return { ...row, secondsInRange };
  }).filter((row) => row.secondsInRange > 0);

  const reportTotalSeconds = filteredReportRows.reduce(
    (total, row) => total + row.secondsInRange,
    0
  );

  const aggregateByKey = (keyFn, nameFn) => {
    const map = new Map();
    filteredReportRows.forEach((row) => {
      const key = keyFn(row);
      const existing = map.get(key) || {
        id: key,
        name: nameFn(row),
        seconds: 0,
        latestAt: null,
        latestTask: '',
        latestProject: '',
      };
      existing.seconds += row.secondsInRange;
      if (!existing.latestAt || row.startedAt > existing.latestAt) {
        existing.latestAt = row.startedAt;
        existing.latestTask = row.taskName;
        existing.latestProject = row.projectName;
      }
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
  };

  const reportByProject = aggregateByKey(
    (row) => row.projectId || 'none',
    (row) => row.projectName
  );
  const reportByClient = (() => {
    const map = new Map();
    filteredReportRows.forEach((row) => {
      const list =
        row.clients.length > 0
          ? row.clients
          : [{ id: 'none', name: 'No client' }];
      list.forEach((client) => {
        const existing = map.get(client.id) || {
          id: client.id,
          name: client.name,
          seconds: 0,
        };
        existing.seconds += row.secondsInRange;
        map.set(client.id, existing);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
  })();
  const reportTagOptions = (() => {
    const map = new Map();
    reportRows.forEach((row) => {
      (row.tags || []).forEach((tag) => {
        if (!map.has(tag.id)) map.set(tag.id, tag.name);
      });
    });
    tags.forEach((tag) => {
      if (!String(tag.id).startsWith('local-') && !map.has(tag.id)) {
        map.set(tag.id, tag.name);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();
  const reportByMember = aggregateByKey(
    (row) => row.userId,
    (row) => row.userName
  );

  // Hours by service tag (task tags like Static Posts) — with project/client context.
  const reportByServiceTag = (() => {
    const map = new Map();
    const bumpNamed = (namedMap, id, name) => {
      const existing = namedMap.get(id) || { id, name, seconds: 0 };
      namedMap.set(id, existing);
      return existing;
    };

    filteredReportRows.forEach((row) => {
      const tagList =
        (row.tags || []).length > 0
          ? row.tags
          : [{ id: 'none', name: 'Untagged' }];
      tagList.forEach((tag) => {
        const existing = map.get(tag.id) || {
          id: tag.id,
          name: tag.name,
          seconds: 0,
          projects: new Map(),
          clients: new Map(),
          latestTask: '',
          latestProject: '',
          latestAt: null,
        };
        existing.seconds += row.secondsInRange;

        const projectEntry = bumpNamed(
          existing.projects,
          row.projectId || 'none',
          row.projectName || 'Without project'
        );
        projectEntry.seconds += row.secondsInRange;

        const clientList =
          (row.clients || []).length > 0
            ? row.clients
            : [{ id: 'none', name: 'No client' }];
        clientList.forEach((client) => {
          const clientEntry = bumpNamed(
            existing.clients,
            client.id,
            client.name
          );
          clientEntry.seconds += row.secondsInRange;
        });

        if (!existing.latestAt || row.startedAt > existing.latestAt) {
          existing.latestAt = row.startedAt;
          existing.latestTask = row.taskName;
          existing.latestProject = row.projectName;
        }
        map.set(tag.id, existing);
      });
    });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        projects: Array.from(item.projects.values()).sort(
          (a, b) => b.seconds - a.seconds
        ),
        clients: Array.from(item.clients.values()).sort(
          (a, b) => b.seconds - a.seconds
        ),
      }))
      .sort((a, b) => b.seconds - a.seconds);
  })();

  // Client × service tag (or project when no tags) — for CEO/admin spend tracking.
  const reportByClientService = (() => {
    const map = new Map();
    filteredReportRows.forEach((row) => {
      const clients =
        (row.clients || []).length > 0
          ? row.clients
          : [{ id: 'none', name: 'No client' }];
      const services =
        (row.tags || []).length > 0
          ? row.tags.map((tag) => ({
              kind: 'tag',
              id: tag.id,
              name: tag.name,
            }))
          : [
              {
                kind: 'project',
                id: row.projectId || 'none',
                name: row.projectName || 'Without project',
              },
            ];

      clients.forEach((client) => {
        services.forEach((service) => {
          const key = `${client.id}::${service.kind}:${service.id}`;
          const existing = map.get(key) || {
            id: key,
            clientId: client.id,
            clientName: client.name,
            serviceKind: service.kind,
            serviceId: service.id,
            serviceName: service.name,
            projectName: row.projectName,
            seconds: 0,
            latestTask: '',
            latestProject: '',
            latestAt: null,
          };
          existing.seconds += row.secondsInRange;
          if (!existing.latestAt || row.startedAt > existing.latestAt) {
            existing.latestAt = row.startedAt;
            existing.latestTask = row.taskName;
            existing.latestProject = row.projectName;
          }
          map.set(key, existing);
        });
      });
    });
    return Array.from(map.values()).sort((a, b) => b.seconds - a.seconds);
  })();

  const reportByDay = (() => {
    const days = [];
    const cursor = getStartOfDay(reportBounds.from);
    const endDay = getStartOfDay(reportBounds.to);
    while (cursor.getTime() <= endDay.getTime()) {
      days.push({
        key: toLocalDateKey(cursor),
        label: cursor.toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }),
        seconds: 0,
        projects: new Map(),
        clients: new Map(),
        tags: new Map(),
      });
      cursor.setDate(cursor.getDate() + 1);
      if (days.length > 62) break; // safety for very wide custom ranges
    }
    const byKey = new Map(days.map((day) => [day.key, day]));
    const bump = (map, id, name, seconds) => {
      const existing = map.get(id) || { id, name, seconds: 0 };
      existing.seconds += seconds;
      map.set(id, existing);
    };

    filteredReportRows.forEach((row) => {
      const dayKey = toLocalDateKey(getStartOfDay(row.startedAt));
      const bucket = byKey.get(dayKey);
      if (!bucket) return;
      bucket.seconds += row.secondsInRange;
      bump(
        bucket.projects,
        row.projectId || 'none',
        row.projectName || 'Without project',
        row.secondsInRange
      );
      const clientList =
        (row.clients || []).length > 0
          ? row.clients
          : [{ id: 'none', name: 'No client' }];
      clientList.forEach((client) =>
        bump(bucket.clients, client.id, client.name, row.secondsInRange)
      );
      const tagList =
        (row.tags || []).length > 0
          ? row.tags
          : [{ id: 'none', name: 'Untagged' }];
      tagList.forEach((tag) =>
        bump(bucket.tags, tag.id, tag.name, row.secondsInRange)
      );
    });

    return days.map((day) => ({
      ...day,
      projects: Array.from(day.projects.values()).sort(
        (a, b) => b.seconds - a.seconds
      ),
      clients: Array.from(day.clients.values()).sort(
        (a, b) => b.seconds - a.seconds
      ),
      tags: Array.from(day.tags.values()).sort((a, b) => b.seconds - a.seconds),
    }));
  })();

  const maxDaySeconds = Math.max(
    1,
    ...reportByDay.map((day) => day.seconds)
  );
  const topProject = reportByProject[0] || null;
  const topClient = reportByClient[0] || null;

  const exportReportCsv = () => {
    const header = [
      'Member',
      'Task',
      'Project',
      'Clients',
      'Service Tags',
      'Started At',
      'Ended At',
      'Duration (HH:MM:SS)',
      'Duration Seconds',
    ];
    const lines = [
      header.join(','),
      ...filteredReportRows.map((row) =>
        [
          escapeCsvValue(row.userName),
          escapeCsvValue(row.taskName),
          escapeCsvValue(row.projectName),
          escapeCsvValue(
            (row.clients || []).map((c) => c.name).join('; ') || 'No client'
          ),
          escapeCsvValue(
            (row.tags || []).map((t) => t.name).join('; ') || 'Untagged'
          ),
          escapeCsvValue(row.startedAt.toISOString()),
          escapeCsvValue(row.endedAt ? row.endedAt.toISOString() : 'Running'),
          escapeCsvValue(formatTime(row.secondsInRange)),
          escapeCsvValue(row.secondsInRange),
        ].join(',')
      ),
    ];
    downloadTextFile(
      `tikix-report-${reportBounds.from.toISOString().slice(0, 10)}.csv`,
      lines.join('\n'),
      'text/csv;charset=utf-8'
    );
  };

  const exportReportPdf = () => {
    const rowsHtml = filteredReportRows
      .map(
        (row) => `
      <tr>
        <td>${row.userName}</td>
        <td>${row.taskName}</td>
        <td>${row.projectName}</td>
        <td>${(row.clients || []).map((c) => c.name).join(', ') || 'No client'}</td>
        <td>${(row.tags || []).map((t) => t.name).join(', ') || 'Untagged'}</td>
        <td>${formatTime(row.secondsInRange)}</td>
      </tr>`
      )
      .join('');

    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Tikix Time Report</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
    h1 { margin: 0 0 8px; }
    p { margin: 4px 0; color: #444; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; text-align: left; }
    th { background: #f3e8ff; }
  </style>
</head>
<body>
  <h1>Tikix Time Report</h1>
  <p>Range: ${reportBounds.from.toLocaleString()} – ${reportBounds.to.toLocaleString()}</p>
  <p>Total time: ${formatTime(reportTotalSeconds)}</p>
  <p>Top project: ${topProject ? `${topProject.name} (${formatTime(topProject.seconds)})` : '—'}</p>
  <p>Top client: ${topClient ? `${topClient.name} (${formatTime(topClient.seconds)})` : '—'}</p>
  <table>
    <thead>
      <tr>
        <th>Member</th>
        <th>Task</th>
        <th>Project</th>
        <th>Clients</th>
        <th>Service Tags</th>
        <th>Duration</th>
      </tr>
    </thead>
    <tbody>${rowsHtml || '<tr><td colspan="6">No time entries in this range.</td></tr>'}</tbody>
  </table>
  <script>window.onload = function () { window.print(); };</script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setDatabaseError({
        title: 'Pop-up blocked',
        message: 'Allow pop-ups for this site to download the PDF report.',
        steps: [
          'Enable pop-ups for localhost in your browser.',
          'Click Download PDF again.',
          'In the print dialog, choose “Save as PDF”.',
        ],
      });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const navItemClass = (active) =>
    `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors ${
      active
        ? 'bg-purple-600 text-white shadow-sm'
        : 'text-gray-600 hover:bg-purple-50 hover:text-purple-800'
    }`;

  const dashboardScreen =
    currentUser.role !== 'admin' ? (
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-5 text-sm font-medium">
        Dashboard reporting is available to administrators only.
      </div>
    ) : (
      <div className="space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-purple-600">
              Analyze
            </p>
            <h1 className="text-2xl font-black text-gray-900 mt-1">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              See hours by client, project, and service tags assigned on tasks
              (for example Static Posts, Carousel Posts).
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setDashboardViewMode('cards')}
                className={`px-3 py-2 rounded-[10px] text-xs font-bold transition-colors ${
                  dashboardViewMode === 'cards'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setDashboardViewMode('list')}
                className={`px-3 py-2 rounded-[10px] text-xs font-bold transition-colors ${
                  dashboardViewMode === 'list'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                List
              </button>
            </div>
            <button
              type="button"
              onClick={loadAdminReport}
              className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={exportReportCsv}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-purple-600 text-white hover:bg-purple-700"
            >
              Download CSV
            </button>
            <button
              type="button"
              onClick={exportReportPdf}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Download PDF
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
            <select
              value={reportRangePreset}
              onChange={(e) => setReportRangePreset(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="custom">Custom dates</option>
            </select>
            <select
              value={reportProjectId}
              onChange={(e) => setReportProjectId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <select
              value={reportClientId}
              onChange={(e) => setReportClientId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All clients</option>
              {activeClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <select
              value={reportTagId}
              onChange={(e) => setReportTagId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All tags</option>
              {reportTagOptions.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
            <select
              value={reportMemberId}
              onChange={(e) => setReportMemberId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All team members</option>
              {teamMembers
                .filter((member) => member.role !== 'client')
                .map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
            </select>
          </div>
          {reportRangePreset === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs font-bold text-gray-500 space-y-1">
                <span>From</span>
                <input
                  type="date"
                  value={reportCustomFrom}
                  onChange={(e) => setReportCustomFrom(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </label>
              <label className="text-xs font-bold text-gray-500 space-y-1">
                <span>To</span>
                <input
                  type="date"
                  value={reportCustomTo}
                  onChange={(e) => setReportCustomTo(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </label>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Showing {reportBounds.from.toLocaleDateString()} –{' '}
            {reportBounds.to.toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
              Total time
            </p>
            <p className="text-3xl font-mono font-black text-purple-900 mt-2">
              {formatTime(reportTotalSeconds)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
              Top project
            </p>
            <p className="text-lg font-black text-gray-900 mt-2 truncate">
              {topProject?.name || '—'}
            </p>
            <p className="text-sm font-mono text-purple-700 mt-1">
              {topProject ? formatTime(topProject.seconds) : '00:00:00'}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
              Top client
            </p>
            <p className="text-lg font-black text-gray-900 mt-2 truncate">
              {topClient?.name || '—'}
            </p>
            <p className="text-sm font-mono text-purple-700 mt-1">
              {topClient ? formatTime(topClient.seconds) : '00:00:00'}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 overflow-visible">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-bold text-gray-900">Time by day</h2>
              <p className="text-xs text-gray-500 mt-1">
                Hover a bar to see that day’s hours by client, project, and
                service tag.
              </p>
            </div>
          </div>
          {reportLoading ? (
            <p className="text-sm text-gray-400">Loading report…</p>
          ) : reportByDay.length === 0 ? (
            <p className="text-sm text-gray-400">No data in this range.</p>
          ) : (
            <>
              {(() => {
                const hoveredDay =
                  reportByDay.find((day) => day.key === dashboardHoverDayKey) ||
                  null;
                // Fixed min-height so expanding this panel does not shift bars
                // under the cursor and immediately clear hover.
                return (
                  <div className="mb-4 min-h-[7.5rem] rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-left">
                    {!hoveredDay ? (
                      <p className="text-xs text-gray-400">
                        Hover a day bar to inspect hours by client, project, and
                        service tag.
                      </p>
                    ) : (
                      <div className="rounded-lg border border-purple-100 bg-purple-50/80 -mx-1 px-3 py-2">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-[10px] font-black uppercase tracking-wider text-purple-700">
                            {hoveredDay.label}
                          </p>
                          <p className="text-sm font-mono font-bold text-gray-900">
                            {formatTime(hoveredDay.seconds)}
                          </p>
                        </div>
                        {hoveredDay.seconds <= 0 ? (
                          <p className="text-[11px] text-gray-500 mt-2">
                            No time logged this day.
                          </p>
                        ) : (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
                            <div className="min-w-0">
                              <p className="font-bold text-gray-500 uppercase tracking-wide text-[9px] mb-1">
                                Clients
                              </p>
                              {hoveredDay.clients.slice(0, 5).map((item) => (
                                <div
                                  key={`day-client-${hoveredDay.key}-${item.id}`}
                                  className="flex justify-between gap-2 text-gray-700 py-0.5"
                                >
                                  <span className="break-words min-w-0">
                                    {item.name}
                                  </span>
                                  <span className="font-mono shrink-0">
                                    {formatTime(item.seconds)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-500 uppercase tracking-wide text-[9px] mb-1">
                                Projects
                              </p>
                              {hoveredDay.projects.slice(0, 5).map((item) => (
                                <div
                                  key={`day-project-${hoveredDay.key}-${item.id}`}
                                  className="flex justify-between gap-2 text-gray-700 py-0.5"
                                >
                                  <span className="break-words min-w-0">
                                    {item.name}
                                  </span>
                                  <span className="font-mono shrink-0">
                                    {formatTime(item.seconds)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-500 uppercase tracking-wide text-[9px] mb-1">
                                Service tags
                              </p>
                              {hoveredDay.tags.slice(0, 5).map((item) => (
                                <div
                                  key={`day-tag-${hoveredDay.key}-${item.id}`}
                                  className="flex justify-between gap-2 text-gray-700 py-0.5"
                                >
                                  <span className="break-words min-w-0">
                                    {item.name}
                                  </span>
                                  <span className="font-mono shrink-0">
                                    {formatTime(item.seconds)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div
                className="flex items-end gap-1.5 h-56 overflow-x-auto pb-1 pt-1"
                onMouseLeave={() => setDashboardHoverDayKey(null)}
              >
                {reportByDay.map((day) => {
                  const isHovered = dashboardHoverDayKey === day.key;
                  const hasTime = day.seconds > 0;
                  const barHeight = hasTime
                    ? Math.max(
                        8,
                        Math.round((day.seconds / maxDaySeconds) * 132)
                      )
                    : 4;
                  return (
                    <button
                      type="button"
                      key={day.key}
                      className={`group flex flex-col items-center justify-end gap-1 min-w-[3rem] flex-1 h-full rounded-lg border-0 bg-transparent p-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                        isHovered ? 'bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setDashboardHoverDayKey(day.key)}
                      onFocus={() => setDashboardHoverDayKey(day.key)}
                      aria-label={`${day.label}: ${formatTime(day.seconds)}`}
                    >
                      <span
                        className={`text-[10px] font-bold leading-tight text-center px-1.5 py-0.5 rounded min-h-[1.75rem] flex items-end justify-center whitespace-nowrap ${
                          isHovered
                            ? 'text-white bg-gray-900 shadow-sm'
                            : hasTime
                              ? 'text-gray-500 bg-transparent'
                              : 'text-transparent bg-transparent'
                        }`}
                      >
                        {formatTime(day.seconds)}
                      </span>
                      <div
                        className={`w-full max-w-[32px] rounded-t-md transition-all duration-150 ${
                          !hasTime
                            ? isHovered
                              ? 'bg-gray-400'
                              : 'bg-gray-200'
                            : isHovered
                              ? 'bg-purple-800 ring-2 ring-purple-300'
                              : 'bg-purple-500 group-hover:bg-purple-700'
                        }`}
                        style={{ height: `${barHeight}px` }}
                      />
                      <span
                        className={`text-[9px] font-bold truncate w-full text-center ${
                          isHovered ? 'text-purple-800' : 'text-gray-500'
                        }`}
                      >
                        {day.label.split(',')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {dashboardViewMode === 'cards' ? (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
                <h2 className="font-bold text-gray-900 mb-4">Projects</h2>
                {reportByProject.length === 0 ? (
                  <p className="text-sm text-gray-400">No project time yet.</p>
                ) : (
                  <div className="space-y-3">
                    {reportByProject.slice(0, 8).map((item) => {
                      const pct =
                        reportTotalSeconds > 0
                          ? (item.seconds / reportTotalSeconds) * 100
                          : 0;
                      return (
                        <div key={item.id}>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="font-bold text-gray-800 truncate">
                              {item.name}
                            </span>
                            <span className="font-mono text-xs text-gray-600 shrink-0">
                              {formatTime(item.seconds)} · {pct.toFixed(1)}%
                            </span>
                          </div>
                          <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${Math.max(2, pct)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900">Team activities</h2>
                </div>
                {reportByMember.length === 0 ? (
                  <div className="p-8 text-sm text-gray-400">
                    No team activity in this range.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {reportByMember.map((member) => (
                      <div
                        key={member.id}
                        className="px-5 py-4 flex items-start gap-3"
                      >
                        <div className="h-10 w-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-black shrink-0">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm font-bold text-gray-900 truncate"
                            title={hoverFullText(
                              `${member.name}${member.id === currentUser.id ? ' (You)' : ''}`
                            )}
                          >
                            {member.name}
                            {member.id === currentUser.id ? ' (You)' : ''}
                          </p>
                          <p
                            className="text-xs text-gray-500 mt-0.5 truncate"
                            title={hoverFullText(
                              `Latest: ${member.latestTask} (${member.latestProject})`
                            )}
                          >
                            Latest: {member.latestTask} ({member.latestProject})
                          </p>
                          <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{
                                width: `${Math.max(
                                  2,
                                  reportTotalSeconds > 0
                                    ? (member.seconds / reportTotalSeconds) * 100
                                    : 0
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                        <span className="font-mono text-xs font-bold text-gray-800 shrink-0">
                          {formatTime(member.seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900">
                    Service tag hours
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Hours spent on each service tag assigned to tasks, with
                    related clients and projects.
                  </p>
                </div>
                {reportByServiceTag.length === 0 ? (
                  <div className="p-8 text-sm text-gray-400">
                    No service tag time in this range. Tag tasks in Projects to
                    see spend here.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {reportByServiceTag.slice(0, 12).map((item) => (
                      <div
                        key={item.id}
                        className="px-5 py-3 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p
                            className={`text-sm font-bold truncate ${
                              item.id === 'none'
                                ? 'text-gray-500'
                                : 'text-emerald-800'
                            }`}
                          >
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {(item.clients || [])
                              .slice(0, 2)
                              .map((c) => c.name)
                              .join(', ') || 'No client'}
                            {' · '}
                            {(item.projects || [])
                              .slice(0, 2)
                              .map((p) => p.name)
                              .join(', ') || 'No project'}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                            {item.latestTask
                              ? `Latest: ${item.latestTask}`
                              : ''}
                          </p>
                        </div>
                        <span className="font-mono text-xs font-bold text-gray-800 shrink-0">
                          {formatTime(item.seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900">
                    Client × service tag / project
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Hours by client service; untagged work rolls up to the project.
                  </p>
                </div>
                {reportByClientService.length === 0 ? (
                  <div className="p-8 text-sm text-gray-400">
                    No client service time in this range.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {reportByClientService.slice(0, 12).map((item) => (
                      <div
                        key={item.id}
                        className="px-5 py-3 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {item.clientName}
                          </p>
                          <p
                            className={`text-xs font-semibold mt-0.5 truncate ${
                              item.serviceKind === 'tag'
                                ? 'text-emerald-700'
                                : 'text-indigo-700'
                            }`}
                          >
                            {item.serviceKind === 'tag'
                              ? item.serviceName
                              : `Project · ${item.serviceName}`}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                            {item.latestTask}
                            {item.serviceKind === 'tag' && item.latestProject
                              ? ` · ${item.latestProject}`
                              : ''}
                          </p>
                        </div>
                        <span className="font-mono text-xs font-bold text-gray-800 shrink-0">
                          {formatTime(item.seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="font-bold text-gray-900">Projects by time</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3 font-bold">Project</th>
                      <th className="px-5 py-3 font-bold">Time</th>
                      <th className="px-5 py-3 font-bold">Share</th>
                      <th className="px-5 py-3 font-bold">Latest task</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportByProject.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-5 py-6 text-gray-400">
                          No project time yet.
                        </td>
                      </tr>
                    ) : (
                      reportByProject.map((item) => {
                        const pct =
                          reportTotalSeconds > 0
                            ? (item.seconds / reportTotalSeconds) * 100
                            : 0;
                        return (
                          <tr key={item.id} className="hover:bg-purple-50/40">
                            <td className="px-5 py-3 font-semibold text-gray-900">
                              {item.name}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs text-gray-700">
                              {formatTime(item.seconds)}
                            </td>
                            <td className="px-5 py-3 text-xs text-gray-600">
                              {pct.toFixed(1)}%
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-500 truncate max-w-[16rem]"
                              title={hoverFullText(item.latestTask || '—')}
                            >
                              {item.latestTask || '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="font-bold text-gray-900">Service tag hours</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Same service tags used on project tasks (e.g. Static Posts),
                  with time rolled up by client and project.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3 font-bold">Service tag</th>
                      <th className="px-5 py-3 font-bold">Client</th>
                      <th className="px-5 py-3 font-bold">Project</th>
                      <th className="px-5 py-3 font-bold">Time</th>
                      <th className="px-5 py-3 font-bold">Share</th>
                      <th className="px-5 py-3 font-bold">Latest task</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportByServiceTag.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-6 text-gray-400">
                          No service tag time yet. Assign tags on tasks in
                          Projects, then track time against those tasks.
                        </td>
                      </tr>
                    ) : (
                      reportByServiceTag.map((item) => {
                        const pct =
                          reportTotalSeconds > 0
                            ? (item.seconds / reportTotalSeconds) * 100
                            : 0;
                        return (
                          <tr key={item.id} className="hover:bg-purple-50/40">
                            <td
                              className={`px-5 py-3 font-semibold ${
                                item.id === 'none'
                                  ? 'text-gray-500'
                                  : 'text-emerald-800'
                              }`}
                            >
                              {item.name}
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-600 truncate max-w-[10rem]"
                              title={hoverFullText(
                                (item.clients || [])
                                  .map((c) => c.name)
                                  .join(', ') || 'No client'
                              )}
                            >
                              {(item.clients || [])
                                .map((c) => c.name)
                                .join(', ') || 'No client'}
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-600 truncate max-w-[10rem]"
                              title={hoverFullText(
                                (item.projects || [])
                                  .map((p) => p.name)
                                  .join(', ') || '—'
                              )}
                            >
                              {(item.projects || [])
                                .map((p) => p.name)
                                .join(', ') || '—'}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs text-gray-700">
                              {formatTime(item.seconds)}
                            </td>
                            <td className="px-5 py-3 text-xs text-gray-600">
                              {pct.toFixed(1)}%
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-500 truncate max-w-[12rem]"
                              title={hoverFullText(item.latestTask || '—')}
                            >
                              {item.latestTask || '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="font-bold text-gray-900">
                  Client × service tag / project
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Hours spent per client by subscribed service tag, or by project
                  when that work has no service tags.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3 font-bold">Client</th>
                      <th className="px-5 py-3 font-bold">Service / project</th>
                      <th className="px-5 py-3 font-bold">Type</th>
                      <th className="px-5 py-3 font-bold">Project</th>
                      <th className="px-5 py-3 font-bold">Time</th>
                      <th className="px-5 py-3 font-bold">Latest task</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportByClientService.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-6 text-gray-400">
                          No client service time in this range.
                        </td>
                      </tr>
                    ) : (
                      reportByClientService.map((item) => (
                        <tr key={item.id} className="hover:bg-purple-50/40">
                          <td className="px-5 py-3 font-semibold text-gray-900">
                            {item.clientName}
                          </td>
                          <td
                            className={`px-5 py-3 font-semibold ${
                              item.serviceKind === 'tag'
                                ? 'text-emerald-700'
                                : 'text-indigo-700'
                            }`}
                          >
                            {item.serviceName}
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-500">
                            {item.serviceKind === 'tag'
                              ? 'Service tag'
                              : 'Project (no tags)'}
                          </td>
                          <td
                            className="px-5 py-3 text-xs text-gray-600 truncate max-w-[10rem]"
                            title={hoverFullText(
                              item.projectName || item.latestProject || '—'
                            )}
                          >
                            {item.projectName || item.latestProject || '—'}
                          </td>
                          <td className="px-5 py-3 font-mono text-xs text-gray-700">
                            {formatTime(item.seconds)}
                          </td>
                          <td
                            className="px-5 py-3 text-xs text-gray-500 truncate max-w-[14rem]"
                            title={hoverFullText(item.latestTask || '—')}
                          >
                            {item.latestTask || '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="font-bold text-gray-900">Detailed time entries</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3 font-bold">Member</th>
                      <th className="px-5 py-3 font-bold">Task</th>
                      <th className="px-5 py-3 font-bold">Project</th>
                      <th className="px-5 py-3 font-bold">Client</th>
                      <th className="px-5 py-3 font-bold">Service tags</th>
                      <th className="px-5 py-3 font-bold">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredReportRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-6 text-gray-400">
                          No time entries in this range.
                        </td>
                      </tr>
                    ) : (
                      filteredReportRows
                        .slice()
                        .sort((a, b) => b.startedAt - a.startedAt)
                        .map((row) => (
                          <tr key={row.id} className="hover:bg-purple-50/40">
                            <td className="px-5 py-3 font-semibold text-gray-900">
                              {row.userName}
                              {row.userId === currentUser.id ? ' (You)' : ''}
                            </td>
                            <td
                              className="px-5 py-3 text-gray-800 truncate max-w-[12rem]"
                              title={hoverFullText(row.taskName)}
                            >
                              {row.taskName}
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-600 truncate max-w-[10rem]"
                              title={hoverFullText(row.projectName)}
                            >
                              {row.projectName}
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-gray-600 truncate max-w-[10rem]"
                              title={hoverFullText(
                                (row.clients || []).map((c) => c.name).join(', ') ||
                                  'No client'
                              )}
                            >
                              {(row.clients || []).map((c) => c.name).join(', ') ||
                                'No client'}
                            </td>
                            <td
                              className="px-5 py-3 text-xs text-emerald-700 truncate max-w-[12rem]"
                              title={hoverFullText(
                                (row.tags || []).map((t) => t.name).join(', ') ||
                                  'Untagged'
                              )}
                            >
                              {(row.tags || []).map((t) => t.name).join(', ') ||
                                'Untagged'}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs text-gray-700">
                              {formatTime(row.secondsInRange)}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );

  const timeTrackerScreen = (
    <div className="space-y-3">
      {/* Clockify-style compact timer / manual log bar */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 space-y-2">
        <div className="flex flex-wrap lg:flex-nowrap items-center gap-2">
          <input
            type="text"
            value={quickTimerDescription}
            disabled={Boolean(activeTimerTask) && trackerInputMode === 'timer'}
            onChange={(e) => {
              setQuickTimerDescription(e.target.value);
              if (userAlert) clearUserAlert();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (trackerInputMode === 'manual') {
                  handleManualTimeLog(e);
                } else if (activeTimerTask) {
                  return;
                } else {
                  handleQuickTimerStart(e);
                }
              }
            }}
            placeholder={
              activeTimerTask && trackerInputMode === 'timer'
                ? `Tracking: ${activeTimerTask.name}`
                : quickTimerTaskId
                  ? 'Optional note (existing task selected)'
                  : 'What are you working on?'
            }
            className="flex-1 min-w-[12rem] px-2 py-1.5 text-sm text-gray-800 placeholder:text-gray-400 border-0 focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-gray-500"
          />

          <select
            value={quickTimerProjectId}
            disabled={Boolean(activeTimerTask) && trackerInputMode === 'timer'}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__create_project__') {
                setQuickCreatingProject(true);
                setQuickCreatingClient(false);
                setQuickNewProjectName('');
                if (userAlert) clearUserAlert();
                return;
              }
              setQuickTimerProjectId(value);
              setQuickTimerTaskId('');
              setQuickCreatingProject(false);
              if (value) {
                const projectClients = getClientsForProject(value);
                const stillValid = projectClients.some(
                  (client) => client.id === quickTimerClientId
                );
                if (stillValid) {
                  // Keep current client when it belongs to the new project.
                } else {
                  const autoClientId =
                    projectClients[0]?.id ||
                    projects.find((project) => project.id === value)?.clientId ||
                    '';
                  setQuickTimerClientId(autoClientId);
                }
              } else {
                setQuickTimerClientId('');
              }
              if (userAlert) clearUserAlert();
            }}
            className="shrink-0 max-w-[11rem] px-2 py-1.5 text-xs font-medium bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              color: quickTimerProjectId
                ? projectDotColor(quickTimerProjectId)
                : '#9ca3af',
            }}
            title="Project"
          >
            <option value="" disabled={Boolean(quickTimerProjectId)}>
              Project
            </option>
            {quickTimerProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {asDropdownChoice(project.name)}
              </option>
            ))}
            {canCreateProjects && (
              <option value="__create_project__">
                {asDropdownChoice('+ Add New Project')}
              </option>
            )}
          </select>

          <select
            value={quickTimerClientId}
            disabled={
              (Boolean(activeTimerTask) && trackerInputMode === 'timer') ||
              !quickTimerProjectId
            }
            onChange={(e) => {
              const value = e.target.value;
              if (value === '__create_client__') {
                setQuickCreatingClient(true);
                setQuickCreatingProject(false);
                setQuickNewClientName('');
                if (userAlert) clearUserAlert();
                return;
              }
              setQuickTimerClientId(value);
              setQuickCreatingClient(false);
              if (userAlert) clearUserAlert();
            }}
            className="shrink-0 max-w-[9rem] px-2 py-1.5 text-xs text-gray-600 bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            title={
              quickTimerProjectId
                ? 'Client (assigned to selected project)'
                : 'Select a project first'
            }
          >
            <option value="" disabled={Boolean(quickTimerClientId)}>
              Client
            </option>
            {quickTimerClientId ? (
              <option value="">{asDropdownChoice('No client')}</option>
            ) : null}
            {quickTimerProjectClients.map((client) => (
              <option key={client.id} value={client.id}>
                {asDropdownChoice(client.name)}
              </option>
            ))}
            {isSystemAdmin && quickTimerProjectId && (
              <option value="__create_client__">
                {asDropdownChoice('+ Client')}
              </option>
            )}
          </select>

          {quickTimerProjectId &&
            !(activeTimerTask && trackerInputMode === 'timer') && (
            <select
              value={quickTimerTaskId}
              onChange={(e) => {
                const nextId = e.target.value;
                setQuickTimerTaskId(nextId);
                if (nextId) {
                  const selected = tasks.find((task) => task.id === nextId);
                  if (selected) setQuickTimerDescription(selected.name);
                }
                if (userAlert) clearUserAlert();
              }}
              className="shrink-0 max-w-[12rem] px-2 py-1.5 text-xs text-gray-600 bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer"
              title="Existing task"
            >
              <option value="">New / type description</option>
              {quickProjectTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name}
                </option>
              ))}
            </select>
          )}

          {trackerInputMode === 'manual' ? (
            <div className="ml-auto flex flex-wrap items-center gap-1.5 shrink-0">
              <input
                type="date"
                value={manualLogDate}
                onChange={(e) => setManualLogDate(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-700"
                title="Date"
              />
              <input
                type="time"
                value={manualLogStart}
                onChange={(e) => setManualLogStart(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-700"
                title="Start time"
              />
              <span className="text-xs text-gray-400">–</span>
              <input
                type="time"
                value={manualLogEnd}
                onChange={(e) => setManualLogEnd(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-700"
                title="End time"
              />
              <span className="font-mono text-sm font-semibold text-gray-800 tabular-nums w-[4.75rem] text-right">
                {formatTime(manualPreviewSeconds)}
              </span>
              <button
                type="button"
                onClick={handleManualTimeLog}
                disabled={manualLogSaving}
                className="px-4 py-1.5 rounded text-xs font-bold uppercase tracking-wide text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {manualLogSaving ? '…' : 'Add'}
              </button>
            </div>
          ) : (
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {activeTimerTask ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[11px] font-bold text-green-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                  Timer active in top bar
                </span>
              ) : (
                <>
                  <span className="font-mono text-sm font-semibold text-gray-800 tabular-nums w-[4.75rem] text-right">
                    {formatTime(0)}
                  </span>
                  <button
                    type="button"
                    onClick={handleQuickTimerStart}
                    disabled={quickTimerStarting}
                    className="px-4 py-1.5 rounded text-xs font-bold uppercase tracking-wide text-white bg-green-600 hover:bg-green-700 disabled:opacity-60"
                  >
                    {quickTimerStarting ? '…' : 'Start'}
                  </button>
                </>
              )}
            </div>
          )}

          <div className="flex items-center border border-gray-200 rounded-md overflow-hidden shrink-0">
            <button
              type="button"
              onClick={() => setTrackerInputMode('timer')}
              className={`px-2 py-1.5 ${
                trackerInputMode === 'timer'
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              title="Timer mode"
              aria-pressed={trackerInputMode === 'timer'}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setTrackerInputMode('manual')}
              className={`px-2 py-1.5 border-l border-gray-200 ${
                trackerInputMode === 'manual'
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              title="Manual time entry"
              aria-pressed={trackerInputMode === 'manual'}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </button>
          </div>
        </div>

        {quickCreatingClient && (
          <div className="flex flex-wrap items-center gap-2 px-1 pt-1 border-t border-gray-100">
            <span className="text-[10px] font-black uppercase tracking-wider text-purple-700">
              New client
            </span>
            <input
              type="text"
              value={quickNewClientName}
              onChange={(e) => setQuickNewClientName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  createClientFromTracker();
                }
                if (e.key === 'Escape') {
                  setQuickCreatingClient(false);
                  setQuickNewClientName('');
                }
              }}
              placeholder="Client name…"
              autoFocus
              className="flex-1 min-w-[10rem] px-2 py-1.5 text-xs border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              type="button"
              onClick={createClientFromTracker}
              disabled={quickCreateSaving || !quickNewClientName.trim()}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {quickCreateSaving ? '…' : 'Create client'}
            </button>
            <button
              type="button"
              onClick={() => {
                setQuickCreatingClient(false);
                setQuickNewClientName('');
              }}
              className="px-2 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        )}

        {quickCreatingProject && (
          <div className="flex flex-wrap items-center gap-2 px-1 pt-1 border-t border-gray-100">
            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-700">
              New project
            </span>
            <input
              type="text"
              value={quickNewProjectName}
              onChange={(e) => setQuickNewProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  createProjectFromTracker();
                }
                if (e.key === 'Escape') {
                  setQuickCreatingProject(false);
                  setQuickNewProjectName('');
                }
              }}
              placeholder={
                quickTimerClientId
                  ? 'Project name…'
                  : 'Select a client first, then name the project…'
              }
              autoFocus
              className="flex-1 min-w-[10rem] px-2 py-1.5 text-xs border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="button"
              onClick={createProjectFromTracker}
              disabled={
                quickCreateSaving ||
                !quickNewProjectName.trim() ||
                !quickTimerClientId
              }
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {quickCreateSaving ? '…' : 'Create project'}
            </button>
            <button
              type="button"
              onClick={() => {
                setQuickCreatingProject(false);
                setQuickNewProjectName('');
              }}
              className="px-2 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        )}

        <p className="text-[10px] text-gray-400 px-1">
          {trackerInputMode === 'manual'
            ? 'Manual mode: set date, start, and end, then Add. Pick an existing task or type a new description.'
            : 'Timer mode: Start tracks live time. Switch to the checklist icon for manual logging.'}
        </p>
      </div>

      {projects.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm space-y-1">
          <p className="font-bold">{USER_ALERTS.noProjects.title}</p>
          <p>{USER_ALERTS.noProjects.message}</p>
        </div>
      )}

      {timerStatus && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-3 py-2 text-xs font-medium flex items-start justify-between gap-3">
          <span>{timerStatus}</span>
          <button
            type="button"
            onClick={() => setTimerStatus('')}
            className="shrink-0 text-green-700 hover:text-green-900 font-bold"
            aria-label="Dismiss status"
          >
            ×
          </button>
        </div>
      )}

      {/* Day-grouped time entries */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg flex items-center justify-between text-xs text-gray-600">
          <span className="font-medium">This week</span>
          <span>
            Week total:{' '}
            <span className="font-mono font-semibold text-gray-800">
              {formatTime(weekSecondsDisplay)}
            </span>
          </span>
        </div>

        {trackerDayGroups.length === 0 ? (
          <div className="px-4 py-8 text-sm text-gray-500 text-center">
            No time entries yet. Start a timer above to begin tracking.
          </div>
        ) : (
          trackerDayGroups.map((group) => (
            <div key={group.key}>
              <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100 flex items-center justify-between text-xs text-gray-600">
                <span className="font-medium">{formatTrackerDayLabel(group.date)}</span>
                <span>
                  Total:{' '}
                  <span className="font-mono font-semibold text-gray-800">
                    {formatTime(group.totalSeconds)}
                  </span>
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {group.entries.map((entry) => {
                  const isEditing = trackerEditingEntryId === entry.id;
                  const linkedTask = tasks.find((task) => task.id === entry.taskId);
                  const entryTags = linkedTask?.tags || entry.tags || [];
                  const entryProjectClients = getClientsForProject(entry.projectId);
                  const taskClientIds = (linkedTask?.clients || [])
                    .map((client) => client.id)
                    .filter(Boolean);
                  // Prefer an explicit task client; otherwise inherit from the project.
                  const entryClientId =
                    taskClientIds.find((id) =>
                      entryProjectClients.some((client) => client.id === id)
                    ) ||
                    taskClientIds[0] ||
                    entryProjectClients[0]?.id ||
                    '';
                  const entryClientOptions = [...entryProjectClients];
                  if (
                    entryClientId &&
                    !entryClientOptions.some((client) => client.id === entryClientId)
                  ) {
                    const orphan =
                      (linkedTask?.clients || []).find(
                        (client) => client.id === entryClientId
                      ) ||
                      clients.find((client) => client.id === entryClientId);
                    if (orphan) {
                      entryClientOptions.push({
                        id: orphan.id,
                        name: orphan.name,
                      });
                    }
                  }

                  return (
                  <div
                    key={entry.id}
                    className={`px-3 py-2 text-sm ${
                      entry.isRunning ? 'bg-blue-50/40' : ''
                    } ${isEditing ? 'bg-purple-50/50' : 'hover:bg-gray-50'}`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setTrackerEntryMenuId(null);
                        setTrackerTagMenuEntryId(null);
                        setTrackerClientMenuEntryId(null);
                        setTrackerTagSearch('');
                        if (isEditing) {
                          setTrackerEditingEntryId(null);
                          return;
                        }
                        beginEditTimeEntry(entry);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.currentTarget.click();
                        }
                      }}
                      className="flex w-full flex-wrap lg:flex-nowrap items-center gap-x-6 gap-y-2 cursor-pointer rounded-md -mx-1 px-1"
                      title="Click to edit task, tags, or logged time"
                    >
                    <div className="flex-1 min-w-[12rem] max-w-full lg:max-w-[42%] flex items-center gap-2 overflow-hidden">
                      {entry.isFavorite && (
                        <span
                          className="shrink-0 text-amber-500"
                          title="Favorite task"
                          aria-label="Favorite"
                        >
                          ★
                        </span>
                      )}
                      <span
                        className="truncate text-gray-800 font-medium"
                        title={hoverFullText(entry.taskName)}
                      >
                        {entry.taskName}
                      </span>
                      <span
                        className="shrink-0 truncate text-xs font-medium max-w-[10rem]"
                        style={{ color: projectDotColor(entry.projectId) }}
                        title={hoverFullText(entry.projectName)}
                      >
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                          style={{ backgroundColor: projectDotColor(entry.projectId) }}
                        />
                        {entry.projectName}
                      </span>
                    </div>

                    <div
                      className="relative shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setTrackerEntryMenuId(null);
                          setTrackerEditingEntryId(null);
                          setTrackerTagMenuEntryId(null);
                          setTrackerClientMenuEntryId((id) =>
                            id === entry.id ? null : entry.id
                          );
                        }}
                        className="inline-flex items-center max-w-[12rem] truncate rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                        title={
                          entryProjectClients.length > 0
                            ? `Project clients (${entryProjectClients.length}): ${entryProjectClients
                                .map((client) => client.name)
                                .join(', ')}`
                            : 'No clients assigned to this project'
                        }
                        aria-label="Entry client"
                        aria-expanded={trackerClientMenuEntryId === entry.id}
                      >
                        <span className="shrink-0 mr-1 text-gray-500 font-black uppercase tracking-wide">
                          Client:
                        </span>
                        <span
                          className="truncate text-gray-800"
                          title={hoverFullText(
                            entryClientOptions.find((c) => c.id === entryClientId)
                              ?.name || 'No client'
                          )}
                        >
                          {entryClientOptions.find((c) => c.id === entryClientId)
                            ?.name || 'No client'}
                        </span>
                        <svg
                          className="ml-1 h-3 w-3 shrink-0 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {trackerClientMenuEntryId === entry.id && (
                        <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                            <p className="text-[10px] font-black uppercase tracking-wider text-gray-500">
                              Client
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {entryProjectClients.length > 0
                                ? `${entryProjectClients.length} assigned to this project`
                                : 'No clients on this project'}
                            </p>
                          </div>
                          <div className="max-h-48 overflow-y-auto py-1">
                            <button
                              type="button"
                              onClick={() => {
                                handleTrackerEntryClientChange(entry, '');
                                setTrackerClientMenuEntryId(null);
                              }}
                              className={`w-full text-left px-3 py-1.5 text-xs ${
                                !entryClientId
                                  ? 'bg-purple-50 text-purple-800 font-semibold'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {!entryClientId ? '✓ ' : ''}No client
                            </button>
                            {entryClientOptions.map((client) => {
                              const isSelected = client.id === entryClientId;
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() => {
                                    handleTrackerEntryClientChange(
                                      entry,
                                      client.id
                                    );
                                    setTrackerClientMenuEntryId(null);
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-xs ${
                                    isSelected
                                      ? 'bg-purple-50 text-purple-800 font-semibold'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  {isSelected ? '✓ ' : ''}
                                  {client.name}
                                </button>
                              );
                            })}
                            {entryClientOptions.length === 0 && (
                              <p className="px-3 py-2 text-[11px] text-gray-400">
                                Assign clients on the project Configure screen.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Centered service tags column */}
                    <div
                      className="relative shrink-0 w-full sm:w-40 lg:w-44 flex justify-center lg:justify-start"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                        <button
                          type="button"
                          onClick={() => {
                            setTrackerEntryMenuId(null);
                            setTrackerEditingEntryId(null);
                            setTrackerClientMenuEntryId(null);
                            setTrackerTagMenuEntryId((id) => {
                              if (id === entry.id) {
                                setTrackerTagSearch('');
                                setTrackerNewTagName('');
                                return null;
                              }
                              setTrackerTagSearch('');
                              setTrackerNewTagName('');
                              return entry.id;
                            });
                          }}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                            trackerTagMenuEntryId === entry.id || entryTags.length > 0
                              ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                              : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                          }`}
                          title="Service tags"
                          aria-label="Service tags"
                          aria-expanded={trackerTagMenuEntryId === entry.id}
                        >
                          <svg
                            className="w-4 h-4 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          {entryTags.length > 0 ? (
                            <span
                              className="text-[10px] font-bold max-w-[7.5rem] truncate"
                              title={hoverFullText(
                                entryTags.map((tag) => tag.name).join(', ')
                              )}
                            >
                              {entryTags.length === 1
                                ? entryTags[0].name
                                : `${entryTags.length} tags`}
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-gray-400 hidden sm:inline">
                              Tags
                            </span>
                          )}
                        </button>

                        {trackerTagMenuEntryId === entry.id && (
                          <div className="absolute left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-full mt-1 z-50 w-72 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                              <svg
                                className="w-3.5 h-3.5 text-gray-400 shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z"
                                />
                              </svg>
                              <input
                                type="text"
                                value={trackerTagSearch}
                                onChange={(e) => setTrackerTagSearch(e.target.value)}
                                placeholder="Add/Search tags"
                                autoFocus
                                className="flex-1 text-xs text-gray-800 placeholder:text-gray-400 border-0 focus:outline-none focus:ring-0 bg-transparent"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setTrackerTagMenuEntryId(null);
                                  setTrackerTagSearch('');
                                  setTrackerNewTagName('');
                                }}
                                className="text-[10px] font-bold text-emerald-700 hover:underline shrink-0"
                              >
                                Done
                              </button>
                            </div>

                            {entryTags.length > 0 && (
                              <div className="px-2 py-2 border-b border-gray-100 flex flex-wrap gap-1">
                                {entryTags.map((tag) => (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleTaskTag(entry.taskId, tag)}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-100 hover:bg-red-50 hover:text-red-700"
                                    title="Remove tag"
                                  >
                                    {tag.name} ×
                                  </button>
                                ))}
                              </div>
                            )}

                            <div className="max-h-52 overflow-y-auto p-2 space-y-2">
                              {(() => {
                                const query = trackerTagSearch.trim().toLowerCase();
                                const systemTags = tags
                                  .filter(
                                    (tag) => !String(tag.id).startsWith('local-')
                                  )
                                  .filter(
                                    (tag) =>
                                      !query ||
                                      tag.name.toLowerCase().includes(query)
                                  )
                                  .sort((a, b) => a.name.localeCompare(b.name));

                                const systemNames = new Set(
                                  systemTags.map((tag) => tag.name.toLowerCase())
                                );

                                const matrixExtras = MASTER_TASK_MATRIX.map(
                                  (group) => ({
                                    ...group,
                                    services: group.services.filter((service) => {
                                      const lower = service.toLowerCase();
                                      if (systemNames.has(lower)) return false;
                                      if (
                                        tags.some(
                                          (tag) =>
                                            tag.name.toLowerCase() === lower &&
                                            !String(tag.id).startsWith('local-')
                                        )
                                      ) {
                                        return false;
                                      }
                                      return !query || lower.includes(query);
                                    }),
                                  })
                                ).filter((group) => group.services.length > 0);

                                if (
                                  systemTags.length === 0 &&
                                  matrixExtras.length === 0
                                ) {
                                  return (
                                    <p className="px-2 py-3 text-xs text-gray-400 text-center">
                                      {query
                                        ? 'No matching tags. Try another search.'
                                        : 'No service tags in the system yet.'}
                                    </p>
                                  );
                                }

                                return (
                                  <>
                                    {systemTags.length > 0 && (
                                      <div>
                                        <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                          All service tags
                                        </p>
                                        <div className="mt-0.5 space-y-0.5">
                                          {systemTags.map((tag) => {
                                            const isTagged = entryTags.some(
                                              (item) => item.id === tag.id
                                            );
                                            return (
                                              <button
                                                key={`tracker-sys-tag-${entry.id}-${tag.id}`}
                                                type="button"
                                                onClick={() =>
                                                  toggleTaskTag(entry.taskId, tag)
                                                }
                                                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium ${
                                                  isTagged
                                                    ? 'bg-emerald-50 text-emerald-800'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                              >
                                                {isTagged ? '✓ ' : '+ '}
                                                {tag.name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    {matrixExtras.map((group) => (
                                        <div
                                          key={`tracker-tag-icon-${entry.id}-${group.category}`}
                                        >
                                          <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                            Add from matrix · {group.category}
                                          </p>
                                          <div className="mt-0.5 space-y-0.5">
                                            {group.services.map((service) => (
                                              <button
                                                key={`tracker-tag-svc-${entry.id}-${service}`}
                                                type="button"
                                                onClick={async () => {
                                                  const tag =
                                                    await ensureTagByName(service);
                                                  if (tag) {
                                                    await toggleTaskTag(
                                                      entry.taskId,
                                                      tag
                                                    );
                                                  }
                                                }}
                                                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50"
                                              >
                                                + {service}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                  </>
                                );
                              })()}
                            </div>

                            {isSystemAdmin ? (
                              <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                                <button
                                  type="button"
                                  disabled={trackerNewTagSaving}
                                  onClick={async () => {
                                    const name =
                                      trackerNewTagName.trim() ||
                                      trackerTagSearch.trim();
                                    if (!name) {
                                      setTrackerNewTagName('');
                                      return;
                                    }
                                    await createProjectAdminTagForTask(
                                      entry.taskId,
                                      entry.projectId,
                                      name
                                    );
                                  }}
                                  className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-purple-700 hover:bg-purple-50 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <span className="text-sm leading-none">+</span>
                                  Add New Tag
                                  {(trackerNewTagName.trim() ||
                                    trackerTagSearch.trim()) && (
                                    <span className="font-medium text-gray-500 max-w-[9rem] truncate">
                                      “
                                      {trackerNewTagName.trim() ||
                                        trackerTagSearch.trim()}
                                      ”
                                    </span>
                                  )}
                                </button>
                                <div className="flex gap-1.5">
                                  <input
                                    type="text"
                                    value={trackerNewTagName}
                                    onChange={(e) =>
                                      setTrackerNewTagName(e.target.value)
                                    }
                                    onKeyDown={async (e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      const name =
                                        trackerNewTagName.trim() ||
                                        trackerTagSearch.trim();
                                      if (!name) return;
                                      await createProjectAdminTagForTask(
                                        entry.taskId,
                                        entry.projectId,
                                        name
                                      );
                                    }}
                                    placeholder="New tag name…"
                                    disabled={trackerNewTagSaving}
                                    className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                  />
                                  <button
                                    type="button"
                                    disabled={
                                      trackerNewTagSaving ||
                                      !(
                                        trackerNewTagName.trim() ||
                                        trackerTagSearch.trim()
                                      )
                                    }
                                    onClick={async () => {
                                      const name =
                                        trackerNewTagName.trim() ||
                                        trackerTagSearch.trim();
                                      if (!name) return;
                                      await createProjectAdminTagForTask(
                                        entry.taskId,
                                        entry.projectId,
                                        name
                                      );
                                    }}
                                    className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                  >
                                    {trackerNewTagSaving ? '…' : 'Add'}
                                  </button>
                                </div>
                                <p className="px-1 text-[10px] text-gray-400">
                                  Team admins can create tags for the shared catalog.
                                </p>
                              </div>
                            ) : (
                              <div className="border-t border-gray-100 px-3 py-2">
                                <p className="text-[10px] text-gray-400">
                                  Ask a Team admin to create a new tag for the
                                  shared catalog.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                    </div>

                    <div
                      className="ml-auto flex items-center gap-2 shrink-0 text-xs text-gray-600"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <span className="tabular-nums hidden sm:inline">
                        {!entry.isRunning && entry.durationSeconds < 0
                          ? 'Adjustment'
                          : (
                            <>
                              {formatClockRangeTime(entry.startedAt)}
                              {' – '}
                              {entry.endedAt
                                ? formatClockRangeTime(entry.endedAt)
                                : 'now'}
                            </>
                          )}
                      </span>
                      <span
                        className={`font-mono font-semibold tabular-nums w-[4.75rem] text-right ${
                          entry.durationSeconds < 0
                            ? 'text-red-700'
                            : 'text-gray-800'
                        }`}
                      >
                        {formatTime(entry.durationSeconds)}
                      </span>
                      {!entry.isRunning && (
                        <button
                          type="button"
                          onClick={() => continueTimerFromEntry(entry)}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Continue"
                          aria-label="Continue timer"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setTrackerEditingEntryId(null);
                          setTrackerTagMenuEntryId(null);
                          setTrackerClientMenuEntryId(null);
                          setTrackerTagSearch('');
                          setTrackerEntryMenuId((id) =>
                            id === entry.id ? null : entry.id
                          );
                        }}
                        className={`p-1.5 rounded ${
                          trackerEntryMenuId === entry.id
                            ? 'text-gray-800 bg-gray-100'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-label="Entry options"
                        aria-expanded={trackerEntryMenuId === entry.id}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>
                    </div>
                    </div>

                    {isEditing && (
                      <div
                        className="mt-2 border-t border-purple-100 pt-3 space-y-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col sm:flex-row gap-2">
                          <label className="flex-1 space-y-1">
                            <span className="text-[10px] font-black uppercase tracking-wider text-purple-700">
                              Task name
                            </span>
                            <input
                              type="text"
                              value={editTaskName}
                              onChange={(e) => setEditTaskName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleRenameTask(entry.taskId);
                                }
                              }}
                              className="w-full px-3 py-2 border border-purple-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                            />
                          </label>
                          <label className="sm:w-48 space-y-1">
                            <span className="text-[10px] font-black uppercase tracking-wider text-teal-700">
                              Project
                            </span>
                            <select
                              value={entry.projectId || ''}
                              onChange={(e) =>
                                handleTrackerEntryProjectChange(
                                  entry,
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-teal-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                              title="Change project (clients update to match)"
                            >
                              <option value="__title__" disabled>
                                Project
                              </option>
                              {projects.map((project) => (
                                <option key={project.id} value={project.id}>
                                  {asDropdownChoice(project.name)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="sm:w-44 space-y-1">
                            <span className="text-[10px] font-black uppercase tracking-wider text-gray-600">
                              Client
                            </span>
                            <select
                              value={
                                entryClientOptions.some(
                                  (c) => c.id === entryClientId
                                )
                                  ? entryClientId
                                  : ''
                              }
                              onChange={(e) =>
                                handleTrackerEntryClientChange(
                                  entry,
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                              title="Clients assigned to this project"
                            >
                              <option value="__title__" disabled>
                                Client
                              </option>
                              <option value="">
                                {asDropdownChoice('No client')}
                              </option>
                              {entryClientOptions.map((client) => (
                                <option key={client.id} value={client.id}>
                                  {asDropdownChoice(client.name)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="flex items-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleRenameTask(entry.taskId)}
                              disabled={
                                !editTaskName.trim() ||
                                editTaskName.trim() === entry.taskName
                              }
                              className="px-3 py-2 rounded-xl text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                            >
                              Save name
                            </button>
                            <button
                              type="button"
                              onClick={() => setTrackerEditingEntryId(null)}
                              className="px-3 py-2 rounded-xl text-xs font-bold border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                            >
                              Done
                            </button>
                          </div>
                        </div>

                        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-800">
                              Logged duration
                            </span>
                            <span className="text-[10px] text-amber-700/80">
                              Current: {formatTime(entry.durationSeconds)}
                            </span>
                          </div>
                          {entry.isRunning ? (
                            <p className="text-[11px] text-amber-800">
                              Pause or save this timer before editing duration.
                            </p>
                          ) : (
                            <>
                              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                                <label className="flex-1 space-y-1">
                                  <span className="text-[10px] font-bold text-gray-600">
                                    Set hours to
                                  </span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={editEntryDurationInput}
                                    onChange={(e) =>
                                      setEditEntryDurationInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      handleSaveEntryDuration(entry);
                                    }}
                                    placeholder="e.g. 13 or 13:00:00"
                                    disabled={editEntryDurationSaving}
                                    className="w-full px-3 py-2 border border-amber-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 font-mono"
                                  />
                                </label>
                                <button
                                  type="button"
                                  onClick={() => handleSaveEntryDuration(entry)}
                                  disabled={
                                    editEntryDurationSaving ||
                                    !String(editEntryDurationInput).trim()
                                  }
                                  className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                                >
                                  {editEntryDurationSaving
                                    ? 'Saving…'
                                    : 'Save'}
                                </button>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                                <label className="flex-1 space-y-1">
                                  <span className="text-[10px] font-bold text-gray-600">
                                    Or adjust by (hours)
                                  </span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={editEntryAdjustInput}
                                    onChange={(e) =>
                                      setEditEntryAdjustInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      handleAdjustEntryDuration(entry);
                                    }}
                                    placeholder="e.g. -2 to go from 15h → 13h"
                                    disabled={editEntryDurationSaving}
                                    className="w-full px-3 py-2 border border-amber-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 font-mono"
                                  />
                                </label>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAdjustEntryDuration(entry)
                                  }
                                  disabled={
                                    editEntryDurationSaving ||
                                    !String(editEntryAdjustInput).trim()
                                  }
                                  className="px-3 py-2 rounded-xl text-xs font-bold border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                                >
                                  Apply
                                </button>
                              </div>
                              <p className="text-[10px] text-amber-800/80">
                                To change <span className="font-mono">15:00:00</span>{' '}
                                → <span className="font-mono">13:00:00</span>, enter{' '}
                                <span className="font-mono font-bold">13</span> in
                                “Set hours to” (not -13). Or enter{' '}
                                <span className="font-mono font-bold">-2</span> in
                                “Adjust by”.
                              </p>
                            </>
                          )}
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">
                              Service tags
                            </span>
                            <span className="text-[10px] text-gray-400">
                              Toggle tags for this assigned task
                            </span>
                          </div>
                          {entryTags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {entryTags.map((tag) => (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => toggleTaskTag(entry.taskId, tag)}
                                  className="text-[10px] font-bold px-2 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-100 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                                  title="Remove tag"
                                >
                                  {tag.name} ×
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 space-y-2">
                            {MASTER_TASK_MATRIX.map((group) => (
                              <div key={`tracker-tag-${entry.id}-${group.category}`}>
                                <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                  {group.category}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {group.services.map((service) => {
                                    const existing = tags.find(
                                      (tag) =>
                                        tag.name.toLowerCase() ===
                                          service.toLowerCase() &&
                                        !String(tag.id).startsWith('local-')
                                    );
                                    const isTagged = existing
                                      ? entryTags.some((tag) => tag.id === existing.id)
                                      : entryTags.some(
                                          (tag) =>
                                            tag.name.toLowerCase() ===
                                            service.toLowerCase()
                                        );
                                    if (
                                      currentUser.role !== 'admin' &&
                                      !existing
                                    ) {
                                      return null;
                                    }
                                    return (
                                      <button
                                        key={`tracker-svc-${entry.id}-${service}`}
                                        type="button"
                                        onClick={async () => {
                                          const tag =
                                            existing ||
                                            (await ensureTagByName(service));
                                          if (tag) await toggleTaskTag(entry.taskId, tag);
                                        }}
                                        className={`px-2 py-1 rounded-lg text-[11px] font-medium ${
                                          isTagged
                                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                            : 'text-gray-600 border border-transparent hover:bg-gray-50 hover:border-gray-200'
                                        }`}
                                      >
                                        {isTagged ? '✓ ' : '+ '}
                                        {service}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                            {tags.filter((tag) => {
                              if (String(tag.id).startsWith('local-')) return false;
                              return !MASTER_TASK_MATRIX.some((group) =>
                                group.services.some(
                                  (service) =>
                                    service.toLowerCase() === tag.name.toLowerCase()
                                )
                              );
                            }).length > 0 && (
                              <div>
                                <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                  Custom
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {tags
                                    .filter((tag) => {
                                      if (String(tag.id).startsWith('local-')) {
                                        return false;
                                      }
                                      return !MASTER_TASK_MATRIX.some((group) =>
                                        group.services.some(
                                          (service) =>
                                            service.toLowerCase() ===
                                            tag.name.toLowerCase()
                                        )
                                      );
                                    })
                                    .map((tag) => {
                                      const isTagged = entryTags.some(
                                        (item) => item.id === tag.id
                                      );
                                      return (
                                        <button
                                          key={`tracker-edit-custom-${entry.id}-${tag.id}`}
                                          type="button"
                                          onClick={() =>
                                            toggleTaskTag(entry.taskId, tag)
                                          }
                                          className={`px-2 py-1 rounded-lg text-[11px] font-medium ${
                                            isTagged
                                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                              : 'text-gray-600 border border-transparent hover:bg-gray-50 hover:border-gray-200'
                                          }`}
                                        >
                                          {isTagged ? '✓ ' : '+ '}
                                          {tag.name}
                                        </button>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                          </div>
                          {isSystemAdmin ? (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 space-y-1.5">
                              <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                Add New Tag
                              </p>
                              <div className="flex gap-1.5">
                                <input
                                  type="text"
                                  value={trackerNewTagName}
                                  onChange={(e) =>
                                    setTrackerNewTagName(e.target.value)
                                  }
                                  onKeyDown={async (e) => {
                                    if (e.key !== 'Enter') return;
                                    e.preventDefault();
                                    if (!trackerNewTagName.trim()) return;
                                    await createProjectAdminTagForTask(
                                      entry.taskId,
                                      entry.projectId,
                                      trackerNewTagName
                                    );
                                  }}
                                  placeholder="New tag name…"
                                  disabled={trackerNewTagSaving}
                                  className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                />
                                <button
                                  type="button"
                                  disabled={
                                    trackerNewTagSaving || !trackerNewTagName.trim()
                                  }
                                  onClick={async () => {
                                    if (!trackerNewTagName.trim()) return;
                                    await createProjectAdminTagForTask(
                                      entry.taskId,
                                      entry.projectId,
                                      trackerNewTagName
                                    );
                                  }}
                                  className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                >
                                  {trackerNewTagSaving ? '…' : 'Add'}
                                </button>
                              </div>
                              <p className="px-1 text-[10px] text-gray-400">
                                Team admins can create tags for the shared catalog.
                              </p>
                            </div>
                          ) : (
                            <p className="text-[10px] text-gray-400">
                              Ask a Team admin to create a new tag for the shared
                              catalog.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {trackerEntryMenuId === entry.id && (
                      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-2">
                        <button
                          type="button"
                          onClick={() => beginEditTimeEntry(entry)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold text-purple-700 bg-white border border-purple-200 hover:bg-purple-50"
                        >
                          Edit task / time
                        </button>
                        <button
                          type="button"
                          onClick={() => duplicateTaskFromEntry(entry)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleTaskFavoriteFromEntry(entry)}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                        >
                          {entry.isFavorite
                            ? 'Remove favorite'
                            : 'Add as favorite'}
                        </button>
                        {!entry.isRunning && (
                          <button
                            type="button"
                            onClick={() => deleteTimeEntry(entry)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 bg-white border border-red-200 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        )}
                        {entry.isRunning && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setTrackerEntryMenuId(null);
                                toggleTimer(entry.taskId, entry.taskName);
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-700 bg-white border border-amber-200 hover:bg-amber-50"
                            >
                              Pause
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setTrackerEntryMenuId(null);
                                saveTimer(entry.taskId);
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-green-700 bg-white border border-green-200 hover:bg-green-50"
                            >
                              Save
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const clientsScreen = (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-purple-600">Clients</p>
          <h1 className="text-2xl font-black text-gray-900 mt-1">Client labels</h1>
          <p className="text-sm text-gray-500 mt-1">
            Admins can add, rename, archive, or permanently delete client labels.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentUser.role === 'admin' && (
            <button
              type="button"
              onClick={() => setShowArchivedClients((value) => !value)}
              className="text-xs font-bold text-gray-600 hover:text-purple-800 bg-white border border-gray-200 px-3 py-2 rounded-xl"
            >
              {showArchivedClients ? 'Hide archived' : 'Show archived'}
            </button>
          )}
          <span className="text-xs font-bold text-purple-700 bg-purple-50 border border-purple-100 px-3 py-2 rounded-xl">
            {selectedClientIds.length} selected
          </span>
        </div>
      </div>

      {currentUser.role === 'admin' ? (
        <form onSubmit={handleCreateClient} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-bold text-gray-900">Add client</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              required
              value={newClientName}
              onChange={(event) => setNewClientName(event.target.value)}
              placeholder="Client name"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold">
              Add client
            </button>
          </div>
          {clientInviteStatus && <p className="text-xs text-purple-700 font-medium">{clientInviteStatus}</p>}
        </form>
      ) : (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 text-sm font-medium">
          Only Team administrators can add, rename, archive, or delete clients.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_auto] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-[10px] font-black uppercase tracking-wider text-gray-400">
          <span>Select</span>
          <span>Client name</span>
          <span>Actions</span>
        </div>
        {clientsLoading ? (
          <div className="p-10 text-center text-sm text-gray-400">Loading clients…</div>
        ) : visibleClients.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {showArchivedClients ? 'No archived clients found.' : 'No clients found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleClients.map((client) => {
              const selected = selectedClientIds.includes(client.id);
              const isEditing = editingClientId === client.id;
              const isArchived = Boolean(client.archivedAt);
              return (
                <div
                  key={client.id}
                  className={`grid grid-cols-[auto_1fr_auto] gap-4 items-center px-5 py-4 ${
                    selected ? 'bg-purple-50' : 'hover:bg-gray-50'
                  } ${isArchived ? 'opacity-75' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleClientSelection(client.id)}
                    className="h-4 w-4 accent-purple-600"
                    aria-label={`Select ${client.name}`}
                    disabled={isArchived}
                  />
                  <div className="min-w-0">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editClientName}
                        onChange={(event) => setEditClientName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleRenameClient(client.id);
                          }
                          if (event.key === 'Escape') {
                            setEditingClientId(null);
                            setEditClientName('');
                          }
                        }}
                        className="w-full px-3 py-1.5 border border-purple-300 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="text-sm font-bold text-gray-900 truncate"
                          title={hoverFullText(client.name)}
                        >
                          {client.name}
                        </span>
                        {isArchived && (
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-lg">
                            Archived
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {currentUser.role === 'admin' ? (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleRenameClient(client.id)}
                            className="text-xs font-bold text-green-700 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingClientId(null);
                              setEditClientName('');
                            }}
                            className="text-xs font-bold text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {!isArchived && (
                            <button
                              type="button"
                              onClick={() => beginRenameClient(client)}
                              className="text-xs font-bold text-purple-700 hover:text-purple-900"
                            >
                              Rename
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleArchiveClient(client.id, !isArchived)}
                            className="text-xs font-bold text-amber-700 hover:text-amber-900"
                          >
                            {isArchived ? 'Restore' : 'Archive'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClient(client.id)}
                            className="text-xs font-bold text-red-500 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className="text-[10px] font-bold uppercase text-purple-700 bg-purple-100 px-2 py-1 rounded-lg">
                      Client
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const teamScreen = (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-purple-600">Team</p>
        <h1 className="text-2xl font-black text-gray-900 mt-1">Portal colleagues</h1>
        <p className="text-sm text-gray-500 mt-1">
          Invite, edit, or remove registered colleagues.
        </p>
      </div>

      {currentUser.role === 'admin' ? (
        <form onSubmit={handleInviteColleague} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <label htmlFor="invite-name" className="text-xs font-black uppercase tracking-wide text-gray-500">
            Add team user
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 mt-2">
            <input
              id="invite-name"
              type="text"
              required
              value={inviteName}
              onChange={(event) => setInviteName(event.target.value)}
              placeholder="Full name"
              className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <input
              id="invite-email"
              type="email"
              required
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="colleague@company.com"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <select
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={teamSaving}
              className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60"
            >
              Invite
            </button>
          </div>
          {inviteStatus && (
            <p
              className={`text-xs font-medium mt-2 ${
                /fail|error|not available|could not/i.test(inviteStatus)
                  ? 'text-red-600'
                  : 'text-purple-700'
              }`}
            >
              {inviteStatus}
            </p>
          )}
        </form>
      ) : (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 text-sm font-medium">
          Only administrators can invite or manage colleagues.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {teamMembers
          .filter((member) => member.role !== 'client')
          .map((member) => {
            const isEditing = editingTeamMemberId === member.id;
            const isProtectedOwner =
              isTeamAdminOwner(member) && !isTeamAdminOwner(currentUser);
            const canEditMember =
              currentUser.role === 'admin' && !isProtectedOwner;
            const canDeleteMember =
              currentUser.role === 'admin' &&
              member.id !== currentUser.id &&
              !isProtectedOwner;
            return (
              <div
                key={member.id}
                className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-black shrink-0">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    {isEditing && canEditMember ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editTeamMemberName}
                          onChange={(e) => setEditTeamMemberName(e.target.value)}
                          className="w-full px-3 py-1.5 border border-purple-300 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <select
                          value={editTeamMemberRole}
                          onChange={(e) => setEditTeamMemberRole(e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="employee">Employee</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    ) : (
                      <>
                        <p
                          className="text-sm font-bold text-gray-900 truncate"
                          title={hoverFullText(
                            `${member.name}${member.id === currentUser.id ? ' (You)' : ''}`
                          )}
                        >
                          {member.name}
                          {member.id === currentUser.id ? ' (You)' : ''}
                        </p>
                        <p
                          className="text-xs text-gray-500 truncate"
                          title={hoverFullText(member.email)}
                        >
                          {member.email}
                        </p>
                      </>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className="text-[10px] font-bold uppercase text-purple-700">
                        {member.role}
                      </span>
                      {isTeamAdminOwner(member) && (
                        <span className="text-[9px] font-black uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                          Owner
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {currentUser.role === 'admin' && (
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
                    {isProtectedOwner ? (
                      <p className="text-[11px] text-gray-400 font-medium">
                        Admin Owner is protected — other Team admins cannot edit or remove this account.
                      </p>
                    ) : isEditing ? (
                      <>
                        <button
                          type="button"
                          disabled={teamSaving}
                          onClick={() => handleUpdateTeamMember(member.id)}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                        >
                          {teamSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          disabled={teamSaving}
                          onClick={() => setEditingTeamMemberId(null)}
                          className="text-xs font-bold px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {canEditMember && (
                          <button
                            type="button"
                            disabled={teamSaving}
                            onClick={() => {
                              setEditingTeamMemberId(member.id);
                              setEditTeamMemberName(member.name);
                              setEditTeamMemberRole(
                                member.role === 'admin' ? 'admin' : 'employee'
                              );
                              setInviteStatus('');
                              clearUserAlert();
                            }}
                            className="text-xs font-bold px-3 py-1.5 rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100"
                          >
                            Edit
                          </button>
                        )}
                        {canDeleteMember && (
                          <button
                            type="button"
                            disabled={teamSaving}
                            onClick={() => handleDeleteTeamMember(member)}
                            className="text-xs font-bold px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );

  const settingsScreen = (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-purple-600">Settings</p>
        <h1 className="text-2xl font-black text-gray-900 mt-1">Project preferences</h1>
        <p className="text-sm text-gray-500 mt-1">Available options are based on your permission level.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm divide-y divide-gray-100">
        <div className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-gray-900">Email notifications</p>
            <p className="text-xs text-gray-500 mt-1">
              Email me about project updates, task assignments, and changes on tasks I follow or am assigned to. Auth emails (invite, reset password, confirm signup) always send when configured in Supabase.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleEmailNotifications}
            className={`w-11 h-6 rounded-full p-1 transition-colors ${notificationsEnabled ? 'bg-purple-600' : 'bg-gray-300'}`}
            aria-pressed={notificationsEnabled}
          >
            <span className={`block h-4 w-4 bg-white rounded-full transition-transform ${notificationsEnabled ? 'translate-x-5' : ''}`} />
          </button>
        </div>
        <div className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-gray-900">Week starts on Monday</p>
            <p className="text-xs text-gray-500 mt-1">Used for timesheets and weekly summaries.</p>
          </div>
          <button
            type="button"
            onClick={() => setWeekStartsMonday((enabled) => !enabled)}
            className={`w-11 h-6 rounded-full p-1 transition-colors ${weekStartsMonday ? 'bg-purple-600' : 'bg-gray-300'}`}
            aria-pressed={weekStartsMonday}
          >
            <span className={`block h-4 w-4 bg-white rounded-full transition-transform ${weekStartsMonday ? 'translate-x-5' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-900">Permission settings</h2>
            <p className="text-xs text-gray-500 mt-1">Signed in as {currentUser.role}.</p>
          </div>
          <span className="text-[10px] font-black uppercase bg-purple-100 text-purple-700 px-2 py-1 rounded-lg">{currentUser.role}</span>
        </div>
        {currentUser.role === 'admin' ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="border border-green-200 bg-green-50 text-green-800 rounded-xl p-3 font-medium">Manage projects and clients</div>
            <div className="border border-green-200 bg-green-50 text-green-800 rounded-xl p-3 font-medium">Invite and manage team members</div>
            <div className="border border-green-200 bg-green-50 text-green-800 rounded-xl p-3 font-medium">View all project time</div>
            <div className="border border-green-200 bg-green-50 text-green-800 rounded-xl p-3 font-medium">Configure project access</div>
          </div>
        ) : (
          <div className="mt-4 border border-gray-200 bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
            {currentUser.role === 'client'
              ? 'Client access is limited to permitted portal features. Administrative settings are locked.'
              : 'Employee access includes assigned projects, tasks, and personal time tracking. Administrative settings are locked.'}
          </div>
        )}
      </div>
    </div>
  );

  const showActivityFeed = activeMenu === 'projects' && Boolean(activeProjectId);

  const activityHasLinkedTask = (activity) => {
    if (!activity) return false;
    if (activity.taskId || activity.taskName) return true;
    return Boolean(resolveActivityTaskName(activity));
  };

  const activityHasFiles = (activity) =>
    Boolean(
      (activity?.attachments || []).length > 0 ||
        (activity?.replies || []).some(
          (reply) => (reply.attachments || []).length > 0
        )
    );

  const filteredFeedActivities = (() => {
    let list = [...(activities || [])];
    if (activityFeedFilterTask) {
      list = list.filter(
        (act) =>
          activityHasLinkedTask(act) ||
          (act.replies || []).some((reply) => activityHasLinkedTask(reply))
      );
    }
    if (activityFeedFilterFiles) {
      list = list.filter((act) => activityHasFiles(act));
    }
    list.sort((a, b) => {
      const aTime = a.createdAt?.getTime?.() || 0;
      const bTime = b.createdAt?.getTime?.() || 0;
      return activityFeedSort === 'oldest' ? aTime - bTime : bTime - aTime;
    });
    return list;
  })();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans flex">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Side menu */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 ${
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        } bg-white border-r border-gray-200 flex flex-col shrink-0 transform transition-all duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className={`px-5 py-5 border-b border-gray-100 ${sidebarCollapsed ? 'lg:px-3' : ''}`}>
          <img
            src="/tikix-logo.png"
            alt="Tikix"
            className={`w-24 h-auto ${sidebarCollapsed ? 'lg:hidden' : ''}`}
          />
          <div className={`hidden ${sidebarCollapsed ? 'lg:flex' : ''} w-12 h-12 mx-auto rounded-xl bg-purple-600 text-white items-center justify-center text-xl font-black`}>
            T
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <button type="button" onClick={() => openMenu('time-tracker')} className={navItemClass(activeMenu === 'time-tracker')} title="Time Tracker">
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Time Tracker</span>
          </button>

          {currentUser.role === 'admin' && (
            <button
              type="button"
              onClick={() => openMenu('dashboard')}
              className={navItemClass(activeMenu === 'dashboard')}
              title="Dashboard"
            >
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Dashboard</span>
            </button>
          )}

          <button type="button" onClick={goToOverview} className={navItemClass(activeMenu === 'projects')} title="Projects">
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Projects</span>
          </button>

          {isSystemAdmin && (
            <button type="button" onClick={() => openMenu('clients')} className={navItemClass(activeMenu === 'clients')} title="Clients">
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.36-1.86M17 20H7m10 0v-2c0-.66-.13-1.28-.36-1.86M7 20H2v-2a3 3 0 015.36-1.86M7 20v-2c0-.66.13-1.28.36-1.86m0 0a5 5 0 019.28 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Clients</span>
            </button>
          )}

          {isSystemAdmin && (
            <button type="button" onClick={() => openMenu('team')} className={navItemClass(activeMenu === 'team')} title="Team">
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.35a4 4 0 110 7.3 4 4 0 010-7.3zM5.5 20a6.5 6.5 0 0113 0M19 8v6m3-3h-6" />
              </svg>
              <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Team</span>
            </button>
          )}

          <button type="button" onClick={() => openMenu('settings')} className={navItemClass(activeMenu === 'settings')} title="Settings">
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.33 4.32c.43-1.77 2.95-1.77 3.38 0a1.74 1.74 0 002.6 1.08c1.56-.95 3.34.83 2.39 2.39a1.74 1.74 0 001.08 2.6c1.77.43 1.77 2.95 0 3.38a1.74 1.74 0 00-1.08 2.6c.95 1.56-.83 3.34-2.39 2.39a1.74 1.74 0 00-2.6 1.08c-.43 1.77-2.95 1.77-3.38 0a1.74 1.74 0 00-2.6-1.08c-1.56.95-3.34-.83-2.39-2.39a1.74 1.74 0 00-1.08-2.6c-1.77-.43-1.77-2.95 0-3.38a1.74 1.74 0 001.08-2.6c-.95-1.56.83-3.34 2.39-2.39a1.74 1.74 0 002.6-1.08z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={sidebarCollapsed ? 'lg:hidden' : ''}>Settings</span>
          </button>

        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
      {/* Top Main Application Control Menu Banner */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm sticky top-0 z-30">
        <div className="flex items-center space-x-3 shrink-0">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            className="hidden lg:block p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            aria-label={sidebarCollapsed ? 'Expand side menu' : 'Collapse side menu'}
            title={sidebarCollapsed ? 'Expand side menu' : 'Collapse side menu'}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="hidden sm:inline text-purple-600 font-bold text-sm md:text-lg">
            PMS Tracker
          </span>
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative">
            <div
              className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 sm:px-3 ${
                activeTimerTask
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              {activeTimerTask ? (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                  <span
                    className="hidden md:inline text-xs font-bold text-gray-700 truncate max-w-[140px]"
                    title={hoverFullText(activeTimerTask.name)}
                  >
                    {activeTimerTask.name}
                  </span>
                  <span className="font-mono text-sm font-black text-purple-900 tabular-nums">
                    {formatTime(activeSessionSeconds)}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleTimer(activeTimerTask.id, activeTimerTask.name)}
                    className="text-xs font-bold text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    Pause
                  </button>
                  <button
                    type="button"
                    onClick={() => saveTimer(activeTimerTask.id)}
                    className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => resetTimer(activeTimerTask.id, activeTimerTask.name)}
                    className="text-xs font-bold text-gray-600 hover:text-gray-900 bg-white border border-gray-200 px-2.5 py-1 rounded-lg transition-colors"
                  >
                    Reset
                  </button>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden sm:inline text-xs font-bold text-gray-500">No timer</span>
                </>
              )}
              <button
                type="button"
                onClick={() => setTimerMenuOpen((open) => !open)}
                className="text-xs font-bold text-purple-700 hover:text-purple-900 bg-white border border-gray-200 hover:border-purple-300 px-2.5 py-1 rounded-lg transition-colors"
              >
                {timerMenuOpen ? 'Close' : 'Tasks'}
              </button>
            </div>

            {timerMenuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close task picker"
                  className="fixed inset-0 z-40"
                  onClick={() => setTimerMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-black uppercase tracking-wider text-gray-500">Quick timer</p>
                    <p className="text-sm font-bold text-gray-900 mt-1">Start tracking a task</p>
                  </div>
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex flex-col gap-2">
                      <select
                        value={selectedQuickTaskId}
                        onChange={(event) => setSelectedQuickTaskId(event.target.value)}
                        className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select a task...</option>
                        {quickTaskOptions.map((task) => (
                          <option key={task.id} value={task.id}>
                            {task.menuLabel}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!selectedQuickTaskId}
                        onClick={() => startQuickTimer(selectedQuickTaskId)}
                        className="w-full shrink-0 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-bold"
                      >
                        Start
                      </button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {quickTaskOptions.length === 0 ? (
                      <div className="p-4 text-xs text-gray-600 space-y-2">
                        <p className="font-bold text-gray-800">No tasks available</p>
                        <p>
                          Create or join a project first, then create/get assigned a task before starting a timer.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setTimerMenuOpen(false);
                            openMenu('projects');
                          }}
                          className="text-purple-700 font-bold hover:underline"
                        >
                          Go to Projects →
                        </button>
                      </div>
                    ) : (
                      quickTaskOptions.map((task) => (
                        <button
                          key={task.id}
                          type="button"
                          onClick={() => {
                            if (task.isRunning) {
                              toggleTimer(task.id, task.name);
                            } else {
                              startQuickTimer(task.id);
                            }
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            task.isRunning ? 'bg-green-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p
                                className="text-sm font-bold text-gray-900 truncate"
                                title={hoverFullText(task.name)}
                              >
                                {task.name}
                              </p>
                              <p
                                className="text-xs text-purple-600 truncate"
                                title={hoverFullText(
                                  `${task.projectName} · Client: ${task.clientLabel}`
                                )}
                              >
                                {task.projectName} · Client: {task.clientLabel}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-mono text-xs font-bold text-gray-800">{formatTime(getTaskLiveSeconds(task))}</p>
                              {task.isRunning && (
                                <p className="text-[10px] font-bold uppercase text-green-600">
                                  Running
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {activeMenu === 'projects' && !activeProjectId && (
            <div className="flex items-center gap-2">
              <div className="bg-gray-100 p-1 rounded-xl border border-gray-200 flex space-x-1">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'grid' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  Grid Card View
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'list' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  List Table View
                </button>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setProjectViewHelpOpen((open) => !open)}
                  onBlur={() => {
                    // Delay so clicks inside the panel can register on mouse devices.
                    window.setTimeout(() => setProjectViewHelpOpen(false), 150);
                  }}
                  className={`h-8 w-8 rounded-full border text-sm font-black transition-colors ${
                    projectViewHelpOpen
                      ? 'border-purple-400 bg-purple-100 text-purple-800'
                      : 'border-gray-200 bg-white text-gray-500 hover:border-purple-300 hover:text-purple-700'
                  }`}
                  title="Help: how to configure projects"
                  aria-label="Help: how to configure projects"
                  aria-expanded={projectViewHelpOpen}
                >
                  ?
                </button>
                {projectViewHelpOpen && (
                  <div
                    role="tooltip"
                    className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-purple-100 bg-white p-3 shadow-lg text-left"
                  >
                    <p className="text-[10px] font-black uppercase tracking-wider text-purple-600 mb-1.5">
                      Configure projects
                    </p>
                    <ul className="space-y-1.5 text-[11px] text-gray-600 leading-snug">
                      <li>
                        <span className="font-bold text-gray-800">Grid Card View:</span>{' '}
                        click Configure, or click Staff Members / Clients / Service Tags
                        on a card to edit inline.
                      </li>
                      <li>
                        <span className="font-bold text-gray-800">List Table View:</span>{' '}
                        switch here for a spreadsheet-style layout—often easier for
                        project admins to edit many fields at once.
                      </li>
                      <li>
                        <span className="font-bold text-gray-800">Open Project:</span>{' '}
                        open the project workspace for full task, member, and activity
                        configuration.
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-xl border border-gray-200 min-w-0 max-w-[min(100%,22rem)]">
            <span className="text-xs font-bold text-gray-400 shrink-0">SIGNED IN:</span>
            <span
              className="text-sm font-bold text-purple-900 truncate"
              title={hoverFullText(
                `${currentUser.name} (${
                  currentUser.role === 'admin'
                    ? 'Admin'
                    : currentUser.role === 'client'
                      ? 'Client'
                      : 'Employee'
                })`
              )}
            >
              {currentUser.name}{' '}
              <span className="text-purple-600 font-medium">
                ({currentUser.role === 'admin' ? 'Admin' : currentUser.role === 'client' ? 'Client' : 'Employee'})
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="text-xs font-bold text-gray-500 hover:text-red-600 border border-gray-200 bg-white px-3 py-1.5 rounded-xl transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Primary Container Frame Project */}
      <main className={`flex-1 max-w-7xl w-full mx-auto p-4 md:p-6 grid grid-cols-1 gap-6 ${showActivityFeed ? 'lg:grid-cols-3' : ''}`}>
        
        <section className={`space-y-6 min-w-0 overflow-hidden ${showActivityFeed ? 'lg:col-span-2' : ''}`}>
          {userAlert && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <p className="font-bold text-red-900">{userAlert.title}</p>
                {userAlert.message && (
                  <p className="font-medium text-red-800">{userAlert.message}</p>
                )}
                {userAlert.steps?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-1">
                      What to do
                    </p>
                    <ol className="list-decimal list-inside space-y-1 text-red-800">
                      {userAlert.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={clearUserAlert}
                aria-label="Dismiss error"
                className="shrink-0 text-red-500 hover:text-red-800 font-bold leading-none px-1"
              >
                ×
              </button>
            </div>
          )}
          {activeMenu === 'time-tracker' && timeTrackerScreen}
          {activeMenu === 'dashboard' && dashboardScreen}
          {activeMenu === 'clients' && clientsScreen}
          {activeMenu === 'team' && teamScreen}
          {activeMenu === 'settings' && settingsScreen}

          {activeMenu === 'projects' && (
            <>
          {activeProjectId && (
            <button onClick={goToOverview} className="flex items-center text-sm font-bold text-purple-700 hover:text-purple-900 transition-colors">
              ← Back to Project Overview
            </button>
          )}

          {/* VIEW ONE: Overview Dashboard */}
          {!activeProjectId ? (
            <div className="space-y-6">
              {canCreateProjects ? (
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <h2 className="text-lg font-bold text-gray-900 mb-1">
                    Create New Project Space
                  </h2>
                  <p className="text-xs text-gray-500 mb-4">
                    You become Project Admin of projects you create. Team / app-wide
                    admin stays separate in the Team section.
                  </p>
                  <form onSubmit={handleCreateProject} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          list="project-category-suggestions"
                          placeholder="Project name (e.g. Social Media, CRM…)"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                        />
                        <datalist id="project-category-suggestions">
                          {PROJECT_CATEGORY_SUGGESTIONS.map((category) => (
                            <option key={category} value={category} />
                          ))}
                        </datalist>
                        <p className="mt-1 text-[10px] text-gray-400">
                          Tip: Categories from the Master Task Matrix appear as suggestions.
                        </p>
                      </div>
                      <input
                        type="text"
                        placeholder="Description narrative..."
                        value={newProjectDesc}
                        onChange={(e) => setNewProjectDesc(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 relative">
                        <label className="text-xs font-black uppercase tracking-wide text-gray-500">
                          Client (required)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setProjectServiceMenuOpen(false);
                            setProjectClientMenuOpen((open) => !open);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm bg-white text-left flex items-center justify-between gap-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                          aria-expanded={projectClientMenuOpen}
                        >
                          <span
                            className={`truncate ${
                              (newProjectClientMode === 'select' && newProjectClientId) ||
                              (newProjectClientMode === 'create' && newProjectClientName.trim())
                                ? 'text-gray-800'
                                : 'text-gray-400'
                            }`}
                          >
                            {newProjectClientMode === 'create' && newProjectClientName.trim()
                              ? `New: ${newProjectClientName.trim()}`
                              : activeClients.find((c) => c.id === newProjectClientId)?.name ||
                                'Select a client…'}
                          </span>
                          <svg
                            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${
                              projectClientMenuOpen ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {projectClientMenuOpen && (
                          <div className="absolute z-30 left-0 right-0 top-full mt-1 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="max-h-48 overflow-y-auto p-2 space-y-0.5">
                              {activeClients.length === 0 ? (
                                <p className="px-2 py-2 text-xs text-amber-700 font-medium">
                                  No clients yet. Create one below.
                                </p>
                              ) : (
                                activeClients.map((client) => {
                                  const isSelected =
                                    newProjectClientMode === 'select' &&
                                    newProjectClientId === client.id;
                                  return (
                                    <button
                                      key={client.id}
                                      type="button"
                                      onClick={() => {
                                        setNewProjectClientMode('select');
                                        setNewProjectClientId(client.id);
                                        setNewProjectClientName('');
                                        setProjectClientMenuOpen(false);
                                      }}
                                      className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
                                        isSelected
                                          ? 'bg-purple-50 text-purple-800'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                    >
                                      <span
                                        className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center text-[9px] font-bold ${
                                          isSelected
                                            ? 'bg-purple-600 border-purple-600 text-white'
                                            : 'border-gray-300 text-transparent'
                                        }`}
                                      >
                                        ✓
                                      </span>
                                      <span className="truncate">{client.name}</span>
                                    </button>
                                  );
                                })
                              )}
                            </div>

                            <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                              {isSystemAdmin ? (
                                <>
                                  <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                    Create a new client
                                  </p>
                                  <div className="flex gap-1.5">
                                    <input
                                      type="text"
                                      value={
                                        newProjectClientMode === 'create'
                                          ? newProjectClientName
                                          : ''
                                      }
                                      onChange={(e) => {
                                        setNewProjectClientMode('create');
                                        setNewProjectClientId('');
                                        setNewProjectClientName(e.target.value);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          if (newProjectClientName.trim()) {
                                            setNewProjectClientMode('create');
                                            setNewProjectClientId('');
                                            setProjectClientMenuOpen(false);
                                          }
                                        }
                                      }}
                                      placeholder="New client name…"
                                      className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!newProjectClientName.trim()) return;
                                        setNewProjectClientMode('create');
                                        setNewProjectClientId('');
                                        setProjectClientMenuOpen(false);
                                      }}
                                      disabled={!newProjectClientName.trim()}
                                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 disabled:opacity-50"
                                    >
                                      Add
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <p className="px-1 text-[11px] text-gray-500 font-medium">
                                  Only Team admins can create clients. Select an
                                  existing client above.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 relative">
                        <label className="text-xs font-black uppercase tracking-wide text-gray-500">
                          Service tags (optional)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setProjectClientMenuOpen(false);
                            setProjectServiceMenuOpen((open) => !open);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-xl text-sm bg-white text-left flex items-center justify-between gap-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                          aria-expanded={projectServiceMenuOpen}
                        >
                          <span
                            className={`truncate ${
                              selectedProjectServices.length
                                ? 'text-gray-800'
                                : 'text-gray-400'
                            }`}
                          >
                            {selectedProjectServices.length === 0
                              ? 'Select service tags…'
                              : selectedProjectServices.length <= 2
                                ? selectedProjectServices.join(', ')
                                : `${selectedProjectServices.length} services selected`}
                          </span>
                          <svg
                            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${
                              projectServiceMenuOpen ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {projectServiceMenuOpen && (
                          <div className="absolute z-30 left-0 right-0 top-full mt-1 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="max-h-48 overflow-y-auto p-2 space-y-2">
                              {MASTER_TASK_MATRIX.map((group) => (
                                <div key={group.category}>
                                  <p className="px-2 pt-1 text-[10px] font-black uppercase tracking-wider text-gray-600">
                                    {group.category}
                                  </p>
                                  {group.services.map((service) => {
                                    const isSelected =
                                      selectedProjectServices.includes(service);
                                    return (
                                      <button
                                        key={service}
                                        type="button"
                                        title={service}
                                        onClick={() =>
                                          toggleProjectServiceSelection(service)
                                        }
                                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 ${
                                          isSelected
                                            ? 'bg-purple-100 text-purple-950 hover:bg-purple-100 hover:text-purple-950'
                                            : 'text-gray-900 hover:bg-purple-50 hover:text-gray-900'
                                        }`}
                                      >
                                        <span
                                          className={`h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center text-[9px] font-bold ${
                                            isSelected
                                              ? 'bg-purple-600 border-purple-600 text-white'
                                              : 'border-gray-400 text-transparent bg-white'
                                          }`}
                                        >
                                          ✓
                                        </span>
                                        <span className="truncate">{service}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              ))}
                              {customProjectServices.length > 0 && (
                                <div>
                                  <p className="px-2 pt-1 text-[10px] font-black uppercase tracking-wider text-gray-600">
                                    Custom
                                  </p>
                                  {customProjectServices.map((service) => {
                                    const isSelected =
                                      selectedProjectServices.includes(service);
                                    return (
                                      <button
                                        key={`custom-${service}`}
                                        type="button"
                                        title={service}
                                        onClick={() =>
                                          toggleProjectServiceSelection(service)
                                        }
                                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 ${
                                          isSelected
                                            ? 'bg-purple-100 text-purple-950 hover:bg-purple-100 hover:text-purple-950'
                                            : 'text-gray-900 hover:bg-purple-50 hover:text-gray-900'
                                        }`}
                                      >
                                        <span
                                          className={`h-3.5 w-3.5 rounded border flex items-center justify-center text-[9px] font-bold ${
                                            isSelected
                                              ? 'bg-purple-600 border-purple-600 text-white'
                                              : 'border-gray-300 text-transparent'
                                          }`}
                                        >
                                          ✓
                                        </span>
                                        <span className="truncate">{service}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {selectedProjectServices.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedProjectServices([])}
                                  className="w-full text-left px-2 py-1.5 text-[11px] font-bold text-gray-500 hover:text-red-600 border-t border-gray-100 mt-1"
                                >
                                  Clear selection
                                </button>
                              )}
                            </div>

                            {isSystemAdmin && (
                            <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                              <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                Add New Tag
                              </p>
                              <div className="flex gap-1.5">
                                <input
                                  type="text"
                                  value={newProjectTagName}
                                  onChange={(e) =>
                                    setNewProjectTagName(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddCustomProjectTag(e);
                                    }
                                  }}
                                  placeholder="New tag name…"
                                  className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <button
                                  type="button"
                                  onClick={handleAddCustomProjectTag}
                                  disabled={!newProjectTagName.trim()}
                                  className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                              <p className="px-1 text-[10px] text-gray-400">
                                Team admins can add tags to the shared catalog.
                              </p>
                            </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={projectSaving}
                      className="bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-purple-700 shadow-sm disabled:opacity-60"
                    >
                      {projectSaving ? 'Saving…' : 'Create a Project'}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 text-sm text-purple-800 font-medium">
                  Project creation is not available for client accounts. Ask a
                  Team member to create a project and add you.
                </div>
              )}

              {projectsLoading ? (
                <div className="text-center p-12 bg-white rounded-2xl border border-gray-200 text-gray-500 text-sm font-medium">
                  Loading projects…
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center p-12 bg-white rounded-2xl border border-gray-200 text-gray-400 text-sm">
                  No projects yet. Create the first one above.
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-purple-100 bg-purple-50/70 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-xs text-purple-900 font-medium leading-relaxed">
                      <span className="font-black uppercase tracking-wide text-[10px] text-purple-600 mr-1.5">
                        Tip
                      </span>
                      Project admins can click fields on a card to configure, switch to{' '}
                      <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        className="font-bold text-purple-800 underline underline-offset-2 hover:text-purple-950"
                      >
                        List Table View
                      </button>{' '}
                      for easier bulk editing, or open a project for full workspace setup.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setViewMode((mode) =>
                          mode === 'grid' ? 'list' : 'grid'
                        )
                      }
                      className="shrink-0 self-start sm:self-auto text-[11px] font-bold px-3 py-1.5 rounded-lg bg-white border border-purple-200 text-purple-800 hover:bg-purple-100"
                    >
                      {viewMode === 'grid'
                        ? 'Switch to List Table View'
                        : 'Switch to Grid Card View'}
                    </button>
                  </div>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {projects.map((project) => {
                    const canManageThisProject = canManageProject(project.id);
                    const isConfiguring =
                      canManageThisProject &&
                      inlineEditProjectId === project.id;
                    return (
                    <div
                      key={project.id}
                      className={`bg-white p-6 rounded-2xl border shadow-sm flex flex-col justify-between hover:shadow-md transition-all ${
                        isConfiguring
                          ? 'border-purple-300 ring-1 ring-purple-100'
                          : 'border-gray-200'
                      }`}
                    >
                      <div>
                        <div className="flex justify-between items-start mb-2 gap-2">
                          {isConfiguring ? (
                            <input
                              type="text"
                              value={editProjectName}
                              onChange={(e) => setEditProjectName(e.target.value)}
                              className="border border-purple-300 px-2 py-1.5 text-sm font-bold rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Project title"
                            />
                          ) : (
                            <div className="flex items-center justify-between w-full gap-2 min-w-0">
                              <h3
                                className="text-xl font-bold text-purple-900 truncate"
                                title={hoverFullText(project.name)}
                              >
                                {project.name}
                              </h3>
                              <div className="flex items-center space-x-2 shrink-0">
                                {canManageThisProject && (
                                  <button
                                    type="button"
                                    onClick={() => beginInlineProjectEdit(project)}
                                    className="text-xs text-purple-700 hover:text-purple-900 font-semibold px-1.5 py-0.5 border border-purple-200 hover:bg-purple-50 rounded-md transition-all"
                                  >
                                    Configure
                                  </button>
                                )}
                                {canManageThisProject && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDeleteProject(project.id, project.name)
                                    }
                                    className="text-xs text-red-400 hover:text-red-600 font-semibold px-1.5 py-0.5 border border-transparent hover:border-gray-200 rounded-md transition-all"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {isConfiguring ? (
                          <textarea
                            value={editProjectDescription}
                            onChange={(e) =>
                              setEditProjectDescription(e.target.value)
                            }
                            rows={3}
                            className="w-full mb-4 px-2.5 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Project description"
                          />
                        ) : (
                          <button
                            type="button"
                            disabled={!canManageThisProject}
                            onClick={() => beginInlineProjectEdit(project)}
                            className={`w-full text-left text-sm text-gray-600 mb-4 line-clamp-2 rounded-lg -mx-1 px-1 py-0.5 transition-colors ${
                              canManageThisProject
                                ? 'hover:bg-purple-50/70 hover:text-purple-900 cursor-pointer'
                                : 'cursor-default'
                            }`}
                            title={hoverFullText(
                              project.description || 'No description provided.',
                              canManageThisProject
                                ? 'Click to edit description'
                                : null
                            )}
                          >
                            {project.description || 'No description provided.'}
                          </button>
                        )}

                        <div className="space-y-2.5 mb-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-400 font-bold shrink-0">
                              Staff Members:
                            </span>
                            {isConfiguring ? (
                              <div className="w-full space-y-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {(project.members || []).map((member) => (
                                    <span
                                      key={member.id}
                                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-purple-50 text-purple-800 border border-purple-100"
                                    >
                                      {member.name}
                                      <select
                                        value={member.role}
                                        onChange={(e) =>
                                          handleUpdateProjectMemberRole(
                                            project.id,
                                            member,
                                            e.target.value
                                          )
                                        }
                                        className="text-[9px] font-mono font-bold text-purple-700 bg-white border border-purple-100 rounded px-1 py-0.5"
                                        title="Change project permission"
                                      >
                                        <option value="Employee">Ordinary User</option>
                                        <option value="Admin">Project Admin</option>
                                      </select>
                                      {member.id !== currentUser.id && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveProjectMember(
                                              project.id,
                                              member
                                            )
                                          }
                                          className="text-purple-400 hover:text-red-600"
                                          title={`Remove ${member.name}`}
                                        >
                                          ×
                                        </button>
                                      )}
                                    </span>
                                  ))}
                                  {(project.members || []).length === 0 && (
                                    <span className="text-xs text-gray-400 font-medium">
                                      No staff yet
                                    </span>
                                  )}
                                </div>
                                {(() => {
                                  const memberIds = new Set(
                                    (project.members || []).map((m) => m.id)
                                  );
                                  const availableTeammates = teamMembers.filter(
                                    (member) =>
                                      member.role !== 'client' &&
                                      !memberIds.has(member.id)
                                  );
                                  return (
                                    <div className="flex flex-col sm:flex-row gap-1.5">
                                      <select
                                        value={inlineMemberUserId}
                                        onChange={(e) =>
                                          setInlineMemberUserId(e.target.value)
                                        }
                                        className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                      >
                                        <option value="">Add staff member…</option>
                                        {availableTeammates.map((member) => (
                                          <option key={member.id} value={member.id}>
                                            {member.name}
                                          </option>
                                        ))}
                                      </select>
                                      <select
                                        value={inlineMemberRole}
                                        onChange={(e) =>
                                          setInlineMemberRole(e.target.value)
                                        }
                                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                      >
                                        <option value="Employee">Ordinary User</option>
                                        <option value="Admin">Project Admin</option>
                                      </select>
                                      <button
                                        type="button"
                                        disabled={!inlineMemberUserId}
                                        onClick={() =>
                                          handleAddProjectMember(project.id)
                                        }
                                        className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-purple-600 text-white disabled:opacity-50"
                                      >
                                        Add
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className={`flex flex-wrap items-center gap-1.5 text-left rounded-lg -m-1 p-1 transition-colors ${
                                  canManageThisProject
                                    ? 'hover:bg-purple-50/70 cursor-pointer'
                                    : 'cursor-default'
                                }`}
                                title={
                                  canManageThisProject
                                    ? 'Click to manage staff members'
                                    : undefined
                                }
                              >
                                {(project.members || []).length > 0 ? (
                                  (project.members || []).map((member) => (
                                    <span
                                      key={member.id}
                                      className="bg-purple-50 text-purple-700 text-xs font-extrabold px-2 py-0.5 rounded-full border border-purple-100"
                                    >
                                      {member.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium hover:text-purple-700">
                                    {canManageThisProject
                                      ? 'Add staff members'
                                      : 'No staff yet'}
                                  </span>
                                )}
                              </button>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-400 font-bold shrink-0">
                              Clients:
                            </span>
                            {isConfiguring ? (
                              activeClients.length > 0 ? (
                                activeClients.map((client) => {
                                  const selected = (project.clients || []).some(
                                    (entry) => entry.id === client.id
                                  );
                                  return (
                                    <button
                                      key={client.id}
                                      type="button"
                                      onClick={() =>
                                        toggleProjectClient(project.id, client.id)
                                      }
                                      className={`text-xs font-extrabold px-2 py-0.5 rounded-full border transition-colors ${
                                        selected
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      {client.name}
                                    </button>
                                  );
                                })
                              ) : (
                                <span className="text-xs text-gray-400 font-medium">
                                  No clients configured
                                </span>
                              )
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className={`flex flex-wrap items-center gap-1.5 text-left rounded-lg -m-1 p-1 transition-colors ${
                                  canManageThisProject
                                    ? 'hover:bg-indigo-50/70 cursor-pointer'
                                    : 'cursor-default'
                                }`}
                                title={
                                  canManageThisProject
                                    ? 'Click to manage clients'
                                    : undefined
                                }
                              >
                                {(project.clients || []).length > 0 ? (
                                  (project.clients || []).map((client) => (
                                    <span
                                      key={client.id}
                                      className="bg-indigo-50 text-indigo-700 text-xs font-extrabold px-2 py-0.5 rounded-full border border-indigo-100"
                                    >
                                      {client.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium hover:text-purple-700">
                                    {canManageThisProject
                                      ? 'Assign clients'
                                      : 'No client'}
                                  </span>
                                )}
                              </button>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-400 font-bold shrink-0">
                              Service Tags:
                            </span>
                            {isConfiguring ? (
                              <>
                                {MASTER_TASK_MATRIX.flatMap((group) =>
                                  group.services
                                )
                                  .slice(0, 24)
                                  .map((serviceName) => {
                                    const selected = (project.tags || []).some(
                                      (tag) =>
                                        tag.name.toLowerCase() ===
                                        serviceName.toLowerCase()
                                    );
                                    return (
                                      <button
                                        key={serviceName}
                                        type="button"
                                        disabled={inlineProjectTagSaving}
                                        onClick={() =>
                                          toggleInlineProjectServiceTag(
                                            project.id,
                                            serviceName
                                          )
                                        }
                                        className={`text-xs font-extrabold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${
                                          selected
                                            ? 'bg-emerald-600 text-white border-emerald-600'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'
                                        }`}
                                      >
                                        {serviceName}
                                      </button>
                                    );
                                  })}
                                {(project.tags || [])
                                  .filter(
                                    (tag) =>
                                      !MASTER_TASK_MATRIX.some((group) =>
                                        group.services.some(
                                          (service) =>
                                            service.toLowerCase() ===
                                            tag.name.toLowerCase()
                                        )
                                      )
                                  )
                                  .map((tag) => (
                                    <button
                                      key={tag.id}
                                      type="button"
                                      disabled={inlineProjectTagSaving}
                                      onClick={() =>
                                        toggleInlineProjectServiceTag(
                                          project.id,
                                          tag.name
                                        )
                                      }
                                      className="bg-emerald-600 text-white text-xs font-extrabold px-2 py-0.5 rounded-full border border-emerald-600"
                                    >
                                      {tag.name} ×
                                    </button>
                                  ))}
                              </>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className={`flex flex-wrap items-center gap-1.5 text-left rounded-lg -m-1 p-1 transition-colors ${
                                  canManageThisProject
                                    ? 'hover:bg-emerald-50/70 cursor-pointer'
                                    : 'cursor-default'
                                }`}
                                title={
                                  canManageThisProject
                                    ? 'Click to manage service tags'
                                    : undefined
                                }
                              >
                                {(project.tags || []).length > 0 ? (
                                  (project.tags || []).map((tag) => (
                                    <span
                                      key={tag.id}
                                      className="bg-emerald-50 text-emerald-700 text-xs font-extrabold px-2 py-0.5 rounded-full border border-emerald-100"
                                    >
                                      {tag.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium hover:text-purple-700">
                                    {canManageThisProject ? 'Add tags' : 'None'}
                                  </span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {isConfiguring ? (
                        <div className="flex items-center gap-2 mt-auto">
                          <button
                            type="button"
                            onClick={async () => {
                              const renamed = await handleRenameProject(
                                project.id
                              );
                              if (renamed === false) return;
                              await handleUpdateProjectDescription(project.id);
                              cancelInlineProjectEdit();
                            }}
                            disabled={projectDetailsSavingId === project.id}
                            className="flex-1 text-center bg-purple-600 text-white py-2 rounded-xl text-sm font-bold hover:bg-purple-700 transition-colors disabled:opacity-60"
                          >
                            {projectDetailsSavingId === project.id
                              ? 'Saving…'
                              : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelInlineProjectEdit}
                            className="flex-1 text-center bg-gray-100 border border-gray-200 text-gray-700 py-2 rounded-xl text-sm font-bold hover:bg-gray-200 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openProject(project.id)}
                          className="w-full text-center bg-purple-50 border border-purple-200 text-purple-700 py-2 rounded-xl text-sm font-bold hover:bg-purple-100 transition-colors mt-auto"
                        >
                          Open Project
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Project Name</th>
                        <th className="p-4 hidden md:table-cell">Details / Summary</th>
                        <th className="p-4">Staff Members</th>
                        <th className="p-4">Clients</th>
                        <th className="p-4">Service Tags</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {projects.map((project) => {
                        const canManageThisProject = canManageProject(project.id);
                        const isInlineEditing =
                          canManageThisProject &&
                          inlineEditProjectId === project.id;
                        const memberIds = new Set(
                          (project.members || []).map((member) => member.id)
                        );
                        const availableTeammates = teamMembers.filter(
                          (member) =>
                            member.role !== 'client' && !memberIds.has(member.id)
                        );

                        return (
                        <tr key={project.id} className={`align-top ${isInlineEditing ? 'bg-purple-50/40' : 'hover:bg-gray-50/50'} transition-colors`}>
                          <td className="p-4 font-bold text-purple-950">
                            {editingProjectId === project.id ? (
                              <div className="flex items-center gap-1">
                                <input 
                                  type="text" 
                                  value={editProjectName} 
                                  onChange={(e) => setEditProjectName(e.target.value)}
                                  className="border border-purple-300 px-2 py-0.5 text-xs rounded-md focus:outline-none"
                                />
                                <button type="button" onClick={async () => {
                                  const ok = await handleRenameProject(project.id);
                                  if (ok) setEditingProjectId(null);
                                }} className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded font-bold">Save</button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span
                                  className="truncate max-w-[14rem]"
                                  title={hoverFullText(project.name)}
                                >
                                  {project.name}
                                </span>
                                {canManageThisProject && (
                                  <button type="button" onClick={() => { setEditingProjectId(project.id); setEditProjectName(project.name); }} className="text-[10px] text-gray-400 hover:text-purple-600 font-medium">Rename</button>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-gray-500 max-w-xs hidden md:table-cell">
                            {isInlineEditing ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editProjectDescription}
                                  onChange={(e) => setEditProjectDescription(e.target.value)}
                                  rows={3}
                                  placeholder="Add project details / summary..."
                                  className="w-full px-2 py-1.5 border border-purple-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    disabled={projectDetailsSavingId === project.id}
                                    onClick={() => handleUpdateProjectDescription(project.id)}
                                    className={`text-[10px] px-2 py-1 rounded-md font-bold transition-colors disabled:opacity-70 ${
                                      projectDetailsSavedId === project.id
                                        ? 'bg-emerald-700 text-white'
                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                    }`}
                                  >
                                    {projectDetailsSavingId === project.id
                                      ? 'Saving…'
                                      : projectDetailsSavedId === project.id
                                        ? 'Saved ✓'
                                        : 'Save details'}
                                  </button>
                                  {projectDetailsSavedId === project.id && (
                                    <span className="text-[10px] font-medium text-emerald-700">
                                      Details updated
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={cancelInlineProjectEdit}
                                    className="text-[10px] bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 px-2 py-1 rounded-md font-bold"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className={`text-left w-full ${canManageThisProject ? 'hover:text-purple-700' : ''}`}
                                title={canManageThisProject ? 'Click to edit details' : undefined}
                              >
                                <span className="line-clamp-3">{project.description || 'No description provided.'}</span>
                              </button>
                            )}
                            {(project.tags || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {(project.tags || []).map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100"
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-4 min-w-[14rem]">
                            {isInlineEditing ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {(project.members || []).map((member) => (
                                    <span
                                      key={member.id}
                                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-800"
                                    >
                                      {member.name}
                                      <select
                                        value={member.role}
                                        onChange={(e) =>
                                          handleUpdateProjectMemberRole(
                                            project.id,
                                            member,
                                            e.target.value
                                          )
                                        }
                                        className="text-[9px] font-mono font-bold text-purple-700 bg-purple-50 border border-purple-100 rounded px-1 py-0.5"
                                        title="Change project permission"
                                      >
                                        <option value="Employee">Ordinary User</option>
                                        <option value="Admin">Project Admin</option>
                                      </select>
                                      {member.id !== currentUser.id && (
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveProjectMember(project.id, member)}
                                          className="text-gray-400 hover:text-red-600"
                                          title={`Remove ${member.name}`}
                                        >
                                          ×
                                        </button>
                                      )}
                                    </span>
                                  ))}
                                  {(project.members || []).length === 0 && (
                                    <span className="text-xs text-gray-400">No staff yet</span>
                                  )}
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  <select
                                    value={inlineMemberUserId}
                                    onChange={(e) => setInlineMemberUserId(e.target.value)}
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  >
                                    <option value="">Add staff member…</option>
                                    {availableTeammates.map((member) => (
                                      <option key={member.id} value={member.id}>
                                        {member.name}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="flex gap-1.5">
                                    <select
                                      value={inlineMemberRole}
                                      onChange={(e) => setInlineMemberRole(e.target.value)}
                                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    >
                                      <option value="Employee">Ordinary User</option>
                                      <option value="Admin">Project Admin</option>
                                    </select>
                                    <button
                                      type="button"
                                      disabled={!inlineMemberUserId}
                                      onClick={() => handleAddProjectMember(project.id)}
                                      className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-purple-600 text-white disabled:opacity-50"
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className={`w-full text-left ${
                                  canManageThisProject
                                    ? 'hover:bg-purple-50/60 rounded-lg -m-1 p-1 transition-colors'
                                    : ''
                                }`}
                                title={
                                  canManageThisProject
                                    ? 'Click to edit staff members'
                                    : undefined
                                }
                              >
                                {(project.members || []).length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {(project.members || []).map((member) => (
                                      <span
                                        key={member.id}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg bg-purple-50 text-purple-800 border border-purple-100"
                                      >
                                        {member.name}
                                        <span className="text-[9px] font-mono font-medium text-purple-500">
                                          [{member.role === 'Admin' ? 'Project Admin' : 'Ordinary User'}]
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium">
                                    No staff yet
                                  </span>
                                )}
                              </button>
                            )}
                          </td>
                          <td className="p-4 min-w-[11rem]">
                            {isInlineEditing ? (
                              <div className="flex flex-wrap gap-1.5">
                                {activeClients.map((client) => {
                                  const selected = (project.clients || []).some(
                                    (entry) => entry.id === client.id
                                  );
                                  return (
                                    <button
                                      key={client.id}
                                      type="button"
                                      onClick={() =>
                                        toggleProjectClient(project.id, client.id)
                                      }
                                      className={`text-xs font-bold px-2 py-0.5 rounded-md border transition-colors ${
                                        selected
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                                      }`}
                                    >
                                      {client.name}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (project.clients || []).length > 0 ? (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className="flex flex-wrap gap-1 text-left"
                              >
                                {(project.clients || []).map((client) => (
                                  <span
                                    key={client.id}
                                    className="text-xs font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100"
                                  >
                                    {client.name}
                                  </span>
                                ))}
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className="text-xs text-gray-400 font-medium hover:text-purple-700 disabled:hover:text-gray-400"
                              >
                                {canManageThisProject ? 'Assign client' : '—'}
                              </button>
                            )}
                          </td>
                          <td className="p-4 min-w-[12rem]">
                            {isInlineEditing ? (
                              <div className="space-y-2 relative">
                                <div className="flex flex-wrap gap-1">
                                  {(project.tags || []).length > 0 ? (
                                    (project.tags || []).map((tag) => (
                                      <span
                                        key={tag.id}
                                        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md border border-emerald-100"
                                      >
                                        {tag.name}
                                        <button
                                          type="button"
                                          disabled={inlineProjectTagSaving}
                                          onClick={() =>
                                            toggleInlineProjectServiceTag(
                                              project.id,
                                              tag.name
                                            )
                                          }
                                          className="text-emerald-500 hover:text-red-600 disabled:opacity-50"
                                          aria-label={`Remove ${tag.name}`}
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-xs text-gray-400 font-medium">
                                      No service tags
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  disabled={inlineProjectTagSaving}
                                  onClick={() =>
                                    setInlineProjectServiceMenuOpen((open) => !open)
                                  }
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                  aria-expanded={inlineProjectServiceMenuOpen}
                                >
                                  <span className="truncate text-gray-500">
                                    Add service tags…
                                  </span>
                                  <svg
                                    className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${
                                      inlineProjectServiceMenuOpen ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>
                                {inlineProjectServiceMenuOpen && (
                                  <div className="absolute z-30 left-0 right-0 top-full mt-1 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                                    <div className="max-h-44 overflow-y-auto p-2 space-y-2 bg-white">
                                      {MASTER_TASK_MATRIX.map((group) => (
                                        <div key={group.category}>
                                          <p className="px-2 pt-1 text-[10px] font-black uppercase tracking-wider text-gray-600">
                                            {group.category}
                                          </p>
                                          {group.services.map((service) => {
                                            const isSelected = (project.tags || []).some(
                                              (tag) =>
                                                tag.name.toLowerCase() ===
                                                service.toLowerCase()
                                            );
                                            return (
                                              <button
                                                key={service}
                                                type="button"
                                                disabled={inlineProjectTagSaving}
                                                title={service}
                                                onClick={() =>
                                                  toggleInlineProjectServiceTag(
                                                    project.id,
                                                    service
                                                  )
                                                }
                                                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 disabled:opacity-50 ${
                                                  isSelected
                                                    ? 'bg-emerald-100 text-emerald-950 hover:bg-emerald-100 hover:text-emerald-950'
                                                    : 'text-gray-900 hover:bg-emerald-50 hover:text-gray-900'
                                                }`}
                                              >
                                                <span
                                                  className={`h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center text-[9px] font-bold ${
                                                    isSelected
                                                      ? 'bg-emerald-600 border-emerald-600 text-white'
                                                      : 'border-gray-400 text-transparent bg-white'
                                                  }`}
                                                >
                                                  ✓
                                                </span>
                                                <span className="truncate">{service}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      ))}
                                      {(project.tags || []).filter(
                                        (tag) =>
                                          !MASTER_TASK_MATRIX.some((group) =>
                                            group.services.some(
                                              (service) =>
                                                service.toLowerCase() ===
                                                tag.name.toLowerCase()
                                            )
                                          )
                                      ).length > 0 && (
                                        <div>
                                          <p className="px-2 pt-1 text-[10px] font-black uppercase tracking-wider text-gray-600">
                                            On this project
                                          </p>
                                          {(project.tags || [])
                                            .filter(
                                              (tag) =>
                                                !MASTER_TASK_MATRIX.some((group) =>
                                                  group.services.some(
                                                    (service) =>
                                                      service.toLowerCase() ===
                                                      tag.name.toLowerCase()
                                                  )
                                                )
                                            )
                                            .map((tag) => (
                                              <button
                                                key={`inline-custom-${tag.id}`}
                                                type="button"
                                                disabled={inlineProjectTagSaving}
                                                title={tag.name}
                                                onClick={() =>
                                                  toggleInlineProjectServiceTag(
                                                    project.id,
                                                    tag.name
                                                  )
                                                }
                                                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 bg-emerald-100 text-emerald-950 hover:bg-emerald-100 hover:text-emerald-950 disabled:opacity-50"
                                              >
                                                <span className="h-3.5 w-3.5 shrink-0 rounded border bg-emerald-600 border-emerald-600 text-white flex items-center justify-center text-[9px] font-bold">
                                                  ✓
                                                </span>
                                                <span className="truncate">{tag.name}</span>
                                              </button>
                                            ))}
                                        </div>
                                      )}
                                    </div>
                                    {isSystemAdmin ? (
                                    <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                                      <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                        Add New Tag
                                      </p>
                                      <form
                                        onSubmit={(event) =>
                                          handleAddInlineCustomProjectTag(
                                            project.id,
                                            event
                                          )
                                        }
                                        className="flex gap-1.5"
                                      >
                                        <input
                                          type="text"
                                          value={inlineProjectTagName}
                                          onChange={(e) =>
                                            setInlineProjectTagName(e.target.value)
                                          }
                                          placeholder="New tag name…"
                                          disabled={inlineProjectTagSaving}
                                          className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                        />
                                        <button
                                          type="submit"
                                          disabled={
                                            inlineProjectTagSaving ||
                                            !inlineProjectTagName.trim()
                                          }
                                          className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                        >
                                          Add
                                        </button>
                                      </form>
                                      <p className="px-1 text-[10px] text-gray-400">
                                        Team admins can create tags for the shared
                                        catalog.
                                      </p>
                                    </div>
                                    ) : (
                                      <div className="border-t border-gray-100 px-3 py-2">
                                        <p className="text-[10px] text-gray-400">
                                          Ask a Team admin to create a new tag for
                                          the shared catalog.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (project.tags || []).length > 0 ? (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className="flex flex-wrap gap-1 text-left"
                              >
                                {(project.tags || []).map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="text-xs font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md border border-emerald-100"
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={!canManageThisProject}
                                onClick={() => beginInlineProjectEdit(project)}
                                className="text-xs text-gray-400 font-medium hover:text-purple-700 disabled:hover:text-gray-400"
                              >
                                {canManageThisProject ? 'Add service tags' : '—'}
                              </button>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex flex-col items-end gap-1.5">
                              {canManageThisProject && (
                                isInlineEditing ? (
                                  <button
                                    type="button"
                                    onClick={cancelInlineProjectEdit}
                                    className="text-xs font-bold text-gray-600 hover:text-gray-900 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => beginInlineProjectEdit(project)}
                                    className="text-xs font-bold text-purple-700 hover:underline"
                                  >
                                    Inline edit
                                  </button>
                                )
                              )}
                              <div className="flex items-center justify-end space-x-2">
                                <button type="button" onClick={() => openProject(project.id)} className="text-xs bg-purple-600 text-white px-3 py-1 rounded-lg font-bold hover:bg-purple-700 transition-colors">
                                  Open Project
                                </button>
                                {canManageThisProject && (
                                  <button type="button" onClick={() => handleDeleteProject(project.id, project.name)} className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-lg font-bold hover:bg-red-100 transition-colors">
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
                </>
              )}
            </div>
          ) : (
            
            /* VIEW TWO: Opened Active Project Screen View */
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="relative">
                      {canManageActiveProject ? (
                        <button
                          type="button"
                          onClick={() =>
                            setProjectStatusMenuOpen((open) => !open)
                          }
                          className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border transition-colors ${statusBadgeClass(
                            (currentProject?.projectStatuses || []).find(
                              (item) =>
                                item.name ===
                                (currentProject?.status || 'Active')
                            )?.color || 'purple'
                          )} hover:ring-2 hover:ring-purple-200`}
                          title="Click to change project status"
                          aria-expanded={projectStatusMenuOpen}
                        >
                          {currentProject?.status || 'Active'}
                          <span className="text-[9px] opacity-70">▾</span>
                        </button>
                      ) : (
                        <span
                          className={`inline-flex items-center text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${statusBadgeClass(
                            (currentProject?.projectStatuses || []).find(
                              (item) =>
                                item.name ===
                                (currentProject?.status || 'Active')
                            )?.color || 'purple'
                          )}`}
                        >
                          {currentProject?.status || 'Active'}
                        </span>
                      )}
                      {canManageActiveProject && projectStatusMenuOpen && (
                        <div className="absolute left-0 top-full mt-1 z-40 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 space-y-1">
                          <p className="px-2 py-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                            Project status
                          </p>
                          {(currentProject?.projectStatuses || []).map(
                            (option) => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() =>
                                  handleUpdateProjectStatus(
                                    currentProject.id,
                                    option.name
                                  )
                                }
                                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold ${
                                  (currentProject?.status || 'Active') ===
                                  option.name
                                    ? 'bg-purple-50 text-purple-800'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {option.name}
                              </button>
                            )
                          )}
                          <div className="border-t border-gray-100 pt-2 mt-1 space-y-1.5">
                            <p className="px-2 text-[10px] font-black uppercase tracking-wider text-gray-400">
                              Add custom status
                            </p>
                            <div className="flex gap-1.5 px-1">
                              <input
                                type="text"
                                value={newProjectStatusName}
                                onChange={(e) =>
                                  setNewProjectStatusName(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddProjectStatusOption(
                                      currentProject.id,
                                      newProjectStatusName
                                    );
                                  }
                                }}
                                placeholder="e.g. In Review"
                                className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                              <button
                                type="button"
                                disabled={
                                  statusOptionSaving ||
                                  !newProjectStatusName.trim()
                                }
                                onClick={() =>
                                  handleAddProjectStatusOption(
                                    currentProject.id,
                                    newProjectStatusName
                                  )
                                }
                                className="shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-purple-600 text-white disabled:opacity-50"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setProjectStatusMenuOpen(false)}
                            className="w-full mt-1 px-2 py-1.5 text-[11px] font-bold text-gray-500 hover:bg-gray-50 rounded-lg"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                    {canManageActiveProject && editingProjectId !== currentProject?.id && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!currentProject) return;
                          setEditingProjectId(currentProject.id);
                          setEditProjectName(currentProject.name || '');
                          setEditProjectDescription(
                            currentProject.description === 'No description provided.'
                              ? ''
                              : currentProject.description || ''
                          );
                        }}
                        className="shrink-0 text-[11px] font-bold text-purple-700 hover:text-purple-900 border border-purple-200 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-lg transition-colors"
                      >
                        Edit details
                      </button>
                    )}
                  </div>

                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 shrink-0">
                        Clients
                      </span>
                      {(currentProject?.clients || []).length > 0 ? (
                        (currentProject.clients || []).map((client) => (
                          <span
                            key={client.id}
                            className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-extrabold px-2 py-0.5 rounded-full border border-indigo-100"
                          >
                            {client.name}
                            {canManageActiveProject && (
                              <button
                                type="button"
                                onClick={() =>
                                  toggleProjectClient(currentProject.id, client.id)
                                }
                                className="ml-0.5 text-indigo-400 hover:text-red-600 leading-none"
                                title={`Remove ${client.name}`}
                                aria-label={`Remove ${client.name}`}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">
                          No clients assigned
                        </span>
                      )}
                    </div>

                    {canManageActiveProject && (
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-2.5 space-y-1.5">
                        <p className="text-[10px] font-black uppercase tracking-wider text-indigo-700">
                          Assign clients to this project
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {activeClients.length > 0 ? (
                            activeClients.map((client) => {
                              const selected = (currentProject?.clients || []).some(
                                (entry) => entry.id === client.id
                              );
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() =>
                                    toggleProjectClient(currentProject.id, client.id)
                                  }
                                  className={`text-xs font-extrabold px-2 py-0.5 rounded-full border transition-colors ${
                                    selected
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                                  }`}
                                  title={
                                    selected
                                      ? 'Click to remove from this project'
                                      : 'Click to assign to this project'
                                  }
                                >
                                  {selected ? '✓ ' : '+ '}
                                  {client.name}
                                </button>
                              );
                            })
                          ) : (
                            <span className="text-xs text-gray-400 font-medium">
                              No clients configured — add them under Clients first
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {canManageActiveProject && editingProjectId === currentProject?.id ? (
                    <div className="mt-2 space-y-2">
                      <input
                        type="text"
                        value={editProjectName}
                        onChange={(e) => setEditProjectName(e.target.value)}
                        placeholder="Project name"
                        className="w-full px-3 py-2 border border-purple-300 rounded-xl text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <textarea
                        value={editProjectDescription}
                        onChange={(e) => setEditProjectDescription(e.target.value)}
                        placeholder="Add a project description…"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            if (!currentProject?.id) return;
                            if (
                              editProjectName.trim() &&
                              editProjectName.trim() !== currentProject.name
                            ) {
                              const renamed = await handleRenameProject(
                                currentProject.id
                              );
                              if (!renamed) return;
                            }
                            await handleUpdateProjectDescription(currentProject.id);
                            setEditingProjectId(null);
                            setEditProjectName('');
                            setEditProjectDescription('');
                          }}
                          disabled={projectDetailsSavingId === currentProject?.id}
                          className={`text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-70 ${
                            projectDetailsSavedId === currentProject?.id
                              ? 'bg-emerald-700 text-white'
                              : 'bg-purple-600 hover:bg-purple-700 text-white'
                          }`}
                        >
                          {projectDetailsSavingId === currentProject?.id
                            ? 'Saving…'
                            : projectDetailsSavedId === currentProject?.id
                              ? 'Saved ✓'
                              : 'Save details'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingProjectId(null);
                            setEditProjectName('');
                            setEditProjectDescription('');
                          }}
                          className="text-xs font-bold text-gray-500 hover:text-gray-800 px-2 py-1.5"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2
                        className="text-2xl font-black text-gray-900 mt-0.5"
                        title={hoverFullText(currentProject?.name)}
                      >
                        {currentProject?.name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">{currentProject?.description}</p>
                    </>
                  )}
                </div>

                {/* Team Roster List Control with Inline Eviction Cross Buttons */}
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h3 className="text-xs font-black text-gray-500 uppercase tracking-wider">
                        Project Members
                      </h3>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Project Admin can manage this project only — not Team or system settings.
                      </p>
                    </div>
                    {canManageActiveProject && (
                      <button
                        type="button"
                        onClick={() =>
                          setInviteMemberOpen((open) => {
                            if (open) {
                              setNewMemberName('');
                              setNewMemberRole('Employee');
                            }
                            return !open;
                          })
                        }
                        className="shrink-0 bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1 rounded-lg text-xs font-bold transition-colors"
                      >
                        {inviteMemberOpen ? 'Cancel' : 'Invite Another Member'}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {currentProject?.members.map((member) => (
                      <span
                        key={member.id}
                        className="bg-white border border-gray-200 px-2.5 py-1 rounded-lg text-xs font-bold text-purple-950 shadow-sm flex items-center gap-1.5"
                      >
                        <span>
                          👤 {member.name}
                          {member.id === currentUser.id ? ' (You)' : ''}
                        </span>
                        {canManageActiveProject ? (
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleUpdateProjectMemberRole(
                                activeProjectId,
                                member,
                                e.target.value
                              )
                            }
                            className="text-purple-700 font-mono text-[10px] bg-purple-50 border border-purple-100 rounded px-1 py-0.5 font-bold focus:outline-none focus:ring-1 focus:ring-purple-400"
                            title="Change project permission"
                          >
                            <option value="Employee">Ordinary User</option>
                            <option value="Admin">Project Admin</option>
                          </select>
                        ) : (
                          <span className="text-purple-600 font-medium font-mono text-[10px] bg-purple-50 px-1 py-0.5 rounded">
                            [{member.role === 'Admin' ? 'Project Admin' : 'Ordinary User'}]
                          </span>
                        )}
                        {canManageActiveProject && member.id !== currentUser.id && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member)}
                            className="text-gray-400 hover:text-red-500 font-extrabold px-0.5 transition-colors"
                            title="Remove member from project"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>

                  {canManageActiveProject && inviteMemberOpen && (
                    <form
                      onSubmit={handleInviteMember}
                      className="flex flex-col md:flex-row gap-2 pt-2 border-t border-gray-200/50"
                    >
                      <input
                        type="text"
                        placeholder="Type name to invite a member/employee..."
                        value={newMemberName}
                        onChange={(e) => setNewMemberName(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        autoFocus
                      />
                      <select
                        value={newMemberRole}
                        onChange={(e) => setNewMemberRole(e.target.value)}
                        className="px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        title="Permission on this project only"
                      >
                        <option value="Employee">Ordinary User Role</option>
                        <option value="Admin">Project Admin Role</option>
                      </select>
                      <button
                        type="submit"
                        className="bg-purple-600 text-white hover:bg-purple-700 px-4 py-1.5 rounded-lg text-xs font-bold transition-colors"
                      >
                        Invite
                      </button>
                    </form>
                  )}
                </div>

              </div>

              {/* ClickUp-style task list view */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-xs font-black text-gray-500 uppercase tracking-wider">
                      Add New Task
                    </h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      List view · {filteredTasks.length} task
                      {filteredTasks.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDetailedColumns((open) => !open)}
                      className={`h-8 px-2.5 rounded-lg border text-[11px] font-bold transition-colors ${
                        showDetailedColumns
                          ? 'border-purple-300 bg-purple-50 text-purple-800'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                      title="Show or hide Est. time, Start date, and Duration date"
                    >
                      {showDetailedColumns
                        ? 'Hide Time Details'
                        : 'Show Time Details'}
                    </button>
                  <div className="relative" ref={listColumnPickerRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setListRowMenuId(null);
                        setListColumnPickerOpen((open) => !open);
                      }}
                      className="h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 text-lg font-bold leading-none"
                      title={listColumnPickerOpen ? 'Close columns' : 'Customize columns'}
                      aria-expanded={listColumnPickerOpen}
                      aria-haspopup="menu"
                    >
                      {listColumnPickerOpen ? '×' : '+'}
                    </button>
                    {listColumnPickerOpen && (
                      <div
                        role="menu"
                        className="absolute right-0 top-full mt-1 z-40 w-52 rounded-xl border border-gray-200 bg-white shadow-lg p-2 space-y-1"
                      >
                        <div className="flex items-center justify-between gap-2 px-2 py-1">
                          <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                            Optional columns
                          </p>
                          <button
                            type="button"
                            onClick={() => setListColumnPickerOpen(false)}
                            className="text-gray-400 hover:text-gray-700 text-sm font-bold leading-none px-1"
                            aria-label="Close optional columns"
                          >
                            ×
                          </button>
                        </div>
                        {[
                          { key: 'tags', label: 'Service tags' },
                          { key: 'followers', label: 'Followers' },
                          { key: 'totalTrackedHours', label: 'Total Tracked Hours' },
                        ].map((col) => (
                          <button
                            key={col.key}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={listColumns[col.key]}
                            onClick={() => toggleListOptionalColumn(col.key)}
                            className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold ${
                              listColumns[col.key]
                                ? 'bg-purple-50 text-purple-800'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {listColumns[col.key] ? '✓ ' : '+ '}
                            {col.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setListColumnPickerOpen(false)}
                          className="w-full mt-1 px-2 py-1.5 rounded-lg text-[11px] font-bold text-purple-700 hover:bg-purple-50 border-t border-gray-100"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div
                    className="w-full min-w-0 grid items-center gap-x-2 px-3 py-2 border-b border-gray-100 bg-gray-50/80 text-[10px] font-black uppercase tracking-wider text-gray-400"
                    style={{ gridTemplateColumns: listGridTemplate }}
                  >
                    <span className="truncate">Task Title</span>
                    <span className="truncate" title="Default column">
                      Task Status
                    </span>
                    <span className="truncate">Assignee</span>
                    <span className="truncate">Hours</span>
                    {showDetailedColumns && (
                      <span className="truncate">Est. time</span>
                    )}
                    {showDetailedColumns && (
                      <span className="truncate">Start date</span>
                    )}
                    {showDetailedColumns && (
                      <span className="truncate">Duration date</span>
                    )}
                    {listColumns.tags && <span className="truncate">Service tags</span>}
                    {listColumns.followers && <span className="truncate">Followers</span>}
                    {listColumns.totalTrackedHours && (
                      <span className="truncate" title="Total time tracked by everyone on this task">
                        Total tracked
                      </span>
                    )}
                    <span className="sr-only">Actions</span>
                  </div>

                  {filteredTasks.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-gray-400">
                      No tasks yet. Add one in the row below.
                    </div>
                  ) : (
                    filteredTasks.map((task) => {
                      const isExpanded = expandedTaskIds.has(task.id);
                      const subtaskCount = (task.subtasks || []).length;
                      const menuOpen = listRowMenuId === task.id;
                      const estimateDraft =
                        estimatedHoursDraftByTaskId[task.id] !== undefined
                          ? estimatedHoursDraftByTaskId[task.id]
                          : secondsToHoursInput(task.estimatedSeconds);
                      const taskStatusName = normalizeLegacyTaskStatus(
                        task.status || 'To Do'
                      );
                      const taskStatusColor = resolveStatusColor(
                        currentProject?.taskStatuses,
                        taskStatusName,
                        'gray'
                      );

                      return (
                        <div key={task.id} className="border-b border-gray-100 last:border-b-0">
                          <div
                            className="w-full min-w-0 grid items-center gap-x-2 px-3 py-2.5 hover:bg-gray-50/70 group"
                            style={{ gridTemplateColumns: listGridTemplate }}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <button
                                type="button"
                                onClick={() => toggleTaskExpanded(task.id)}
                                className={`shrink-0 h-6 w-6 rounded text-gray-400 hover:bg-gray-100 text-xs ${
                                  subtaskCount === 0 ? 'opacity-30' : ''
                                }`}
                                aria-label={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                              >
                                {isExpanded ? '▾' : '▸'}
                              </button>
                              {editingTaskId === task.id ? (
                                <div className="flex items-center gap-1 min-w-0 flex-1">
                                  <input
                                    type="text"
                                    value={editTaskName}
                                    autoFocus
                                    onChange={(e) => setEditTaskName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleRenameTask(task.id);
                                      }
                                      if (e.key === 'Escape') {
                                        setEditingTaskId(null);
                                      }
                                    }}
                                    onBlur={() => {
                                      if (
                                        editTaskName.trim() &&
                                        editTaskName.trim() !== task.name
                                      ) {
                                        handleRenameTask(task.id);
                                      } else {
                                        setEditingTaskId(null);
                                      }
                                    }}
                                    className="flex-1 min-w-0 border border-purple-400 rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none"
                                  />
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    closeListMenus();
                                    setAssigningTaskId(null);
                                    setFollowingTaskId(null);
                                    setTaggingTaskId(null);
                                    setEditingTaskId(task.id);
                                    setEditTaskName(task.name);
                                  }}
                                  className="min-w-0 flex items-center gap-2 text-left rounded-lg hover:bg-purple-50/80 -ml-1 pl-1 pr-2 py-0.5 transition-colors"
                                  title={hoverFullText(task.name, 'Click to rename')}
                                >
                                  <span
                                    className={`h-4 w-4 rounded-full shrink-0 ring-2 ring-white shadow-sm ${statusDotClass(
                                      taskStatusColor,
                                      task.isRunning
                                    )}`}
                                    title={`Status: ${taskStatusName}`}
                                  />
                                  <span
                                    className="text-sm font-semibold text-gray-900 truncate"
                                    title={hoverFullText(task.name)}
                                  >
                                    {task.name}
                                  </span>
                                  {subtaskCount > 0 && (
                                    <span className="shrink-0 text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                      {subtaskCount}
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>

                            <div className="min-w-0">
                              <select
                                value={taskStatusName}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '__add_status__') {
                                    if (!canManageActiveProject) return;
                                    const name = window.prompt(
                                      'New task status name:'
                                    );
                                    if (!name?.trim()) return;
                                    handleAddTaskStatusOption(
                                      activeProjectId,
                                      name
                                    ).then(() =>
                                      handleUpdateTaskStatus(
                                        task.id,
                                        name.trim()
                                      )
                                    );
                                    return;
                                  }
                                  handleUpdateTaskStatus(task.id, value);
                                }}
                                className={`w-full max-w-full px-1.5 py-1.5 text-[10px] font-black uppercase tracking-wide rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 ${statusBadgeClass(
                                  taskStatusColor
                                )}`}
                                title="Task status"
                              >
                                {(currentProject?.taskStatuses ||
                                  DEFAULT_TASK_STATUSES.map((item, index) => ({
                                    id: `fallback-${index}`,
                                    name: item.name,
                                  }))
                                ).map((option) => (
                                  <option key={option.id} value={option.name}>
                                    {option.name}
                                  </option>
                                ))}
                                {!(currentProject?.taskStatuses || []).some(
                                  (option) => option.name === taskStatusName
                                ) &&
                                  !DEFAULT_TASK_STATUSES.some(
                                    (item) => item.name === taskStatusName
                                  ) && (
                                    <option value={taskStatusName}>
                                      {taskStatusName}
                                    </option>
                                  )}
                                {canManageActiveProject && (
                                  <option value="__add_status__">
                                    + Add status…
                                  </option>
                                )}
                              </select>
                            </div>

                            <div className="relative flex flex-wrap gap-1 min-w-0">
                              {(task.assignees || [])
                                .filter((a) => a !== 'Unassigned')
                                .slice(0, 3)
                                .map((assignee) => (
                                  <button
                                    key={assignee}
                                    type="button"
                                    title={assignee}
                                    onClick={() => {
                                      closeListMenus();
                                      setFollowingTaskId(null);
                                      setTaggingTaskId(null);
                                      setEditingTaskId(null);
                                      setAssigningTaskId(
                                        assigningTaskId === task.id ? null : task.id
                                      );
                                    }}
                                    className="h-7 w-7 rounded-full bg-purple-100 text-purple-800 text-[10px] font-black border border-purple-200"
                                  >
                                    {getAssigneeInitials(assignee)}
                                  </button>
                                ))}
                              {(task.assignees || []).filter((a) => a !== 'Unassigned')
                                .length === 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    closeListMenus();
                                    setEditingTaskId(null);
                                    setAssigningTaskId(
                                      assigningTaskId === task.id ? null : task.id
                                    );
                                  }}
                                  className="text-[11px] text-gray-400 font-medium hover:text-purple-600"
                                >
                                  + Assign
                                </button>
                              )}
                              {assigningTaskId === task.id && (
                                <div className="absolute left-0 top-full mt-1 z-30 w-56 rounded-xl border border-purple-100 bg-white shadow-lg p-2 space-y-1">
                                  <div className="flex items-center justify-between px-1 pb-1">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-purple-700">
                                      Assignees
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setAssigningTaskId(null)}
                                      className="text-[10px] font-bold text-purple-700 hover:underline"
                                    >
                                      Done
                                    </button>
                                  </div>
                                  <div className="max-h-40 overflow-y-auto space-y-0.5">
                                    {currentProject?.members.map((member) => {
                                      const memberLabel = `${member.name} (${member.role})`;
                                      const isAssigned =
                                        task.assignees.includes(memberLabel);
                                      return (
                                        <button
                                          key={member.id}
                                          type="button"
                                          onClick={() =>
                                            toggleTaskAssignee(task.id, memberLabel)
                                          }
                                          className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium ${
                                            isAssigned
                                              ? 'bg-purple-50 text-purple-800'
                                              : 'text-gray-700 hover:bg-gray-50'
                                          }`}
                                        >
                                          {isAssigned ? '✓ ' : '+ '}
                                          {memberLabel}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col items-start gap-1 min-w-0">
                              <span
                                className={`font-mono text-xs font-bold tabular-nums ${
                                  task.isRunning ? 'text-blue-700' : 'text-gray-800'
                                }`}
                              >
                                {formatTime(getTaskLiveSeconds(task))}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeListMenus();
                                  toggleTimer(task.id, task.name);
                                }}
                                className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border transition-colors ${
                                  task.isRunning
                                    ? 'border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100'
                                    : 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100'
                                }`}
                                title={
                                  task.isRunning ? 'Pause timer' : 'Start timer'
                                }
                                aria-label={
                                  task.isRunning ? 'Pause timer' : 'Start timer'
                                }
                              >
                                {task.isRunning ? (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M5.75 3A1.75 1.75 0 004 4.75v10.5c0 .966.784 1.75 1.75 1.75h1.5A1.75 1.75 0 009 15.25V4.75A1.75 1.75 0 007.25 3h-1.5zM12.75 3A1.75 1.75 0 0011 4.75v10.5c0 .966.784 1.75 1.75 1.75h1.5A1.75 1.75 0 0016 15.25V4.75A1.75 1.75 0 0014.25 3h-1.5z" />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z" />
                                  </svg>
                                )}
                              </button>
                            </div>

                            {showDetailedColumns && (
                            <div>
                              <input
                                type="number"
                                min="0"
                                step="0.25"
                                value={estimateDraft}
                                onChange={(e) =>
                                  setEstimatedHoursDraftByTaskId((prev) => ({
                                    ...prev,
                                    [task.id]: e.target.value,
                                  }))
                                }
                                onBlur={() =>
                                  updateTaskEstimatedTime(task.id, estimateDraft)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                  }
                                }}
                                placeholder="0h"
                                className="w-full max-w-full px-1.5 py-1 text-xs border border-transparent hover:border-gray-200 focus:border-purple-400 rounded-lg bg-transparent focus:bg-white focus:outline-none"
                                title={formatEstimatedHours(task.estimatedSeconds)}
                              />
                            </div>
                            )}

                            {showDetailedColumns && (
                            <div className="min-w-0">
                              <input
                                type="date"
                                value={task.startDate || ''}
                                onChange={(e) =>
                                  updateTaskScheduleDate(
                                    task.id,
                                    'startDate',
                                    e.target.value
                                  )
                                }
                                className="w-full min-w-0 px-1 py-1 text-[11px] border border-transparent hover:border-gray-200 focus:border-purple-400 rounded-lg bg-transparent focus:bg-white focus:outline-none"
                                title="Start date"
                              />
                            </div>
                            )}

                            {showDetailedColumns && (
                            <div className="min-w-0">
                              <input
                                type="date"
                                value={task.endDate || ''}
                                onChange={(e) =>
                                  updateTaskScheduleDate(
                                    task.id,
                                    'endDate',
                                    e.target.value
                                  )
                                }
                                className="w-full min-w-0 px-1 py-1 text-[11px] border border-transparent hover:border-gray-200 focus:border-purple-400 rounded-lg bg-transparent focus:bg-white focus:outline-none"
                                title="Duration date"
                              />
                            </div>
                            )}

                            {listColumns.tags && (
                              <div className="relative min-w-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    closeListMenus();
                                    setAssigningTaskId(null);
                                    setFollowingTaskId(null);
                                    setEditingTaskId(null);
                                    const opening = taggingTaskId !== task.id;
                                    setTaggingTaskId(opening ? task.id : null);
                                    setTaskServiceMenuOpen(opening);
                                  }}
                                  className="w-full text-left px-2 py-1 rounded-lg border border-transparent hover:border-emerald-200 hover:bg-emerald-50/50 text-[10px] font-bold text-emerald-800 truncate"
                                  title={hoverFullText(
                                    (task.tags || []).length === 0
                                      ? 'Select service tags'
                                      : (task.tags || []).map((t) => t.name).join(', ')
                                  )}
                                >
                                  {(task.tags || []).length === 0
                                    ? 'Select tags…'
                                    : (task.tags || []).length <= 2
                                      ? (task.tags || []).map((t) => t.name).join(', ')
                                      : `${(task.tags || []).length} tags`}
                                </button>
                                {taggingTaskId === task.id && taskServiceMenuOpen && (
                                  <div className="absolute left-0 top-full mt-1 z-40 w-64 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-100">
                                      <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">
                                        Service tags
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setTaggingTaskId(null);
                                          setTaskServiceMenuOpen(false);
                                        }}
                                        className="text-[10px] font-bold text-emerald-700 hover:underline"
                                      >
                                        Done
                                      </button>
                                    </div>
                                    <div className="max-h-44 overflow-y-auto p-2 space-y-2">
                                      {MASTER_TASK_MATRIX.map((group) => (
                                        <div key={`inline-tag-${group.category}`}>
                                          <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                            {group.category}
                                          </p>
                                          {group.services.map((service) => {
                                            const existing = tags.find(
                                              (tag) =>
                                                tag.name.toLowerCase() ===
                                                  service.toLowerCase() &&
                                                !String(tag.id).startsWith('local-')
                                            );
                                            const isTagged = existing
                                              ? (task.tags || []).some(
                                                  (tag) => tag.id === existing.id
                                                )
                                              : (task.tags || []).some(
                                                  (tag) =>
                                                    tag.name.toLowerCase() ===
                                                    service.toLowerCase()
                                                );
                                            if (
                                              currentUser.role !== 'admin' &&
                                              !existing
                                            ) {
                                              return null;
                                            }
                                            return (
                                              <button
                                                key={`inline-svc-${service}`}
                                                type="button"
                                                onClick={async () => {
                                                  const tag =
                                                    existing ||
                                                    (await ensureTagByName(service));
                                                  if (tag) await toggleTaskTag(task.id, tag);
                                                }}
                                                className={`w-full text-left px-2 py-1 rounded-lg text-xs font-medium ${
                                                  isTagged
                                                    ? 'bg-emerald-50 text-emerald-800'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                              >
                                                {isTagged ? '✓ ' : '+ '}
                                                {service}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      ))}
                                      {tags.filter((tag) => {
                                        if (String(tag.id).startsWith('local-')) {
                                          return false;
                                        }
                                        return !MASTER_TASK_MATRIX.some((group) =>
                                          group.services.some(
                                            (service) =>
                                              service.toLowerCase() ===
                                              tag.name.toLowerCase()
                                          )
                                        );
                                      }).length > 0 && (
                                        <div>
                                          <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                            Custom
                                          </p>
                                          {tags
                                            .filter((tag) => {
                                              if (String(tag.id).startsWith('local-')) {
                                                return false;
                                              }
                                              return !MASTER_TASK_MATRIX.some((group) =>
                                                group.services.some(
                                                  (service) =>
                                                    service.toLowerCase() ===
                                                    tag.name.toLowerCase()
                                                )
                                              );
                                            })
                                            .map((tag) => {
                                              const isTagged = (task.tags || []).some(
                                                (item) => item.id === tag.id
                                              );
                                              return (
                                                <button
                                                  key={`inline-catalog-${tag.id}`}
                                                  type="button"
                                                  onClick={() => toggleTaskTag(task.id, tag)}
                                                  className={`w-full text-left px-2 py-1 rounded-lg text-xs font-medium ${
                                                    isTagged
                                                      ? 'bg-emerald-50 text-emerald-800'
                                                      : 'text-gray-700 hover:bg-gray-50'
                                                  }`}
                                                >
                                                  {isTagged ? '✓ ' : '+ '}
                                                  {tag.name}
                                                </button>
                                              );
                                            })}
                                        </div>
                                      )}
                                    </div>
                                    {isSystemAdmin ? (
                                      <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                                        <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                          Add New Tag
                                        </p>
                                        <div className="flex gap-1.5">
                                          <input
                                            type="text"
                                            value={newTaskTagName}
                                            onChange={(e) =>
                                              setNewTaskTagName(e.target.value)
                                            }
                                            onKeyDown={async (e) => {
                                              if (e.key !== 'Enter') return;
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (!newTaskTagName.trim()) return;
                                              await createProjectAdminTagForTask(
                                                task.id,
                                                task.projectId,
                                                newTaskTagName
                                              );
                                            }}
                                            placeholder="New tag name…"
                                            disabled={trackerNewTagSaving}
                                            className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                          />
                                          <button
                                            type="button"
                                            disabled={
                                              trackerNewTagSaving ||
                                              !newTaskTagName.trim()
                                            }
                                            onClick={async () => {
                                              if (!newTaskTagName.trim()) return;
                                              await createProjectAdminTagForTask(
                                                task.id,
                                                task.projectId,
                                                newTaskTagName
                                              );
                                            }}
                                            className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                          >
                                            {trackerNewTagSaving ? '…' : 'Add'}
                                          </button>
                                        </div>
                                        <p className="px-1 text-[10px] text-gray-400">
                                          Team admins can create tags for the shared
                                          catalog.
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="border-t border-gray-100 px-3 py-2">
                                        <p className="text-[10px] text-gray-400">
                                          Ask a Team admin to create a new tag for the
                                          shared catalog.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {listColumns.followers && (
                              <div className="relative min-w-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    closeListMenus();
                                    setAssigningTaskId(null);
                                    setTaggingTaskId(null);
                                    setEditingTaskId(null);
                                    setFollowingTaskId(
                                      followingTaskId === task.id ? null : task.id
                                    );
                                  }}
                                  className="w-full text-left px-2 py-1 rounded-lg border border-transparent hover:border-teal-200 hover:bg-teal-50/50 text-[10px] font-bold text-teal-800 truncate"
                                  title={hoverFullText(
                                    (task.followers || []).length === 0
                                      ? 'Select followers'
                                      : (task.followers || []).join(', ')
                                  )}
                                >
                                  {(task.followers || []).length === 0
                                    ? 'Select followers…'
                                    : (task.followers || []).length <= 2
                                      ? (task.followers || []).join(', ')
                                      : `${(task.followers || []).length} followers`}
                                </button>
                                {followingTaskId === task.id && (
                                  <div className="absolute left-0 top-full mt-1 z-30 w-56 rounded-xl border border-teal-100 bg-white shadow-lg p-2 space-y-1">
                                    <div className="flex items-center justify-between px-1 pb-1">
                                      <span className="text-[10px] font-black uppercase tracking-wider text-teal-700">
                                        Followers
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setFollowingTaskId(null)}
                                        className="text-[10px] font-bold text-teal-700 hover:underline"
                                      >
                                        Done
                                      </button>
                                    </div>
                                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                                      {currentProject?.members.map((member) => {
                                        const memberLabel = `${member.name} (${member.role})`;
                                        const isFollowing = (
                                          task.followers || []
                                        ).includes(memberLabel);
                                        return (
                                          <button
                                            key={`follow-inline-${member.id}`}
                                            type="button"
                                            onClick={() =>
                                              toggleTaskFollower(task.id, memberLabel)
                                            }
                                            className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium ${
                                              isFollowing
                                                ? 'bg-teal-50 text-teal-800'
                                                : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                          >
                                            {isFollowing ? '✓ ' : '+ '}
                                            {memberLabel}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {listColumns.totalTrackedHours && (
                              <div
                                className="min-w-0"
                                title="Total time tracked by everyone on this task"
                              >
                                <span className="font-mono text-xs font-bold tabular-nums text-indigo-800">
                                  {formatTime(getTaskTotalTrackedSeconds(task))}
                                </span>
                              </div>
                            )}

                            <div className="relative flex justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  setListColumnPickerOpen(false);
                                  setListRowMenuId(menuOpen ? null : task.id);
                                }}
                                className="h-7 w-7 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 text-base font-black leading-none"
                                aria-label="Task actions"
                              >
                                ⋯
                              </button>
                              {menuOpen && (
                                <div className="absolute right-0 top-full mt-1 z-40 w-48 rounded-xl border border-gray-200 bg-white shadow-lg py-1 text-xs font-medium text-gray-700">
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setEditingTaskId(task.id);
                                      setEditTaskName(task.name);
                                    }}
                                  >
                                    Rename
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setFollowingTaskId(null);
                                      setTaggingTaskId(null);
                                      setAssigningTaskId(task.id);
                                    }}
                                  >
                                    Edit assignees
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setAssigningTaskId(null);
                                      setTaggingTaskId(null);
                                      setFollowingTaskId(task.id);
                                    }}
                                  >
                                    Edit followers
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setAssigningTaskId(null);
                                      setFollowingTaskId(null);
                                      setTaggingTaskId(task.id);
                                      setTaskServiceMenuOpen(true);
                                    }}
                                  >
                                    Edit tags
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      toggleTimer(task.id, task.name);
                                    }}
                                  >
                                    {task.isRunning ? 'Pause timer' : 'Start timer'}
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setLoggingTimeTaskId(task.id);
                                      setTaskManualDate(toLocalDateInputValue());
                                    }}
                                  >
                                    Log time
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      resetTimer(task.id, task.name);
                                    }}
                                  >
                                    Reset time
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                    onClick={() => {
                                      setListRowMenuId(null);
                                      setExpandedTaskIds((prev) => new Set(prev).add(task.id));
                                      setNewSubtaskByTaskId((prev) => ({
                                        ...prev,
                                        [task.id]: prev[task.id] || '',
                                      }));
                                    }}
                                  >
                                    Add subtask
                                  </button>
                                  {canManageActiveProject && (
                                    <button
                                      type="button"
                                      className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600"
                                      onClick={() => {
                                        setListRowMenuId(null);
                                        handleDeleteTask(task.id, task.name);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {loggingTimeTaskId === task.id && (
                            <div className="px-4 pb-3">
                              <form
                                onSubmit={(e) =>
                                  handleTaskManualTimeLog(task.id, e)
                                }
                                className="bg-blue-50 border border-blue-100 rounded-xl p-3 space-y-2"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-[10px] font-black uppercase tracking-wider text-blue-800">
                                    Manual time log
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setLoggingTimeTaskId(null)}
                                    className="text-[10px] font-bold text-blue-700 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="date"
                                    value={taskManualDate}
                                    onChange={(e) =>
                                      setTaskManualDate(e.target.value)
                                    }
                                    className="px-2 py-1.5 text-xs border border-blue-200 rounded-lg bg-white"
                                  />
                                  <input
                                    type="time"
                                    value={taskManualStart}
                                    onChange={(e) =>
                                      setTaskManualStart(e.target.value)
                                    }
                                    className="px-2 py-1.5 text-xs border border-blue-200 rounded-lg bg-white"
                                  />
                                  <span className="text-xs text-blue-400">–</span>
                                  <input
                                    type="time"
                                    value={taskManualEnd}
                                    onChange={(e) =>
                                      setTaskManualEnd(e.target.value)
                                    }
                                    className="px-2 py-1.5 text-xs border border-blue-200 rounded-lg bg-white"
                                  />
                                  <button
                                    type="submit"
                                    disabled={manualLogSaving}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 disabled:opacity-60"
                                  >
                                    {manualLogSaving ? 'Saving…' : 'Add time'}
                                  </button>
                                </div>
                              </form>
                            </div>
                          )}

                          {isExpanded && (
                            <div className="bg-gray-50/50 border-t border-gray-50">
                              {(task.subtasks || []).map((subtask) => {
                                const subKey = `${task.id}:${subtask.id}`;
                                const subMenuId = `sub:${subKey}`;
                                const subMenuOpen = listRowMenuId === subMenuId;
                                const subStatusName =
                                  subtask.status ||
                                  (subtask.isCompleted ? 'Done' : 'To Do');
                                const subStatusColor = resolveStatusColor(
                                  currentProject?.taskStatuses,
                                  subStatusName,
                                  'gray'
                                );
                                return (
                                  <div key={subtask.id}>
                                    <div
                                      className="w-full min-w-0 grid items-center gap-x-2 px-3 py-2 hover:bg-white/80"
                                      style={{
                                        gridTemplateColumns: listGridTemplate,
                                      }}
                                    >
                                      <div className="flex items-center gap-2 min-w-0 pl-8">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleSubtaskComplete(task.id, subtask)
                                          }
                                          className={`shrink-0 h-4 w-4 rounded border flex items-center justify-center text-[10px] font-bold ${
                                            subtask.isCompleted
                                              ? 'bg-green-600 border-green-600 text-white'
                                              : 'bg-white border-gray-300 text-transparent'
                                          }`}
                                        >
                                          ✓
                                        </button>
                                        {editingSubtaskKey === subKey ? (
                                          <input
                                            type="text"
                                            value={editSubtaskName}
                                            autoFocus
                                            onChange={(e) =>
                                              setEditSubtaskName(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleRenameSubtask(
                                                  task.id,
                                                  subtask.id
                                                );
                                              }
                                              if (e.key === 'Escape') {
                                                setEditingSubtaskKey(null);
                                              }
                                            }}
                                            onBlur={() => {
                                              if (
                                                editSubtaskName.trim() &&
                                                editSubtaskName.trim() !==
                                                  subtask.name
                                              ) {
                                                handleRenameSubtask(
                                                  task.id,
                                                  subtask.id
                                                );
                                              } else {
                                                setEditingSubtaskKey(null);
                                              }
                                            }}
                                            className="flex-1 min-w-0 border border-purple-400 rounded-lg px-2 py-1 text-xs font-semibold focus:outline-none"
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              closeListMenus();
                                              setAssigningSubtaskKey(null);
                                              setTaggingSubtaskKey(null);
                                              setEditingSubtaskKey(subKey);
                                              setEditSubtaskName(subtask.name);
                                            }}
                                            className={`min-w-0 flex-1 text-left text-xs truncate rounded-lg hover:bg-purple-50/80 px-1 py-0.5 ${
                                              subtask.isCompleted
                                                ? 'text-gray-400 line-through'
                                                : 'text-gray-800 font-medium'
                                            }`}
                                            title={hoverFullText(
                                              subtask.name,
                                              'Click to rename'
                                            )}
                                          >
                                            {subtask.name}
                                          </button>
                                        )}
                                      </div>
                                      <div className="min-w-0">
                                        <select
                                          value={subStatusName}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '__add_status__') {
                                              if (!canManageActiveProject) return;
                                              const name = window.prompt(
                                                'New task status name:'
                                              );
                                              if (!name?.trim()) return;
                                              handleAddTaskStatusOption(
                                                activeProjectId,
                                                name
                                              ).then(() =>
                                                handleUpdateSubtaskStatus(
                                                  task.id,
                                                  subtask.id,
                                                  name.trim()
                                                )
                                              );
                                              return;
                                            }
                                            handleUpdateSubtaskStatus(
                                              task.id,
                                              subtask.id,
                                              value
                                            );
                                          }}
                                          className={`w-full max-w-full px-1 py-0.5 text-[10px] font-bold rounded-md border focus:outline-none focus:ring-2 focus:ring-purple-500 ${statusBadgeClass(
                                            subStatusColor
                                          )}`}
                                          title="Subtask status"
                                        >
                                          {(
                                            currentProject?.taskStatuses ||
                                            DEFAULT_TASK_STATUSES.map(
                                              (item, index) => ({
                                                id: `fallback-${index}`,
                                                name: item.name,
                                              })
                                            )
                                          ).map((option) => (
                                            <option
                                              key={option.id}
                                              value={option.name}
                                            >
                                              {option.name}
                                            </option>
                                          ))}
                                          {canManageActiveProject && (
                                            <option value="__add_status__">
                                              + Add status…
                                            </option>
                                          )}
                                        </select>
                                      </div>
                                      <div className="relative flex flex-wrap gap-1 min-w-0">
                                        {(subtask.assignees || [])
                                          .filter((a) => a !== 'Unassigned')
                                          .slice(0, 3)
                                          .map((assignee) => (
                                            <button
                                              key={assignee}
                                              type="button"
                                              title={assignee}
                                              onClick={() => {
                                                closeListMenus();
                                                setEditingSubtaskKey(null);
                                                setTaggingSubtaskKey(null);
                                                setAssigningSubtaskKey(
                                                  assigningSubtaskKey === subKey
                                                    ? null
                                                    : subKey
                                                );
                                              }}
                                              className="h-7 w-7 rounded-full bg-purple-100 text-purple-800 text-[10px] font-black border border-purple-200"
                                            >
                                              {getAssigneeInitials(assignee)}
                                            </button>
                                          ))}
                                        {(subtask.assignees || []).filter(
                                          (a) => a !== 'Unassigned'
                                        ).length === 0 && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              closeListMenus();
                                              setEditingSubtaskKey(null);
                                              setAssigningSubtaskKey(
                                                assigningSubtaskKey === subKey
                                                  ? null
                                                  : subKey
                                              );
                                            }}
                                            className="text-[11px] text-gray-400 font-medium hover:text-purple-600"
                                          >
                                            + Assign
                                          </button>
                                        )}
                                        {assigningSubtaskKey === subKey && (
                                          <div className="absolute left-0 top-full mt-1 z-30 w-56 rounded-xl border border-purple-100 bg-white shadow-lg p-2 space-y-1">
                                            <div className="flex items-center justify-between px-1 pb-1">
                                              <span className="text-[10px] font-black uppercase tracking-wider text-purple-700">
                                                Assignees
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setAssigningSubtaskKey(null)
                                                }
                                                className="text-[10px] font-bold text-purple-700 hover:underline"
                                              >
                                                Done
                                              </button>
                                            </div>
                                            <div className="max-h-40 overflow-y-auto space-y-0.5">
                                              {(currentProject?.members || []).map(
                                                (member) => {
                                                  const memberLabel = `${member.name} (${member.role})`;
                                                  const isAssigned = (
                                                    subtask.assignees || []
                                                  ).includes(memberLabel);
                                                  return (
                                                    <button
                                                      key={member.id}
                                                      type="button"
                                                      onClick={() =>
                                                        toggleSubtaskAssignee(
                                                          task.id,
                                                          subtask.id,
                                                          memberLabel
                                                        )
                                                      }
                                                      className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium ${
                                                        isAssigned
                                                          ? 'bg-purple-50 text-purple-800'
                                                          : 'text-gray-700 hover:bg-gray-50'
                                                      }`}
                                                    >
                                                      {isAssigned ? '✓ ' : '+ '}
                                                      {memberLabel}
                                                    </button>
                                                  );
                                                }
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-start gap-1 min-w-0">
                                        <span
                                          className={`font-mono text-xs font-bold tabular-nums ${
                                            task.isRunning
                                              ? 'text-blue-700'
                                              : 'text-gray-800'
                                          }`}
                                          title="Time tracks on the parent task"
                                        >
                                          {formatTime(getTaskLiveSeconds(task))}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            closeListMenus();
                                            toggleTimer(task.id, task.name);
                                          }}
                                          className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border transition-colors ${
                                            task.isRunning
                                              ? 'border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100'
                                              : 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100'
                                          }`}
                                          title={
                                            task.isRunning
                                              ? 'Pause timer (parent task)'
                                              : 'Start timer (parent task)'
                                          }
                                          aria-label={
                                            task.isRunning
                                              ? 'Pause timer'
                                              : 'Start timer'
                                          }
                                        >
                                          {task.isRunning ? (
                                            <svg
                                              className="w-3.5 h-3.5"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M5.75 3A1.75 1.75 0 004 4.75v10.5c0 .966.784 1.75 1.75 1.75h1.5A1.75 1.75 0 009 15.25V4.75A1.75 1.75 0 007.25 3h-1.5zM12.75 3A1.75 1.75 0 0011 4.75v10.5c0 .966.784 1.75 1.75 1.75h1.5A1.75 1.75 0 0016 15.25V4.75A1.75 1.75 0 0014.25 3h-1.5z" />
                                            </svg>
                                          ) : (
                                            <svg
                                              className="w-3.5 h-3.5"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z" />
                                            </svg>
                                          )}
                                        </button>
                                      </div>
                                      {showDetailedColumns && (
                                        <span className="text-xs text-gray-300">—</span>
                                      )}
                                      {showDetailedColumns && (
                                      <div className="min-w-0">
                                        <input
                                          type="date"
                                          value={subtask.startDate || ''}
                                          onChange={(e) =>
                                            updateSubtaskScheduleDate(
                                              task.id,
                                              subtask.id,
                                              'startDate',
                                              e.target.value
                                            )
                                          }
                                          className="w-full min-w-0 px-1 py-1 text-[11px] border border-transparent hover:border-gray-200 focus:border-purple-400 rounded-lg bg-transparent focus:bg-white focus:outline-none"
                                          title="Subtask start date"
                                        />
                                      </div>
                                      )}
                                      {showDetailedColumns && (
                                      <div className="min-w-0">
                                        <input
                                          type="date"
                                          value={subtask.endDate || ''}
                                          onChange={(e) =>
                                            updateSubtaskScheduleDate(
                                              task.id,
                                              subtask.id,
                                              'endDate',
                                              e.target.value
                                            )
                                          }
                                          className="w-full min-w-0 px-1 py-1 text-[11px] border border-transparent hover:border-gray-200 focus:border-purple-400 rounded-lg bg-transparent focus:bg-white focus:outline-none"
                                          title="Subtask duration date"
                                        />
                                      </div>
                                      )}
                                      {listColumns.tags && (
                                        <div className="relative flex flex-wrap gap-1">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              closeListMenus();
                                              setAssigningSubtaskKey(null);
                                              setEditingSubtaskKey(null);
                                              setTaggingSubtaskKey((key) =>
                                                key === subKey ? null : subKey
                                              );
                                            }}
                                            className="w-full text-left px-2 py-1 rounded-lg border border-transparent hover:border-emerald-200 hover:bg-emerald-50/50 text-[10px] font-bold text-emerald-800 truncate"
                                            title={hoverFullText(
                                              (subtask.tags || []).length === 0
                                                ? 'Select tags'
                                                : (subtask.tags || [])
                                                    .map((t) => t.name)
                                                    .join(', ')
                                            )}
                                          >
                                            {(subtask.tags || []).length === 0
                                              ? 'Select tags…'
                                              : (subtask.tags || []).length <= 2
                                                ? (subtask.tags || [])
                                                    .map((t) => t.name)
                                                    .join(', ')
                                                : `${(subtask.tags || []).length} tags`}
                                          </button>
                                        </div>
                                      )}
                                      {listColumns.followers && (
                                        <span className="text-xs text-gray-300">—</span>
                                      )}
                                      {listColumns.totalTrackedHours && (
                                        <div
                                          className="min-w-0"
                                          title="Total time tracked on the parent task"
                                        >
                                          <span className="font-mono text-xs font-bold tabular-nums text-indigo-800">
                                            {formatTime(
                                              getTaskTotalTrackedSeconds(task)
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      <div className="relative flex justify-end">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setListColumnPickerOpen(false);
                                            setListRowMenuId(
                                              subMenuOpen ? null : subMenuId
                                            );
                                          }}
                                          className="h-7 w-7 rounded-lg text-gray-400 hover:bg-gray-100 text-base font-black leading-none"
                                          aria-label="Subtask actions"
                                        >
                                          ⋯
                                        </button>
                                        {subMenuOpen && (
                                          <div className="absolute right-0 top-full mt-1 z-40 w-44 rounded-xl border border-gray-200 bg-white shadow-lg py-1 text-xs font-medium text-gray-700">
                                            <button
                                              type="button"
                                              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                              onClick={() => {
                                                setListRowMenuId(null);
                                                setEditingSubtaskKey(subKey);
                                                setEditSubtaskName(subtask.name);
                                              }}
                                            >
                                              Rename
                                            </button>
                                            <button
                                              type="button"
                                              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                              onClick={() => {
                                                setListRowMenuId(null);
                                                setAssigningSubtaskKey(subKey);
                                              }}
                                            >
                                              Edit assignees
                                            </button>
                                            <button
                                              type="button"
                                              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                              onClick={() => {
                                                setListRowMenuId(null);
                                                toggleSubtaskComplete(
                                                  task.id,
                                                  subtask
                                                );
                                              }}
                                            >
                                              {subtask.isCompleted
                                                ? 'Mark incomplete'
                                                : 'Mark complete'}
                                            </button>
                                            <button
                                              type="button"
                                              className="w-full text-left px-3 py-1.5 hover:bg-gray-50"
                                              onClick={() => {
                                                setListRowMenuId(null);
                                                setTaggingSubtaskKey((key) =>
                                                  key === subKey ? null : subKey
                                                );
                                              }}
                                            >
                                              Edit tags
                                            </button>
                                            <button
                                              type="button"
                                              className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600"
                                              onClick={() => {
                                                setListRowMenuId(null);
                                                handleDeleteSubtask(
                                                  task.id,
                                                  subtask
                                                );
                                              }}
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {taggingSubtaskKey === subKey && (
                                      <div className="px-4 pb-2 pl-12 space-y-2">
                                        <div className="flex flex-wrap gap-1">
                                          {tags.slice(0, 40).map((tag) => {
                                            const isTagged = (
                                              subtask.tags || []
                                            ).some((item) => item.id === tag.id);
                                            return (
                                              <button
                                                key={`subtag-list-${subtask.id}-${tag.id}`}
                                                type="button"
                                                onClick={() =>
                                                  toggleSubtaskTag(
                                                    task.id,
                                                    subtask.id,
                                                    tag
                                                  )
                                                }
                                                className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                                                  isTagged
                                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                                    : 'bg-white text-gray-600 border-gray-200'
                                                }`}
                                              >
                                                {isTagged ? '✓ ' : '+ '}
                                                {tag.name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {isSystemAdmin ? (
                                          <div className="rounded-xl border border-gray-200 bg-gray-50 p-2 space-y-1.5 max-w-sm">
                                            <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                              Add New Tag
                                            </p>
                                            <div className="flex gap-1.5">
                                              <input
                                                type="text"
                                                value={newTaskTagName}
                                                onChange={(e) =>
                                                  setNewTaskTagName(e.target.value)
                                                }
                                                onKeyDown={async (e) => {
                                                  if (e.key !== 'Enter') return;
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  if (!newTaskTagName.trim()) return;
                                                  await createProjectAdminTagForSubtask(
                                                    task.id,
                                                    subtask.id,
                                                    task.projectId,
                                                    newTaskTagName
                                                  );
                                                }}
                                                placeholder="New tag name…"
                                                disabled={trackerNewTagSaving}
                                                className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                              />
                                              <button
                                                type="button"
                                                disabled={
                                                  trackerNewTagSaving ||
                                                  !newTaskTagName.trim()
                                                }
                                                onClick={async () => {
                                                  if (!newTaskTagName.trim()) return;
                                                  await createProjectAdminTagForSubtask(
                                                    task.id,
                                                    subtask.id,
                                                    task.projectId,
                                                    newTaskTagName
                                                  );
                                                }}
                                                className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                              >
                                                {trackerNewTagSaving ? '…' : 'Add'}
                                              </button>
                                            </div>
                                            <p className="px-1 text-[10px] text-gray-400">
                                              Team admins can create tags for the
                                              shared catalog.
                                            </p>
                                          </div>
                                        ) : (
                                          <p className="text-[10px] text-gray-400">
                                            Ask a Team admin to create a new tag for
                                            the shared catalog.
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              <form
                                onSubmit={(e) => handleAddSubtask(task.id, e)}
                                className="px-4 py-2 pl-12 flex items-center gap-2"
                              >
                                <input
                                  type="text"
                                  value={newSubtaskByTaskId[task.id] || ''}
                                  onChange={(e) =>
                                    setNewSubtaskByTaskId((prev) => ({
                                      ...prev,
                                      [task.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Add a subtask…"
                                  className="flex-1 min-w-0 max-w-md px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <button
                                  type="submit"
                                  disabled={
                                    subtaskSavingTaskId === task.id ||
                                    !(newSubtaskByTaskId[task.id] || '').trim()
                                  }
                                  className="shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white bg-purple-600 disabled:opacity-50"
                                >
                                  {subtaskSavingTaskId === task.id ? '…' : 'Add'}
                                </button>
                              </form>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  <form
                    onSubmit={handleCreateTask}
                    className="w-full min-w-0 grid items-center gap-x-2 px-3 py-3 border-t border-gray-100 bg-white"
                    style={{ gridTemplateColumns: listGridTemplate }}
                  >
                    <input
                      type="text"
                      placeholder="+ Add task"
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <span
                      className={`inline-flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-wide px-1.5 py-1 rounded-lg border ${statusBadgeClass(
                        'gray'
                      )}`}
                      title="New tasks start as To Do"
                    >
                      <span className={`h-2 w-2 rounded-full ${statusDotClass('gray')}`} />
                      To Do
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {(currentProject?.members || []).slice(0, 4).map((member) => {
                        const memberLabel = `${member.name} (${member.role})`;
                        const isSelected = selectedAssignees.includes(memberLabel);
                        return (
                          <button
                            key={member.id}
                            type="button"
                            title={memberLabel}
                            onClick={() => toggleAssigneeSelection(memberLabel)}
                            className={`h-7 w-7 rounded-full text-[10px] font-black border ${
                              isSelected
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                            }`}
                          >
                            {getAssigneeInitials(member.name)}
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-xs text-gray-300">—</span>
                    {showDetailedColumns && (
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        placeholder="Est. hours"
                        value={newTaskEstimatedHours}
                        onChange={(e) => setNewTaskEstimatedHours(e.target.value)}
                        className="w-full max-w-full px-1.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    )}
                    {showDetailedColumns && (
                      <input
                        type="date"
                        value={newTaskStartDate}
                        onChange={(e) => setNewTaskStartDate(e.target.value)}
                        className="w-full min-w-0 px-1 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        title="Start date"
                      />
                    )}
                    {showDetailedColumns && (
                      <input
                        type="date"
                        value={newTaskEndDate}
                        onChange={(e) => setNewTaskEndDate(e.target.value)}
                        className="w-full min-w-0 px-1 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        title="Duration date"
                      />
                    )}
                    {listColumns.tags && (
                      <div className="relative min-w-0">
                        <button
                          type="button"
                          onClick={() => {
                            const nextOpen = !createTaskTagMenuOpen;
                            setListRowMenuId(null);
                            setListColumnPickerOpen(false);
                            setTaggingTaskId(null);
                            setTaskServiceMenuOpen(false);
                            setTaggingSubtaskKey(null);
                            setCreateTaskTagMenuOpen(nextOpen);
                          }}
                          className="w-full text-left px-2 py-1.5 rounded-lg border border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/50 text-[10px] font-bold text-emerald-800 truncate bg-white"
                          title={hoverFullText(
                            selectedTaskServices.length === 0
                              ? 'Select service tags'
                              : selectedTaskServices.join(', ')
                          )}
                        >
                          {selectedTaskServices.length === 0
                            ? 'Select tags…'
                            : selectedTaskServices.length <= 2
                              ? selectedTaskServices.join(', ')
                              : `${selectedTaskServices.length} tags`}
                        </button>
                        {createTaskTagMenuOpen && (
                          <div className="absolute left-0 bottom-full mb-1 z-40 w-64 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
                            <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-100">
                              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">
                                Service tags
                              </span>
                              <button
                                type="button"
                                onClick={() => setCreateTaskTagMenuOpen(false)}
                                className="text-[10px] font-bold text-emerald-700 hover:underline"
                              >
                                Done
                              </button>
                            </div>
                            <div className="max-h-44 overflow-y-auto p-2 space-y-2">
                              {MASTER_TASK_MATRIX.map((group) => (
                                <div key={`create-tag-${group.category}`}>
                                  <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                    {group.category}
                                  </p>
                                  {group.services.map((service) => {
                                    const isSelected =
                                      selectedTaskServices.includes(service);
                                    return (
                                      <button
                                        key={`create-svc-${service}`}
                                        type="button"
                                        onClick={() =>
                                          toggleTaskServiceSelection(service)
                                        }
                                        className={`w-full text-left px-2 py-1 rounded-lg text-xs font-medium ${
                                          isSelected
                                            ? 'bg-emerald-50 text-emerald-800'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                      >
                                        {isSelected ? '✓ ' : '+ '}
                                        {service}
                                      </button>
                                    );
                                  })}
                                </div>
                              ))}
                              {(customTaskServices.length > 0 ||
                                tags.some((tag) => {
                                  if (String(tag.id).startsWith('local-')) {
                                    return false;
                                  }
                                  return !MASTER_TASK_MATRIX.some((group) =>
                                    group.services.some(
                                      (service) =>
                                        service.toLowerCase() ===
                                        tag.name.toLowerCase()
                                    )
                                  );
                                })) && (
                                <div>
                                  <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-400">
                                    Custom
                                  </p>
                                  {[
                                    ...customTaskServices,
                                    ...tags
                                      .filter((tag) => {
                                        if (String(tag.id).startsWith('local-')) {
                                          return false;
                                        }
                                        const inMatrix = MASTER_TASK_MATRIX.some(
                                          (group) =>
                                            group.services.some(
                                              (service) =>
                                                service.toLowerCase() ===
                                                tag.name.toLowerCase()
                                            )
                                        );
                                        if (inMatrix) return false;
                                        return !customTaskServices.some(
                                          (service) =>
                                            service.toLowerCase() ===
                                            tag.name.toLowerCase()
                                        );
                                      })
                                      .map((tag) => tag.name),
                                  ].map((service) => {
                                    const isSelected =
                                      selectedTaskServices.includes(service);
                                    return (
                                      <button
                                        key={`create-custom-${service}`}
                                        type="button"
                                        onClick={() =>
                                          toggleTaskServiceSelection(service)
                                        }
                                        className={`w-full text-left px-2 py-1 rounded-lg text-xs font-medium ${
                                          isSelected
                                            ? 'bg-emerald-50 text-emerald-800'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                      >
                                        {isSelected ? '✓ ' : '+ '}
                                        {service}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {selectedTaskServices.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedTaskServices([])}
                                  className="w-full text-left px-2 py-1 text-[11px] font-bold text-gray-500 hover:text-red-600 border-t border-gray-100 mt-1"
                                >
                                  Clear selection
                                </button>
                              )}
                            </div>
                            {isSystemAdmin ? (
                              <div className="border-t border-gray-100 bg-gray-50 p-2 space-y-1.5">
                                <p className="px-1 text-[10px] font-black uppercase tracking-wider text-gray-500">
                                  Add New Tag
                                </p>
                                <div className="flex gap-1.5">
                                  <input
                                    type="text"
                                    value={newTaskTagName}
                                    onChange={(e) =>
                                      setNewTaskTagName(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key !== 'Enter') return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleAddCustomTaskTag(e);
                                    }}
                                    placeholder="New tag name…"
                                    className="flex-1 min-w-0 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleAddCustomTaskTag}
                                    disabled={!newTaskTagName.trim()}
                                    className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                                  >
                                    Add
                                  </button>
                                </div>
                                <p className="px-1 text-[10px] text-gray-400">
                                  Team admins can create tags for the shared catalog.
                                </p>
                              </div>
                            ) : (
                              <div className="border-t border-gray-100 px-3 py-2">
                                <p className="text-[10px] text-gray-400">
                                  Ask a Team admin to create a new tag for the shared
                                  catalog.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {listColumns.followers && (
                      <span className="text-[10px] text-gray-400">After create</span>
                    )}
                    {listColumns.totalTrackedHours && (
                      <span className="text-[10px] text-gray-400">—</span>
                    )}
                    <button
                      type="submit"
                      disabled={!newTaskName.trim()}
                      className="justify-self-end px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white bg-purple-600 disabled:opacity-40"
                    >
                      Add
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
            </>
          )}
        </section>

        {/* Activity Feed — only inside an open project */}
        {showActivityFeed && (
        <section className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm h-[600px] flex flex-col justify-between">
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-lg font-bold text-gray-900">Activity Feed</h3>
                <button
                  type="button"
                  onClick={() =>
                    setActivityFeedSort((prev) =>
                      prev === 'newest' ? 'oldest' : 'newest'
                    )
                  }
                  className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide text-gray-600 border border-gray-200 hover:bg-gray-50"
                  title={
                    activityFeedSort === 'newest'
                      ? 'Showing newest first — click for oldest first'
                      : 'Showing oldest first — click for newest first'
                  }
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                  {activityFeedSort === 'newest' ? 'Newest' : 'Oldest'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-2 font-medium">
                Share updates, reply to teammates, and edit your own posts
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    setActivityFeedFilterTask(false);
                    setActivityFeedFilterFiles(false);
                  }}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border ${
                    !activityFeedFilterTask && !activityFeedFilterFiles
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Show all activity"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setActivityFeedFilterTask((prev) => !prev)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border ${
                    activityFeedFilterTask
                      ? 'bg-purple-50 text-purple-800 border-purple-200'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Only posts with a linked task"
                  aria-pressed={activityFeedFilterTask}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    />
                  </svg>
                  Linked task
                </button>
                <button
                  type="button"
                  onClick={() => setActivityFeedFilterFiles((prev) => !prev)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border ${
                    activityFeedFilterFiles
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Only posts with attached files"
                  aria-pressed={activityFeedFilterFiles}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  Files
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-3 pr-1 border-t border-gray-50 pt-3">
                {activitiesLoading && activities.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">Loading activity…</p>
                ) : activities.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">
                    No activity yet. Share an update, attach a file, or paste a screenshot.
                  </p>
                ) : filteredFeedActivities.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">
                    No posts match these filters.
                    {(activityFeedFilterTask || activityFeedFilterFiles) && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => {
                            setActivityFeedFilterTask(false);
                            setActivityFeedFilterFiles(false);
                          }}
                          className="text-purple-700 font-semibold hover:underline"
                        >
                          Clear filters
                        </button>
                      </>
                    )}
                  </p>
                ) : (
                  filteredFeedActivities.map((act) => {
                    const activityTaskName = resolveActivityTaskName(act);
                    const feedTasks = tasks.filter(
                      (task) => task.projectId === activeProjectId
                    );
                    return (
                    <div
                      key={act.id}
                      className="bg-gray-50 p-3 rounded-xl border border-gray-100 text-xs text-gray-700"
                    >
                      {(activityTaskName || act.linkUrl) && (
                        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                          {activityTaskName && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-100 max-w-full truncate"
                              title={hoverFullText(activityTaskName)}
                            >
                              Task: {activityTaskName}
                            </span>
                          )}
                          {act.linkUrl && (
                            <a
                              href={act.linkUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-100 hover:underline max-w-full truncate"
                              title={act.linkUrl}
                            >
                              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Linked URL
                            </a>
                          )}
                        </div>
                      )}
                      {editingActivityId === act.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSaveActivityEdit(act);
                          }}
                          className="space-y-2"
                        >
                          <select
                            value={editActivityTaskId}
                            onChange={(e) =>
                              setEditActivityTaskId(e.target.value)
                            }
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Task (optional)</option>
                            {feedTasks.map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type="url"
                            placeholder="Link a URL (optional)"
                            value={editActivityLinkUrl}
                            onChange={(e) =>
                              setEditActivityLinkUrl(e.target.value)
                            }
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500"
                          />
                          <textarea
                            rows={3}
                            value={editActivityText}
                            onChange={(e) =>
                              setEditActivityText(e.target.value)
                            }
                            className="w-full px-2.5 py-2 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 resize-none"
                            placeholder="Edit your post…"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={cancelEditActivity}
                              className="text-[10px] font-semibold text-gray-500 hover:text-gray-700 px-2 py-1"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={
                                activityEditSaving ||
                                (!editActivityText.trim() &&
                                  !(act.attachments || []).length &&
                                  !normalizeActivityLinkUrl(editActivityLinkUrl))
                              }
                              className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-[11px] font-bold hover:bg-purple-700 disabled:opacity-50"
                            >
                              {activityEditSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <p className="leading-relaxed font-medium whitespace-pre-wrap">
                          {renderActivityMentionText(act.text, [
                            ...activityMentionNames,
                            ...(act.mentions || []).map(
                              (mention) => mention.name
                            ),
                          ])}
                        </p>
                      )}
                      {renderActivityAttachments(act.attachments)}
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              cancelEditActivity();
                              setReplyToId((prev) =>
                                prev === act.id ? null : act.id
                              );
                              setReplyText('');
                              setReplyTaskId(act.taskId || '');
                              setMentionMenu(null);
                              revokePendingFileUrls(replyFiles);
                              setReplyFiles([]);
                            }}
                            className="text-[10px] font-semibold text-purple-700 hover:text-purple-900"
                          >
                            Reply
                          </button>
                          {canEditActivityPost(act) &&
                            editingActivityId !== act.id && (
                              <button
                                type="button"
                                onClick={() => beginEditActivity(act)}
                                className="text-[10px] font-semibold text-gray-500 hover:text-gray-800"
                              >
                                Edit
                              </button>
                            )}
                          {act.editedAt && (
                            <span className="text-[10px] text-gray-400 italic">
                              Edited
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400">
                          {act.time}
                        </span>
                      </div>

                      {act.replies?.length > 0 && (
                        <div className="mt-2 ml-3 pl-3 border-l-2 border-purple-100 space-y-2">
                          {act.replies.map((reply) => {
                            const replyTaskName = resolveActivityTaskName(reply);
                            return (
                            <div key={reply.id} className="bg-white/80 p-2 rounded-lg border border-gray-100">
                              {replyTaskName && (
                                <span className="inline-flex mb-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">
                                  Task: {replyTaskName}
                                </span>
                              )}
                              {editingActivityId === reply.id ? (
                                <form
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSaveActivityEdit(reply);
                                  }}
                                  className="space-y-2"
                                >
                                  <select
                                    value={editActivityTaskId}
                                    onChange={(e) =>
                                      setEditActivityTaskId(e.target.value)
                                    }
                                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-purple-500"
                                  >
                                    <option value="">Task (optional)</option>
                                    {feedTasks.map((task) => (
                                      <option key={task.id} value={task.id}>
                                        {task.name}
                                      </option>
                                    ))}
                                  </select>
                                  <textarea
                                    rows={2}
                                    value={editActivityText}
                                    onChange={(e) =>
                                      setEditActivityText(e.target.value)
                                    }
                                    className="w-full px-2.5 py-2 border border-purple-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 resize-none"
                                    placeholder="Edit your reply…"
                                  />
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={cancelEditActivity}
                                      className="text-[10px] font-semibold text-gray-500 hover:text-gray-700 px-2 py-1"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="submit"
                                      disabled={
                                        activityEditSaving ||
                                        (!editActivityText.trim() &&
                                          !(reply.attachments || []).length)
                                      }
                                      className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-[11px] font-bold hover:bg-purple-700 disabled:opacity-50"
                                    >
                                      {activityEditSaving ? 'Saving…' : 'Save'}
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                <p className="leading-relaxed font-medium whitespace-pre-wrap">
                                  {renderActivityMentionText(reply.text, [
                                    ...activityMentionNames,
                                    ...(reply.mentions || []).map(
                                      (mention) => mention.name
                                    ),
                                  ])}
                                </p>
                              )}
                              {renderActivityAttachments(reply.attachments)}
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {canEditActivityPost(reply) &&
                                    editingActivityId !== reply.id && (
                                      <button
                                        type="button"
                                        onClick={() => beginEditActivity(reply)}
                                        className="text-[10px] font-semibold text-gray-500 hover:text-gray-800"
                                      >
                                        Edit
                                      </button>
                                    )}
                                  {reply.editedAt && (
                                    <span className="text-[10px] text-gray-400 italic">
                                      Edited
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] text-gray-400">
                                  {reply.time}
                                </span>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}

                      {replyToId === act.id && (
                        <form
                          onSubmit={(e) =>
                            handlePostReply(e, act.id, act.taskId || null)
                          }
                          className="mt-2 ml-3 pl-3 border-l-2 border-purple-200 space-y-2"
                        >
                          {renderPendingFiles(replyFiles, (id) =>
                            removePendingActivityFile(id, setReplyFiles)
                          )}
                          <select
                            value={replyTaskId}
                            onChange={(e) => setReplyTaskId(e.target.value)}
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Task (optional)</option>
                            {feedTasks.map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.name}
                              </option>
                            ))}
                          </select>
                          <div className="relative">
                            {renderMentionSuggestionMenu('reply')}
                            <textarea
                              ref={replyTextInputRef}
                              rows={2}
                              placeholder="Write a reply… Use @ to mention. Paste a screenshot here"
                              value={replyText}
                              onChange={(e) => {
                                const value = e.target.value;
                                setReplyText(value);
                                syncMentionMenu(
                                  'reply',
                                  value,
                                  e.target.selectionStart
                                );
                              }}
                              onClick={(e) =>
                                syncMentionMenu(
                                  'reply',
                                  e.target.value,
                                  e.target.selectionStart
                                )
                              }
                              onKeyUp={(e) => {
                                if (
                                  e.key === 'ArrowDown' ||
                                  e.key === 'ArrowUp' ||
                                  e.key === 'Enter' ||
                                  e.key === 'Tab' ||
                                  e.key === 'Escape'
                                ) {
                                  return;
                                }
                                syncMentionMenu(
                                  'reply',
                                  e.target.value,
                                  e.target.selectionStart
                                );
                              }}
                              onKeyDown={handleMentionKeyDown}
                              onPaste={(e) => handleActivityPaste(e, setReplyFiles)}
                              className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 resize-none"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1">
                              <input
                                ref={replyFileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.zip"
                                className="hidden"
                                onChange={(e) => {
                                  addPendingActivityFiles(
                                    Array.from(e.target.files || []),
                                    setReplyFiles
                                  );
                                  e.target.value = '';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => replyFileInputRef.current?.click()}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-purple-700"
                                title="Attach image or file"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setReplyToId(null);
                                  setReplyText('');
                                  setReplyTaskId('');
                                  setMentionMenu(null);
                                  revokePendingFileUrls(replyFiles);
                                  setReplyFiles([]);
                                }}
                                className="text-[10px] text-gray-500 hover:text-gray-700 px-1"
                              >
                                Cancel
                              </button>
                            </div>
                            <button
                              type="submit"
                              disabled={
                                activityPosting ||
                                (!replyText.trim() && replyFiles.length === 0)
                              }
                              className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-[11px] font-bold hover:bg-purple-700 disabled:opacity-50"
                            >
                              Reply
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            </div>

            <form
              onSubmit={handlePostComment}
              className="mt-4 pt-4 border-t border-gray-100 space-y-2"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addPendingActivityFiles(
                  Array.from(e.dataTransfer.files || []),
                  setCommentFiles
                );
              }}
            >
              {renderPendingFiles(commentFiles, (id) =>
                removePendingActivityFile(id, setCommentFiles)
              )}
              <select
                value={commentTaskId}
                onChange={(e) => setCommentTaskId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs bg-white focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Link to task (optional)</option>
                {tasks
                  .filter((task) => task.projectId === activeProjectId)
                  .map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
              </select>
              <input
                type="url"
                placeholder="Link a URL (optional) — e.g. https://…"
                value={commentLinkUrl}
                onChange={(e) => setCommentLinkUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-purple-500"
              />
              <div className="relative">
                {renderMentionSuggestionMenu('comment')}
                <textarea
                  ref={commentInputRef}
                  rows={2}
                  placeholder="Share an update… Type @ to mention a teammate"
                  value={newComment}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewComment(value);
                    syncMentionMenu('comment', value, e.target.selectionStart);
                  }}
                  onClick={(e) =>
                    syncMentionMenu(
                      'comment',
                      e.target.value,
                      e.target.selectionStart
                    )
                  }
                  onKeyUp={(e) => {
                    if (
                      e.key === 'ArrowDown' ||
                      e.key === 'ArrowUp' ||
                      e.key === 'Enter' ||
                      e.key === 'Tab' ||
                      e.key === 'Escape'
                    ) {
                      return;
                    }
                    syncMentionMenu(
                      'comment',
                      e.target.value,
                      e.target.selectionStart
                    );
                  }}
                  onKeyDown={handleMentionKeyDown}
                  onPaste={(e) => handleActivityPaste(e, setCommentFiles)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <input
                    ref={activityFileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.zip"
                    className="hidden"
                    onChange={(e) => {
                      addPendingActivityFiles(
                        Array.from(e.target.files || []),
                        setCommentFiles
                      );
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => activityFileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-purple-700"
                    title="Attach image or file"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attach
                  </button>
                  <span className="text-[10px] text-gray-400 hidden sm:inline">
                    files stay linked to this post
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={
                    activityPosting ||
                    (!newComment.trim() &&
                      commentFiles.length === 0 &&
                      !normalizeActivityLinkUrl(commentLinkUrl))
                  }
                  className="bg-purple-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-purple-700 disabled:opacity-50"
                >
                  {activityPosting ? 'Posting…' : 'Post'}
                </button>
              </div>
            </form>
          </div>
        </section>
        )}

      </main>
      </div>
    </div>
  );
};

export default Dashboard;